(function () {
  "use strict";

  var FABRIC_CDN = "https://cdnjs.cloudflare.com/ajax/libs/fabric.js/5.3.0/fabric.min.js";
  var DCF_BASE = "https://diecutflow.replit.app";
  var UNITS_PER_INCH = 36;

  // ---------------------------------------------------------------
  // Contextual AI Upscale: quality-state model.
  // The DPI that matters is the effective DPI of the artwork at its
  // CURRENT placed size (worse axis), evaluated against the original
  // pre-upscale source pixels. This is what drives whether the AI
  // Upscale toggle is shown, what helper copy appears in the popup,
  // and what outline tone the fabric placement gets.
  //
  // States and DPI tiers for the AI Upscale visibility logic:
  //
  //   ≥200 DPI            → looks_great               (hide Upscale)
  //   150–199 DPI         → good_quality              (hide Upscale)
  //   120–149 DPI         → passable                  (hide Upscale, no warning)
  //   90–119 DPI          → enhancement_recommended   (show Upscale, gentle warning)
  //   <90 DPI             → too_large_for_best_quality (show Upscale, strong warning)
  //
  // Rationale: the prior 250-DPI cutoff for "good" pushed everyone
  // into Upscale even when artwork was already print-fine. The new
  // tiers split the calm range into three honest bands so the copy
  // doesn't oversell artwork that's only "good", and so 200+ stays
  // available as a real "looks great" signal. Copy is centralized
  // below so future tone tweaks are one edit.
  var QUALITY_DPI_GREAT = 200;
  var QUALITY_DPI_GOOD = 150;
  var QUALITY_DPI_PASSABLE = 120;
  var QUALITY_DPI_LOW = 90;
  var QUALITY_COPY = {
    looks_great: {
      title: "Looks great for print.",
      body: ""
    },
    good_quality: {
      title: "Good quality for most uses.",
      body: ""
    },
    passable: {
      title: "Acceptable quality — may appear slightly soft.",
      body: ""
    },
    enhancement_recommended: {
      title: "Lower quality — consider enhancing for better results.",
      body: "We can optimize this for cleaner print quality."
    },
    too_large_for_best_quality: {
      title: "Low resolution — AI enhancement strongly recommended.",
      body: "AI enhancement is strongly recommended for this size."
    }
  };
  // Outline / chip tones per quality state. "looks_great",
  // "good_quality", and "passable" all share the calm purple tone —
  // only the chip label distinguishes them. Warning tiers stay
  // amber/red so the placed-object outline reflects severity.
  var QUALITY_OUTLINE = {
    looks_great:               { border: "#6d4cf5", corner: "#6d4cf5", dash: [6, 4], label: "Looks great" },
    good_quality:              { border: "#6d4cf5", corner: "#6d4cf5", dash: [6, 4], label: "Good quality" },
    passable:                  { border: "#6d4cf5", corner: "#6d4cf5", dash: [6, 4], label: "Acceptable" },
    enhancement_recommended:   { border: "#d97706", corner: "#d97706", dash: [4, 4], label: "Enhancement recommended" },
    too_large_for_best_quality:{ border: "#dc2626", corner: "#dc2626", dash: [3, 3], label: "Larger than recommended" }
  };

  function classifyDpi(dpi) {
    if (!isFinite(dpi) || dpi <= 0) return "too_large_for_best_quality";
    if (dpi >= QUALITY_DPI_GREAT) return "looks_great";
    if (dpi >= QUALITY_DPI_GOOD) return "good_quality";
    if (dpi >= QUALITY_DPI_PASSABLE) return "passable";
    if (dpi >= QUALITY_DPI_LOW) return "enhancement_recommended";
    return "too_large_for_best_quality";
  }

  // SOURCE dims = the underlying upload, ignoring any AI enhance.
  // Used by the setup/edit popup so the recommendation reflects the
  // raw file (the toggle is "should we enhance?" — answering that
  // requires looking at the un-enhanced pixels).
  function getSourceDimensions(asset) {
    return {
      w: asset.originalIntrinsicWidth || asset.intrinsicWidth || 1,
      h: asset.originalIntrinsicHeight || asset.intrinsicHeight || 1
    };
  }

  // PROCESSED dims = the dimensions of the image actually being
  // placed on the canvas (post-upscale when AI enhance ran, raw
  // upload otherwise). Used by the canvas object outline / placed-
  // object DPI badge so the warning state reflects the real pixels
  // the customer is going to print.
  //
  // After applyUpscale succeeds it overwrites asset.intrinsicWidth/
  // Height with the upscaled dims (e.g. 4348×4144), so the same
  // field correctly carries the processed dims here. Falling back
  // to original is only relevant before any upscale has run.
  function getProcessedDimensions(asset) {
    return {
      w: asset.intrinsicWidth || asset.originalIntrinsicWidth || 1,
      h: asset.intrinsicHeight || asset.originalIntrinsicHeight || 1
    };
  }

  function buildQualityState(state, effectiveDpi, opts) {
    opts = opts || {};
    var copy = QUALITY_COPY[state] || QUALITY_COPY.good_quality;
    // Hide Upscale at the three calm tiers (looks_great,
    // good_quality, passable). At 120+ DPI the artwork prints
    // acceptably and we don't want to push the user into the
    // slower enhance pipeline. Show only when DPI drops below
    // 120 (enhancement_recommended) or below 90
    // (too_large_for_best_quality).
    var shouldShow = (state !== "looks_great"
                   && state !== "good_quality"
                   && state !== "passable");
    // If the user has already toggled upscale ON, always keep the
    // toggle visible so they can turn it off again — even if the
    // current placed size now reads as one of the calm tiers.
    if (opts.upscaleAlreadyOn) shouldShow = true;
    return {
      quality_state: state,
      effective_dpi: Math.round(effectiveDpi),
      should_show_upscale: shouldShow,
      helper_title: copy.title,
      helper_body: copy.body
    };
  }

  // Setup/edit popup variant — uses SOURCE dims so the popup can
  // surface "AI enhancement recommended" against the raw upload.
  function getAssetQualityState(asset, placedWidthIn, placedHeightIn) {
    var src = getSourceDimensions(asset);
    var dpiX = src.w / Math.max(placedWidthIn || 0.01, 0.01);
    var dpiY = src.h / Math.max(placedHeightIn || 0.01, 0.01);
    var dpi = Math.min(dpiX, dpiY);
    return buildQualityState(classifyDpi(dpi), dpi, {
      upscaleAlreadyOn: !!asset.upscale
    });
  }

  // Canvas placement variant — uses PROCESSED dims so a successfully
  // upscaled artwork no longer reads as red on the canvas. If the
  // upload was 800×800 and AI enhance produced 4000×4000, the placed
  // object's outline tone reflects the 4000-px effective DPI, not
  // the stale 800-px one.
  function getAssetQualityStateForPlacement(asset, placedWidthIn, placedHeightIn) {
    var src = getProcessedDimensions(asset);
    var dpiX = src.w / Math.max(placedWidthIn || 0.01, 0.01);
    var dpiY = src.h / Math.max(placedHeightIn || 0.01, 0.01);
    var dpi = Math.min(dpiX, dpiY);
    return buildQualityState(classifyDpi(dpi), dpi, {
      upscaleAlreadyOn: !!asset.upscale
    });
  }
  // Map the user-facing "Background removal style" selection to the
  // backend mode value sent to the legacy /api/enhance/dtf-mask
  // endpoint. The dtf-builder-fast endpoint does NOT use this map
  // — it consumes asset.bgRemovalStyle directly as the bg_style
  // form field. This map is retained only for the legacy mask path
  // that some helper code still references.
  //
  //   - balanced       → balanced
  //   - clean_cut      → clean_logo
  //   - sticker_border → sticker_border
  //
  // Unknown / missing values default to "balanced" — the safe
  // default for the dtf-builder-fast flow we ship today.
  var BG_STYLE_TO_BACKEND_MODE = {
    balanced: "balanced",
    clean_cut: "clean_logo",
    sticker_border: "sticker_border"
  };
  function mapBgRemovalStyleToMode(style) {
    return BG_STYLE_TO_BACKEND_MODE[style] || "balanced";
  }

  // Per-style backend request config for /api/enhance/dtf-mask.
  //
  // Most styles only need `mode` and let the backend pick its
  // default removal pipeline. "Clean Cut" is the exception: the
  // user is explicitly asking for the artwork's solid background
  // color to be keyed out EVERYWHERE — including inside enclosed
  // interior regions (e.g. the inside of a shield logo). The
  // backend only does that when we explicitly opt into the
  // global color-key path; if `removal_method` is left as auto,
  // a safety guard downgrades to a rembg silhouette mask which
  // preserves the interior color and defeats the user's intent.
  //
  // Returned shape:
  //   {
  //     mode: <backend mode string>,
  //     removal_method?: "color_key_global" | undefined,
  //     force_bg_removal?: true | undefined,
  //     color_threshold?: number | undefined
  //   }
  //
  // Only fields that are defined are appended to the FormData
  // below — undefined keys are skipped so other styles keep their
  // mode-only payload exactly as before.
  function getBgMaskRequestConfig(style) {
    var mode = mapBgRemovalStyleToMode(style);
    if (style === "clean_cut") {
      return {
        mode: mode,
        removal_method: "color_key_global",
        force_bg_removal: true,
        color_threshold: 25
      };
    }
    return { mode: mode };
  }

  // Build a stable cache-slot key from a request config so that
  // requests with different override fields never reuse each
  // other's cached result. Prior to the Clean Cut override the
  // cache was keyed on `mode` alone, which means a cached
  // `clean_logo` result from before this patch (rembg-silhouette,
  // wrong) would be re-used after the patch unless we bump the
  // key. Suffixing the removal_method gives Clean Cut its own
  // slot ("clean_logo|ckg") and leaves every other style's slot
  // untouched ("balanced", "sticker_border", "clean_logo" — kept
  // available in case a future variant uses clean_logo without
  // the override).
  function getBgMaskCacheKey(reqConfig) {
    if (reqConfig && reqConfig.removal_method) {
      return reqConfig.mode + "|" + reqConfig.removal_method;
    }
    return reqConfig ? reqConfig.mode : "balanced";
  }

  // "sheet" intentionally omitted: the rail tab is hidden and users
  // now navigate Uploads → Arrange → Review directly. The "sheet"
  // panel section and refs stay in the DOM so legacy code paths that
  // reference them keep working, they're just never activated.
  var MODE_ORDER = ["uploads", "arrange", "review"];
  var MAX_AUTOFILL_COPIES = 250;
  var MAX_INLINE_JSON_LENGTH = 6000;
  var MAX_INLINE_PREVIEW_LENGTH = 3500;

  var globalApi = window.ZUG_DTF_BUILDER = window.ZUG_DTF_BUILDER || {};
  globalApi.instances = globalApi.instances || [];

  function uid(prefix) {
    return prefix + "-" + Math.random().toString(36).slice(2, 10);
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function round(value, digits) {
    var precision = typeof digits === "number" ? digits : 2;
    var factor = Math.pow(10, precision);
    return Math.round(value * factor) / factor;
  }

  function safeParseJSON(value, fallback) {
    if (!value) {
      return fallback;
    }

    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  function formatInches(value) {
    return round(value, 2) + " in";
  }

  function formatPercent(value) {
    return round(value, 1) + "%";
  }

  function formatPixels(width, height) {
    return Math.round(width) + " × " + Math.round(height) + " px";
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function loadScriptOnce(url, globalName) {
    if (window[globalName]) {
      return Promise.resolve(window[globalName]);
    }

    if (loadScriptOnce.pending) {
      return loadScriptOnce.pending;
    }

    loadScriptOnce.pending = new Promise(function (resolve, reject) {
      var script = document.createElement("script");
      script.src = url;
      script.async = true;
      script.onload = function () {
        resolve(window[globalName]);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return loadScriptOnce.pending;
  }

  function readImageDimensions(url) {
    return new Promise(function (resolve, reject) {
      var image = new Image();
      image.onload = function () {
        resolve({
          width: image.naturalWidth || image.width,
          height: image.naturalHeight || image.height
        });
      };
      image.onerror = reject;
      image.src = url;
    });
  }

  function fabricImageFromURL(url) {
    return new Promise(function (resolve, reject) {
      window.fabric.Image.fromURL(
        url,
        function (image) {
          if (!image) {
            reject(new Error("Unable to create placed artwork."));
            return;
          }

          resolve(image);
        },
        { crossOrigin: "anonymous" }
      );
    });
  }

  function rectsIntersect(a, b) {
    return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
  }

  function computeAlphaTrimBounds(imageUrl) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = function () {
        var w = img.naturalWidth || img.width;
        var h = img.naturalHeight || img.height;
        var cvs = document.createElement("canvas");
        cvs.width = w;
        cvs.height = h;
        var ctx = cvs.getContext("2d");
        ctx.drawImage(img, 0, 0);
        var data;
        try {
          data = ctx.getImageData(0, 0, w, h).data;
        } catch (e) {
          console.warn("[ZUG][DTF][TRIM_BOUNDS] canvas tainted, skipping trim", e);
          resolve(null);
          return;
        }
        var threshold = 10;
        var minX = w, maxX = 0, minY = h, maxY = 0;
        for (var y = 0; y < h; y++) {
          for (var x = 0; x < w; x++) {
            if (data[(y * w + x) * 4 + 3] > threshold) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }
        if (maxX < minX) {
          console.warn("[ZUG][DTF][TRIM_BOUNDS] fully transparent image");
          resolve(null);
          return;
        }
        var trimW = maxX - minX + 1;
        var trimH = maxY - minY + 1;
        var originalArea = w * h;
        var trimmedArea = trimW * trimH;
        var reduction = round((1 - trimmedArea / originalArea) * 100, 1);
        console.log("[ZUG][DTF][TRIM_BOUNDS] original=" + w + "x" + h + " trimmed=" + trimW + "x" + trimH + " reduction=" + reduction + "%");
        resolve({ x: minX, y: minY, width: trimW, height: trimH, originalWidth: w, originalHeight: h });
      };
      img.onerror = function () { resolve(null); };
      img.src = imageUrl;
    });
  }

  function createTrimmedPreview(imageUrl, bounds) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = function () {
        var cvs = document.createElement("canvas");
        cvs.width = bounds.width;
        cvs.height = bounds.height;
        var ctx = cvs.getContext("2d");
        ctx.drawImage(img, bounds.x, bounds.y, bounds.width, bounds.height, 0, 0, bounds.width, bounds.height);
        cvs.toBlob(function (blob) {
          if (!blob) { resolve(null); return; }
          resolve(URL.createObjectURL(blob));
        }, "image/png");
      };
      img.onerror = function () { resolve(null); };
      img.src = imageUrl;
    });
  }

  function fetchAsBlob(url) {
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error("fetch failed " + r.status);
      return r.blob();
    }).then(function (blob) {
      return URL.createObjectURL(blob);
    });
  }

  function createNotice(level, message) {
    return {
      id: uid("notice"),
      level: level,
      message: message,
      createdAt: Date.now()
    };
  }

  var DTF_PREFLIGHT_COPY = {
    no_foreground: {
      label: "No artwork",
      message: "We couldn't detect any printable artwork in this file.",
      fix: "Re-export with a transparent background, or upload a different file with visible artwork.",
      severity: "critical"
    },
    low_dpi: {
      // Calm recommendation tone — see contextual AI Upscale UX in
      // replit.md. We avoid "low resolution" / "blurry" / "may look
      // soft" wording in favor of a forward-looking suggestion.
      label: "Enhancement recommended",
      message: "This size is larger than recommended for best print quality.",
      fix: "Use AI enhancement for cleaner results, or try a slightly smaller print size."
    },
    soft_alpha: {
      label: "Soft edge",
      message: "Edges look slightly soft \u2014 the print should still look great.",
      fix: "If you'd like crisper edges, try toggling background removal off or use a hard-edged PNG."
    },
    halo_risk: {
      label: "Possible faint outline",
      message: "There may be a faint outline around the artwork on the print.",
      fix: "Toggling background removal off (or supplying a clean transparent PNG) usually softens this."
    },
    fine_detail: {
      label: "Fine detail",
      message: "Some very fine details may look a touch softer at this size.",
      fix: "A slightly larger print size, or thicker lines in the source file, helps these stand out."
    },
    fine_gap: {
      label: "Tight gaps",
      message: "Narrow gaps between shapes may look a bit thinner in print.",
      fix: "Printing a little larger, or widening the gaps in your source file, keeps them open."
    },
    tiny_islands: {
      label: "Tiny pieces",
      message: "Some very small pieces might be tricky for the transfer to pick up cleanly.",
      fix: "Scaling the artwork up, or removing the smallest floating bits, gives the best result."
    },
    small_text: {
      label: "Small text",
      message: "Text looks a little small for this print size \u2014 still readable, just smaller.",
      fix: "A larger print size or a slightly bolder font keeps it easy to read."
    }
  };

  // Sort order for displaying warnings (higher = more important).
  // Critical warnings always lead; among non-critical the spec calls out
  // low_dpi > halo > fine_detail > others.
  var DTF_WARNING_PRIORITY = {
    no_foreground: 1000, // critical (kept first by isCritical sort, priority is fallback)
    low_dpi: 90,
    halo_risk: 80,
    fine_detail: 70,
    fine_gap: 60,
    tiny_islands: 50,
    soft_alpha: 40,
    small_text: 30
  };

  function preflightWarningPriority(code) {
    return DTF_WARNING_PRIORITY[code] || 0;
  }

  function isCriticalPreflightCode(code) {
    var copy = DTF_PREFLIGHT_COPY[code];
    return !!(copy && copy.severity === "critical");
  }

  function sortPreflightWarnings(warnings) {
    return (warnings || []).slice().sort(function (a, b) {
      var aCrit = isCriticalPreflightCode(a && a.code) ? 1 : 0;
      var bCrit = isCriticalPreflightCode(b && b.code) ? 1 : 0;
      if (aCrit !== bCrit) return bCrit - aCrit;
      return preflightWarningPriority(b && b.code) - preflightWarningPriority(a && a.code);
    });
  }

  function preflightCopy(code) {
    return DTF_PREFLIGHT_COPY[code] || { label: String(code || "issue"), message: String(code || "Issue detected."), fix: "" };
  }

  function preflightWarningsFromPayload(payload) {
    if (!payload) return [];
    var raw = payload.warnings || payload.issues || payload.advisories || [];
    if (!Array.isArray(raw)) return [];
    return raw.map(function (entry) {
      if (typeof entry === "string") return { code: entry };
      if (entry && typeof entry === "object") {
        return {
          code: entry.code || entry.id || entry.key || "issue",
          severity: entry.severity || entry.level || "warning",
          detail: entry.detail || entry.message || null
        };
      }
      return { code: "issue" };
    });
  }

  function DtfGangsheetBuilder(root, externalConfig) {
    this.root = root;
    this.sectionId = root.getAttribute("data-section-id") || "modal";
    this._externalConfig = externalConfig || {};
    this.config = this.readConfig();
    this.variantSheetMap = safeParseJSON(this.config.variantSheetMapText, {});
    this.canvas = null;
    this.sheetGuideObjects = [];
    this.productForm = null;
    this.variantInput = null;
    this.resizeHandler = this.resizeCanvasPresentation.bind(this);
    this.keydownHandler = this.handleKeydown.bind(this);
    this._boundListeners = [];

    this.refs = {
      builderMain: root.querySelector(".dtf-builder__main"),
      railButtons: root.querySelectorAll("[data-dtf-mode-button]"),
      panels: root.querySelectorAll("[data-dtf-panel]"),
      dropzone: root.querySelector("[data-dtf-dropzone]"),
      fileInput: root.querySelector("[data-dtf-file-input]"),
      uploadMore: root.querySelector("[data-dtf-upload-more]"),
      fileInputMore: root.querySelector("[data-dtf-file-input-more]"),
      assetList: root.querySelector("[data-dtf-asset-list]"),
      assetEmpty: root.querySelector("[data-dtf-asset-empty]"),
      assetCount: root.querySelector("[data-dtf-asset-count]"),
      selectedAsset: root.querySelector("[data-dtf-selected-asset]"),
      placedWidth: root.querySelector("[data-dtf-placed-width]"),
      placedHeight: root.querySelector("[data-dtf-placed-height]"),
      lockRatio: root.querySelector("[data-dtf-lock-ratio]"),
      copyQuantity: root.querySelector("[data-dtf-copy-quantity]"),
      addCopies: root.querySelector("[data-dtf-add-copies]"),
      duplicateGroup: root.querySelector("[data-dtf-duplicate-group]"),
      spacingInput: root.querySelector("[data-dtf-spacing-input]"),
      spacingDec: root.querySelector("[data-dtf-spacing-dec]"),
      spacingInc: root.querySelector("[data-dtf-spacing-inc]"),
      autoFill: root.querySelector("[data-dtf-auto-fill]"),
      autoArrange: root.querySelector("[data-dtf-auto-arrange]"),
      autoArrangeRotate: root.querySelector("[data-dtf-auto-arrange-rotate]"),
      rotateSelected: root.querySelector("[data-dtf-rotate-selected]"),
      resetLayout: root.querySelector("[data-dtf-reset-layout]"),
      deleteSelected: root.querySelector("[data-dtf-delete-selected]"),
      clearAll: root.querySelector("[data-dtf-clear-all]"),
      advancedToggle: root.querySelector("[data-dtf-advanced-toggle]"),
      advancedPanel: root.querySelector("[data-dtf-advanced-panel]"),
      backButton: root.querySelector("[data-dtf-back]"),
      primaryButton: root.querySelector("[data-dtf-primary]"),
      closeButton: root.querySelector("[data-dtf-close]"),
      canvasEl: root.querySelector("[data-dtf-canvas]"),
      canvasShell: root.querySelector("[data-dtf-canvas-shell]"),
      rulerTop: root.querySelector("[data-dtf-ruler-top]"),
      rulerLeft: root.querySelector("[data-dtf-ruler-left]"),
      stageWrap: root.querySelector("[data-dtf-stage-wrap]"),
      stageEmpty: root.querySelector("[data-dtf-stage-empty]"),
      stageSheet: root.querySelector("[data-dtf-stage-sheet]"),
      stageSelection: root.querySelector("[data-dtf-stage-selection]"),
      statusDpi: root.querySelector("[data-dtf-status-dpi]"),
      statusAssets: root.querySelector("[data-dtf-status-assets]"),
      statusCopies: root.querySelector("[data-dtf-status-copies]"),
      statusUsage: root.querySelector("[data-dtf-status-usage]"),
      statusWarnings: root.querySelector("[data-dtf-status-warnings]"),
      sheetVariant: root.querySelector("[data-dtf-sheet-variant]"),
      sheetSize: root.querySelector("[data-dtf-sheet-size]"),
      printableSize: root.querySelector("[data-dtf-printable-size]"),
      spacingValue: root.querySelector("[data-dtf-spacing-value]"),
      reviewAssets: root.querySelector("[data-dtf-review-assets]"),
      reviewCopies: root.querySelector("[data-dtf-review-copies]"),
      reviewUsed: root.querySelector("[data-dtf-review-used]"),
      reviewFree: root.querySelector("[data-dtf-review-free]"),
      reviewSheetSize: root.querySelector("[data-dtf-review-sheet-size]"),
      reviewMinDpi: root.querySelector("[data-dtf-review-min-dpi]"),
      printCheckGood: root.querySelector("[data-dtf-print-check-good]"),
      warningList: root.querySelector("[data-dtf-warning-list]"),
      reviewPreview: root.querySelector("[data-dtf-review-preview]"),
      previewEmpty: root.querySelector("[data-dtf-preview-empty]"),
      exportOutput: root.querySelector("[data-dtf-export-output]"),
      refreshExport: root.querySelector("[data-dtf-refresh-export]"),
      copyExport: root.querySelector("[data-dtf-copy-export]"),
      notice: root.querySelector("[data-dtf-notice]"),
      setupOverlay: root.querySelector("[data-dtf-setup-overlay]"),
      setupPreview: root.querySelector("[data-dtf-setup-preview]"),
      artZoom: root.querySelector("[data-dtf-art-zoom]"),
      artZoomImg: root.querySelector("[data-dtf-art-zoom-img]"),
      artZoomStage: root.querySelector("[data-dtf-art-zoom-stage]"),
      artZoomSwatches: root.querySelectorAll("[data-dtf-art-bg]"),
      artZoomCloseTargets: root.querySelectorAll("[data-dtf-art-zoom-close]"),
      artZoomOpenTargets: root.querySelectorAll("[data-dtf-art-zoom-open]"),
      setupClose: root.querySelector("[data-dtf-setup-close]"),
      copiesPopup: root.querySelector("[data-dtf-copies-popup]"),
      copiesInput: root.querySelector("[data-dtf-copies-input]"),
      copiesInc: root.querySelector("[data-dtf-copies-inc]"),
      copiesDec: root.querySelector("[data-dtf-copies-dec]"),
      copiesConfirm: root.querySelector("[data-dtf-copies-confirm]"),
      copiesCancel: root.querySelector("[data-dtf-copies-cancel]"),
      setupMeta: root.querySelector("[data-dtf-setup-meta]"),
      setupCounter: root.querySelector("[data-dtf-setup-counter]"),
      setupWidth: root.querySelector("[data-dtf-setup-width]"),
      setupHeight: root.querySelector("[data-dtf-setup-height]"),
      setupLock: root.querySelector("[data-dtf-setup-lock]"),
      setupDpi: root.querySelector("[data-dtf-setup-dpi]"),
      setupQty: root.querySelector("[data-dtf-setup-qty]"),
      setupQtyDec: root.querySelector("[data-dtf-setup-qty-dec]"),
      setupQtyInc: root.querySelector("[data-dtf-setup-qty-inc]"),
      setupAdd: root.querySelector("[data-dtf-setup-add]"),
      setupSkip: root.querySelector("[data-dtf-setup-skip]"),
      setupRmbg: root.querySelector("[data-dtf-setup-rmbg]"),
      // Replaces the old in-stack [data-dtf-setup-bg-style-row]: the
      // picker trigger lives in the main modal, the four style
      // selection buttons (carrying data-dtf-bg-style) now live
      // inside the popover sub-modal at [data-dtf-bg-style-picker].
      setupBgStyleTriggerRow: root.querySelector("[data-dtf-setup-bg-style-trigger-row]"),
      setupBgStyleTrigger: root.querySelector("[data-dtf-setup-bg-style-trigger]"),
      setupBgStyleCurrent: root.querySelector("[data-dtf-setup-bg-style-current]"),
      setupBgStyleSegments: root.querySelector("[data-dtf-setup-bg-style-segments]"),
      bgStylePicker: root.querySelector("[data-dtf-bg-style-picker]"),
      setupUpscale: root.querySelector("[data-dtf-setup-upscale]"),
      setupUpscaleRow: root.querySelector("[data-dtf-setup-upscale-row]"),
      setupUpscaleHint: root.querySelector("[data-dtf-setup-upscale-hint]"),
      qualityHelper: root.querySelector("[data-dtf-quality-helper]"),
      qualityHelperTitle: root.querySelector("[data-dtf-quality-helper-title]"),
      qualityHelperBody: root.querySelector("[data-dtf-quality-helper-body]"),
      setupProcessing: root.querySelector("[data-dtf-setup-processing]"),
      setupProcessingText: root.querySelector("[data-dtf-setup-processing-text]"),
      setupProgressFill: root.querySelector("[data-dtf-setup-progress-fill]"),
      setupProgressPercent: root.querySelector("[data-dtf-setup-progress-percent]"),
      setupCaution: root.querySelector("[data-dtf-setup-caution]"),
      setupCautionText: root.querySelector("[data-dtf-setup-caution-text]"),
      setupCautionPreview: root.querySelector("[data-dtf-setup-caution-preview]"),
      setupCautionOriginal: root.querySelector("[data-dtf-setup-caution-original]"),
      setupPreflight: root.querySelector("[data-dtf-setup-preflight]"),
      setupPreflightBody: root.querySelector("[data-dtf-setup-preflight-body]"),
      reviewPreflightBody: root.querySelector("[data-dtf-review-preflight-body]"),
      canvasActions: root.querySelector("[data-dtf-canvas-actions]"),
      canvasActionDuplicate: root.querySelector("[data-dtf-canvas-action='duplicate']"),
      canvasActionAddCopies: root.querySelector("[data-dtf-canvas-action='add-copies']"),
      canvasActionDelete: root.querySelector("[data-dtf-canvas-action='delete']")
    };

    this.state = {
      currentMode: "uploads",
      assets: [],
      selectedAssetId: null,
      warnings: [],
      notices: [],
      spacingInches: Number(this.config.defaults.spacingInches) || 0.25,
      copyQuantity: 1,
      aspectRatioLocked: true,
      setupQueue: [],
      artZoomBg: "#e6e6e6",
      setupIndex: 0,
      setupMode: "queue",
      setupQty: 1,
      setupLocked: true,
      setupOriginalSnapshot: null,
      previewDataUrl: "",
      exportJson: "",
      submitting: false,
      sheet: this.resolveSheetConfig()
    };
  }

  DtfGangsheetBuilder.prototype.readConfig = function () {
    var configNode = document.getElementById("DtfBuilderConfig-" + this.sectionId);
    return safeParseJSON(configNode ? configNode.textContent : "{}", {
      defaults: {}
    });
  };

  DtfGangsheetBuilder.prototype.init = async function () {
    if (this.root.dataset.dtfBuilderReady === "true") {
      return;
    }

    this._initCancelled = false;
    await loadScriptOnce(FABRIC_CDN, "fabric");

    if (this._initCancelled) return;

    this.productForm = this.findProductForm();
    this.variantInput = this.findVariantInput();
    this.setupCanvas();
    this.bindEvents();
    this.syncSheetFromVariant(true);
    this.render();
    this.root.dataset.dtfBuilderReady = "true";
    document.body.classList.add("dtf-builder-scroll-lock");
    var self = this;
    requestAnimationFrame(function () {
      if (!self._initCancelled) self.resizeCanvasPresentation();
    });

    // Mobile only: open the Uploads drawer by default so the user
    // immediately sees what to do when the modal opens. Without
    // this, mobile users land on the bare canvas with no obvious
    // call-to-action.
    if (window.innerWidth <= 768 && this.refs.builderMain) {
      this.refs.builderMain.classList.add("is-drawer-open");
      console.log("[MOBILE][DTF] default drawer open: uploads");
    }
  };

  DtfGangsheetBuilder.prototype.destroy = function () {
    this._initCancelled = true;
    if (this._setupProgressTimer) {
      clearInterval(this._setupProgressTimer);
      this._setupProgressTimer = null;
    }
    // Cancel any in-flight smooth-completion RAF so the bar
    // doesn't keep painting after teardown.
    if (this._snapRaf && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(this._snapRaf);
      this._snapRaf = null;
    }
    if (this._preflightTimers) {
      Object.keys(this._preflightTimers).forEach(function (k) {
        clearTimeout(this._preflightTimers[k]);
      }, this);
      this._preflightTimers = {};
    }
    this._preflightInflight = {};
    if (this.canvas) {
      this.canvas.off();
      this.canvas.clear();
      this.canvas.dispose();
      this.canvas = null;
    }
    this._removeAllBoundListeners();
    window.removeEventListener("resize", this.resizeHandler);
    document.removeEventListener("keydown", this.keydownHandler);
    this.sheetGuideObjects = [];
    this.state.assets.forEach(function (asset) {
      if (asset.originalPreviewUrl) {
        try { URL.revokeObjectURL(asset.originalPreviewUrl); } catch (e) {}
      }
      if (asset.trimmedPreviewUrl) {
        try { URL.revokeObjectURL(asset.trimmedPreviewUrl); } catch (e) {}
      }
    });
    this.state.assets = [];
    this.state.selectedAssetId = null;
    this.state.warnings = [];
    this.state.notices = [];
    this.state.previewDataUrl = "";
    this.state.exportJson = "";
    this.state.submitting = false;
    this.state.currentMode = "uploads";
    this.root.dataset.dtfBuilderReady = "false";
    document.body.classList.remove("dtf-builder-scroll-lock");
    var idx = globalApi.instances.indexOf(this);
    if (idx !== -1) globalApi.instances.splice(idx, 1);
  };

  DtfGangsheetBuilder.prototype.findProductForm = function () {
    var closestProduct = this.root.closest(".product, .product__info-wrapper, .shopify-section");
    var scopedForm = closestProduct ? closestProduct.querySelector("form[action*=\"/cart/add\"]") : null;
    return scopedForm || document.querySelector("form[action*=\"/cart/add\"]");
  };

  DtfGangsheetBuilder.prototype.findVariantInput = function () {
    if (!this.productForm) {
      return null;
    }

    return this.productForm.querySelector("[name=\"id\"]");
  };

  DtfGangsheetBuilder.prototype.getSelectedVariantId = function () {
    if (this.variantInput && this.variantInput.value) {
      return String(this.variantInput.value);
    }

    if (this.config.product && this.config.product.selectedVariantId) {
      return String(this.config.product.selectedVariantId);
    }

    return "";
  };

  DtfGangsheetBuilder.prototype.getSelectedVariant = function () {
    var variantId = this.getSelectedVariantId();
    var variants = (this.config.product && this.config.product.variants) || [];

    return variants.find(function (variant) {
      return String(variant.id) === variantId;
    }) || null;
  };

  DtfGangsheetBuilder.prototype.parseVariantSizeFromTitle = function (title) {
    if (!title) {
      return null;
    }

    var match = title.match(/(\d+(?:\.\d+)?)\s*(?:x|×)\s*(\d+(?:\.\d+)?)/i);
    if (!match) {
      return null;
    }

    return {
      width: Number(match[1]),
      height: Number(match[2])
    };
  };

  DtfGangsheetBuilder.prototype.resolveSheetConfig = function () {
    var defaults = this.config.defaults || {};
    var selectedVariant = this.getSelectedVariant();
    var defaultWidth = Number(defaults.sheetWidthInches) || 22;
    var defaultHeight = Number(defaults.sheetHeightInches) || 24;
    var defaultMargin = defaults.printableMarginInches != null ? Number(defaults.printableMarginInches) : 0;
    var sheetDefinition = null;

    if (selectedVariant) {
      sheetDefinition = this.variantSheetMap[String(selectedVariant.id)] || this.variantSheetMap[selectedVariant.title];

      if (!sheetDefinition) {
        var parsed = this.parseVariantSizeFromTitle(selectedVariant.title);
        if (parsed) {
          sheetDefinition = {
            width: parsed.width,
            height: parsed.height
          };
        }
      }
    }

    var width = Number(sheetDefinition && (sheetDefinition.width || sheetDefinition.sheetWidth)) || defaultWidth;
    var height = Number(sheetDefinition && (sheetDefinition.height || sheetDefinition.sheetHeight)) || defaultHeight;
    var rawMargin = sheetDefinition && (sheetDefinition.margin != null ? sheetDefinition.margin : sheetDefinition.printableMargin);
    var margin = rawMargin != null ? Number(rawMargin) : defaultMargin;
    var printableWidth = Number(sheetDefinition && sheetDefinition.printableWidth) || Math.max(width - margin * 2, 1);
    var printableHeight = Number(sheetDefinition && sheetDefinition.printableHeight) || Math.max(height - margin * 2, 1);

    return {
      widthInches: width,
      heightInches: height,
      marginInches: margin,
      printableWidthInches: printableWidth,
      printableHeightInches: printableHeight,
      variantTitle: selectedVariant ? selectedVariant.title : "Default sheet",
      label: round(width, 2) + " × " + round(height, 2) + " in"
    };
  };

  DtfGangsheetBuilder.prototype.syncSheetFromVariant = function (force) {
    var previous = this.state.sheet;
    var hadObjects = this.canvas ? this.getPlacedObjects().length > 0 : false;
    var next = this.resolveSheetConfig();
    var changed = force ||
      previous.widthInches !== next.widthInches ||
      previous.heightInches !== next.heightInches ||
      previous.printableWidthInches !== next.printableWidthInches ||
      previous.printableHeightInches !== next.printableHeightInches;

    this.state.sheet = next;

    if (changed && this.canvas) {
      this.refreshCanvasSheet();
      this.constrainAllObjects();

      if (!force && hadObjects) {
        this.pushNotice(
          "warning",
          "Sheet size changed to " + next.label + ". Existing artwork stayed in place and was constrained inside the new printable area. Review the layout before approving."
        );
      }
    }
  };

  DtfGangsheetBuilder.prototype.getSheetUnits = function () {
    return {
      width: this.state.sheet.widthInches * UNITS_PER_INCH,
      height: this.state.sheet.heightInches * UNITS_PER_INCH,
      printableLeft: this.state.sheet.marginInches * UNITS_PER_INCH,
      printableTop: this.state.sheet.marginInches * UNITS_PER_INCH,
      printableWidth: this.state.sheet.printableWidthInches * UNITS_PER_INCH,
      printableHeight: this.state.sheet.printableHeightInches * UNITS_PER_INCH
    };
  };

  DtfGangsheetBuilder.prototype.getPrintableBounds = function () {
    var sheetUnits = this.getSheetUnits();
    return {
      left: sheetUnits.printableLeft,
      top: sheetUnits.printableTop,
      width: sheetUnits.printableWidth,
      height: sheetUnits.printableHeight,
      right: sheetUnits.printableLeft + sheetUnits.printableWidth,
      bottom: sheetUnits.printableTop + sheetUnits.printableHeight
    };
  };

  DtfGangsheetBuilder.prototype.setupCanvas = function () {
    this.refs.canvasShell.tabIndex = 0;

    this.canvas = new window.fabric.Canvas(this.refs.canvasEl, {
      preserveObjectStacking: true,
      selection: true,
      perPixelTargetFind: true,
      targetFindTolerance: 8,
      stopContextMenu: true,
      fireRightClick: false
    });

    var checkerSize = 16;
    var checkerCanvas = document.createElement("canvas");
    checkerCanvas.width = checkerSize * 2;
    checkerCanvas.height = checkerSize * 2;
    var cctx = checkerCanvas.getContext("2d");
    cctx.fillStyle = "#ffffff";
    cctx.fillRect(0, 0, checkerSize * 2, checkerSize * 2);
    cctx.fillStyle = "#ebebeb";
    cctx.fillRect(0, 0, checkerSize, checkerSize);
    cctx.fillRect(checkerSize, checkerSize, checkerSize, checkerSize);
    var checkerPattern = new window.fabric.Pattern({
      source: checkerCanvas,
      repeat: "repeat"
    });
    this.canvas.backgroundColor = checkerPattern;
    this.canvas.selectionColor = "rgba(109, 76, 245, 0.08)";
    this.canvas.selectionBorderColor = "rgba(109, 76, 245, 0.55)";
    this.canvas.selectionLineWidth = 1.5;
    this.canvas.selectionDashArray = [7, 5];
    this.canvas.hoverCursor = "move";
    this.canvas.moveCursor = "grabbing";
    this.refreshCanvasSheet();

    this.canvas.on("selection:created", this.handleSelectionChange.bind(this));
    this.canvas.on("selection:updated", this.handleSelectionChange.bind(this));
    this.canvas.on("selection:cleared", this.handleSelectionClear.bind(this));
    this.canvas.on("object:moving", this.handleObjectTransforming.bind(this));
    this.canvas.on("object:scaling", this.handleObjectTransforming.bind(this));
    this.canvas.on("object:rotating", this.handleObjectTransforming.bind(this));
    this.canvas.on("object:modified", this.handleObjectModified.bind(this));

    var self = this;
    this.canvas.on("after:render", function (opt) {
      self.drawGrid(opt.ctx || self.canvas.getContext());
      self.positionCanvasActions();
    });
    this.canvas.on("object:moving", function () { self.positionCanvasActions(); });
    this.canvas.on("object:scaling", function () { self.positionCanvasActions(); });
    this.canvas.on("object:rotating", function () { self.positionCanvasActions(); });

    if (this.refs.stageWrap) {
      this._on(this.refs.stageWrap, "scroll", function () { self.positionCanvasActions(); });
    }
    this._on(window, "resize", function () { self.positionCanvasActions(); });
  };

  DtfGangsheetBuilder.prototype.drawGrid = function (ctx) {
    var cw = this.canvas.getWidth();
    var ch = this.canvas.getHeight();
    var step = UNITS_PER_INCH / 2;

    ctx.save();
    ctx.strokeStyle = "rgba(0, 0, 0, 0.03)";
    ctx.lineWidth = 0.5;
    ctx.beginPath();

    for (var x = step; x < cw; x += step) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, ch);
    }
    for (var y = step; y < ch; y += step) {
      ctx.moveTo(0, y);
      ctx.lineTo(cw, y);
    }

    ctx.stroke();
    ctx.restore();
  };

  DtfGangsheetBuilder.prototype.refreshCanvasSheet = function () {
    var sheetUnits = this.getSheetUnits();

    this.canvas.setDimensions({
      width: sheetUnits.width,
      height: sheetUnits.height
    });

    this.renderSheetGuides();
    this.resizeCanvasPresentation();
    this.canvas.requestRenderAll();
  };

  DtfGangsheetBuilder.prototype.renderSheetGuides = function () {
    var printable = this.getPrintableBounds();
    var canvasWidth = this.canvas.getWidth();
    var canvasHeight = this.canvas.getHeight();

    this.sheetGuideObjects.forEach(function (object) {
      object.canvas && object.canvas.remove(object);
    });

    this.sheetGuideObjects = [];

    var sheetOutline = new window.fabric.Rect({
      left: 0,
      top: 0,
      width: canvasWidth,
      height: canvasHeight,
      fill: "transparent",
      stroke: "#d4d4d8",
      strokeWidth: 1,
      rx: 22,
      ry: 22,
      evented: false,
      selectable: false,
      hoverCursor: "default"
    });

    var printableRect = new window.fabric.Rect({
      left: printable.left,
      top: printable.top,
      width: printable.width,
      height: printable.height,
      fill: "transparent",
      stroke: "rgba(0, 0, 0, 0.15)",
      strokeWidth: 1,
      strokeDashArray: [10, 8],
      evented: false,
      selectable: false,
      hoverCursor: "default"
    });

    this.sheetGuideObjects.push(sheetOutline, printableRect);
    this.sheetGuideObjects.forEach(function (object, index) {
      object.zugRole = "guide";
      this.canvas.add(object);
      object.moveTo(index);
    }, this);
  };

  DtfGangsheetBuilder.prototype.resizeCanvasPresentation = function () {
    if (!this.canvas || !this.refs.stageWrap) {
      return;
    }

    var stageRect = this.refs.stageWrap.getBoundingClientRect();
    if (!stageRect.width) {
      return;
    }

    var isMobile = window.innerWidth <= 768;
    var rulerSize = isMobile ? 16 : 20;
    var padding = isMobile ? 12 : 24;
    var maxWidth = stageRect.width - padding - rulerSize;
    var scale = Math.min(maxWidth / this.canvas.getWidth(), 1);
    var cssW = this.canvas.getWidth() * scale;
    var cssH = this.canvas.getHeight() * scale;

    this.canvas.setDimensions(
      { width: cssW, height: cssH },
      { cssOnly: true }
    );

    this.canvas.calcOffset();
    this.canvas.requestRenderAll();
    this.drawRulers(cssW, cssH, scale);
  };

  DtfGangsheetBuilder.prototype.drawRulers = function (cssW, cssH, scale) {
    var rulerTop = this.refs.rulerTop;
    var rulerLeft = this.refs.rulerLeft;
    if (!rulerTop || !rulerLeft) return;

    var isMobile = window.innerWidth <= 768;
    var rulerPx = isMobile ? 16 : 20;
    var dpr = window.devicePixelRatio || 1;
    var stepPx = UNITS_PER_INCH * scale;
    var sheetW = this.state.sheet.widthInches;
    var sheetH = this.state.sheet.heightInches;
    var tickColor = "#9994b8";
    var textColor = "#6b6585";
    var labelInterval = isMobile ? 10 : 5;
    var fontSize = isMobile ? "7px" : "9px";
    var fontStack = fontSize + " -apple-system, BlinkMacSystemFont, sans-serif";

    rulerTop.style.width = cssW + "px";
    rulerTop.width = Math.round(cssW * dpr);
    rulerTop.height = Math.round(rulerPx * dpr);
    var ctxT = rulerTop.getContext("2d");
    ctxT.scale(dpr, dpr);
    ctxT.clearRect(0, 0, cssW, rulerPx);
    ctxT.strokeStyle = tickColor;
    ctxT.fillStyle = textColor;
    ctxT.font = fontStack;
    ctxT.textAlign = "center";
    ctxT.textBaseline = "top";

    for (var i = 0; i <= sheetW; i++) {
      var x = i * stepPx;
      var isMajor = i % labelInterval === 0 || i === 0;
      ctxT.beginPath();
      ctxT.moveTo(x, isMajor ? Math.round(rulerPx * 0.4) : Math.round(rulerPx * 0.65));
      ctxT.lineTo(x, rulerPx);
      ctxT.lineWidth = isMajor ? 1 : 0.5;
      ctxT.stroke();
      if (isMajor && i > 0) {
        ctxT.fillText(i + '"', x, 1);
      }
    }

    rulerLeft.style.height = cssH + "px";
    rulerLeft.width = Math.round(rulerPx * dpr);
    rulerLeft.height = Math.round(cssH * dpr);
    var ctxL = rulerLeft.getContext("2d");
    ctxL.scale(dpr, dpr);
    ctxL.clearRect(0, 0, rulerPx, cssH);
    ctxL.strokeStyle = tickColor;
    ctxL.fillStyle = textColor;
    ctxL.font = fontStack;
    ctxL.textAlign = "right";
    ctxL.textBaseline = "middle";

    for (var j = 0; j <= sheetH; j++) {
      var y = j * stepPx;
      var isMajorY = j % labelInterval === 0 || j === 0;
      ctxL.beginPath();
      ctxL.moveTo(isMajorY ? Math.round(rulerPx * 0.4) : Math.round(rulerPx * 0.65), y);
      ctxL.lineTo(rulerPx, y);
      ctxL.lineWidth = isMajorY ? 1 : 0.5;
      ctxL.stroke();
      if (isMajorY && j > 0) {
        ctxL.fillText(j + '"', rulerPx - 4, y);
      }
    }
  };

  DtfGangsheetBuilder.prototype._on = function (el, event, handler) {
    if (!el) return;
    el.addEventListener(event, handler);
    this._boundListeners.push({ el: el, event: event, handler: handler });
  };

  DtfGangsheetBuilder.prototype._removeAllBoundListeners = function () {
    this._boundListeners.forEach(function (entry) {
      entry.el.removeEventListener(entry.event, entry.handler);
    });
    this._boundListeners = [];
  };

  DtfGangsheetBuilder.prototype.openArtZoom = function (assetParam) {
    if (!this.refs.artZoom || !this.refs.artZoomImg) return;
    var asset = assetParam || this.getSetupAsset();
    if (!asset) return;
    // Prefer processed (post-rmbg / post-upscale) URL; fall back to original preview.
    var url = asset.processedUrl || asset.previewUrl || asset.originalPreviewUrl;
    if (!url) return;
    this.refs.artZoomImg.src = url;
    this.refs.artZoomImg.alt = asset.filename || "Artwork preview";
    this.applyArtZoomBackground(this.state.artZoomBg || "#e6e6e6");
    // Reveal: drop both gates (class + attribute) BEFORE forcing
    // reflow so the synchronous offsetHeight read hits the new
    // computed style.
    this.refs.artZoom.removeAttribute("hidden");
    this.refs.artZoom.classList.remove("is-hidden");
    // iOS Safari paint-deferral fix.
    // Symptom: tapping the asset thumbnail's zoom button on mobile
    // logs `[ASSET_CARD] thumb zoom clicked` and mutates the DOM
    // (is-hidden / [hidden] both removed), but the lightbox does
    // not actually paint until the *next* layout-causing event —
    // e.g. tapping Edit, which runs the heavy renderSetupPopup()
    // and forces a reflow that finally flushes the lightbox to
    // the screen.
    // Root cause: openArtZoom mutates very little (one src, one
    // alt, a class + attribute toggle). With the lightbox starting
    // from `display: none`, WebKit on iOS can defer the repaint
    // when the click handler returns without producing any layout
    // work. Reading offsetHeight forces a synchronous layout pass,
    // and a follow-up rAF nudge ensures the compositor uploads the
    // newly visible layer on the same tap instead of the next one.
    var node = this.refs.artZoom;
    // eslint-disable-next-line no-unused-expressions
    node.offsetHeight;
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(function () {
        if (!node) return;
        // eslint-disable-next-line no-unused-expressions
        node.offsetWidth;
      });
    }
    console.log("[ZUG][DTF][ART_ZOOM] open", {
      assetId: asset.id,
      filename: asset.filename,
      srcPrefix: url ? url.substring(0, 100) : null,
      isHiddenClass: node.classList.contains("is-hidden"),
      hiddenAttr: node.hasAttribute("hidden"),
      offsetHeight: node.offsetHeight,
      viewportInnerWidth: typeof window !== "undefined" ? window.innerWidth : null
    });
  };

  DtfGangsheetBuilder.prototype.closeArtZoom = function () {
    if (!this.refs.artZoom) return;
    this.refs.artZoom.classList.add("is-hidden");
    this.refs.artZoom.setAttribute("hidden", "");
    if (this.refs.artZoomImg) this.refs.artZoomImg.removeAttribute("src");
  };

  DtfGangsheetBuilder.prototype.applyArtZoomBackground = function (color) {
    if (!color) color = "#e6e6e6";
    this.state.artZoomBg = color;
    if (this.refs.artZoomStage) this.refs.artZoomStage.style.background = color;
    if (this.refs.artZoomSwatches && this.refs.artZoomSwatches.forEach) {
      this.refs.artZoomSwatches.forEach(function (btn) {
        var match = btn.getAttribute("data-dtf-art-bg") === color;
        btn.classList.toggle("is-active", match);
        btn.setAttribute("aria-checked", match ? "true" : "false");
      });
    }
  };

  DtfGangsheetBuilder.prototype.bindEvents = function () {
    var self = this;

    // Click-to-zoom artwork preview (open from setup popup, close from backdrop / X).
    if (this.refs.artZoomOpenTargets && this.refs.artZoomOpenTargets.forEach) {
      this.refs.artZoomOpenTargets.forEach(function (el) {
        self._on(el, "click", function (e) { e.preventDefault(); self.openArtZoom(); });
      });
    }
    if (this.refs.artZoomCloseTargets && this.refs.artZoomCloseTargets.forEach) {
      this.refs.artZoomCloseTargets.forEach(function (el) {
        self._on(el, "click", function (e) { e.preventDefault(); self.closeArtZoom(); });
      });
    }
    if (this.refs.artZoomSwatches && this.refs.artZoomSwatches.forEach) {
      this.refs.artZoomSwatches.forEach(function (btn) {
        self._on(btn, "click", function (e) {
          e.preventDefault();
          self.applyArtZoomBackground(btn.getAttribute("data-dtf-art-bg"));
        });
      });
    }
    // Esc closes the zoom lightbox when open.
    this._on(document, "keydown", function (e) {
      if (e.key === "Escape" && self.refs.artZoom && !self.refs.artZoom.classList.contains("is-hidden")) {
        self.closeArtZoom();
      }
    });
    // Delegated toggle for collapsible preflight summary.
    this._on(this.root, "click", function (e) {
      var trigger = e.target && e.target.closest && e.target.closest("[data-dtf-preflight-toggle]");
      if (!trigger) return;
      e.preventDefault();
      var group = trigger.parentNode;
      if (!group) return;
      var list = group.querySelector("[data-dtf-preflight-list]");
      if (!list) return;
      var nowExpanded = list.classList.toggle("is-collapsed") === false;
      trigger.setAttribute("aria-expanded", nowExpanded ? "true" : "false");
      var label = trigger.querySelector("[data-dtf-preflight-toggle-label]");
      if (label) label.textContent = nowExpanded ? "Hide details" : "View details";
    });

    this._on(this.refs.fileInput, "change", function (event) {
      self.handleFiles(event.target.files);
      event.target.value = "";
    });

    if (this.refs.dropzone) {
      var dz = this.refs.dropzone;
      this._on(dz, "dragenter", function (e) { e.preventDefault(); dz.classList.add("is-dragover"); });
      this._on(dz, "dragover", function (e) { e.preventDefault(); dz.classList.add("is-dragover"); });
      this._on(dz, "dragleave", function (e) { e.preventDefault(); dz.classList.remove("is-dragover"); });
      this._on(dz, "drop", function (e) {
        e.preventDefault();
        dz.classList.remove("is-dragover");
        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
          self.handleFiles(e.dataTransfer.files);
        }
      });
    }

    this._on(this.refs.uploadMore, "click", function () {
      if (self.refs.fileInputMore) self.refs.fileInputMore.click();
    });

    this._on(this.refs.fileInputMore, "change", function (event) {
      self.handleFiles(event.target.files);
      event.target.value = "";
    });

    this._on(this.refs.setupWidth, "input", function () {
      self.handleSetupSizeChange("width");
    });

    this._on(this.refs.setupHeight, "input", function () {
      self.handleSetupSizeChange("height");
    });

    function syncSetupQtyToAsset() {
      var asset = self.getSetupAsset();
      if (asset) asset.lastQuantity = self.state.setupQty;
    }

    this._on(this.refs.setupLock, "click", function () {
      self.state.setupLocked = !self.state.setupLocked;
      self.refs.setupLock.classList.toggle("is-locked", self.state.setupLocked);
      var lockPath = self.refs.setupLock.querySelector("[data-dtf-setup-lock-path]");
      if (lockPath) {
        lockPath.setAttribute("d", self.state.setupLocked
          ? "M7 11V7a5 5 0 0 1 10 0v4"
          : "M7 11V7a5 5 0 0 1 9.9-.5");
      }
      var asset = self.getSetupAsset();
      if (asset) asset.lockAspectRatio = self.state.setupLocked;
    });

    this._on(this.refs.setupQtyDec, "click", function () {
      self.state.setupQty = Math.max(1, self.state.setupQty - 1);
      self.refs.setupQty.value = self.state.setupQty;
      syncSetupQtyToAsset();
    });

    this._on(this.refs.setupQtyInc, "click", function () {
      self.state.setupQty = Math.min(MAX_AUTOFILL_COPIES, self.state.setupQty + 1);
      self.refs.setupQty.value = self.state.setupQty;
      syncSetupQtyToAsset();
    });

    this._on(this.refs.setupQty, "blur", function () {
      var v = parseInt(self.refs.setupQty.value, 10);
      self.state.setupQty = isNaN(v) || v < 1 ? 1 : Math.min(v, MAX_AUTOFILL_COPIES);
      self.refs.setupQty.value = self.state.setupQty;
      syncSetupQtyToAsset();
    });

    this._on(this.refs.setupAdd, "click", function () {
      self.handleSetupAdd();
    });

    this._on(this.refs.setupSkip, "click", function () {
      self.handleSetupSkip();
    });

    function toggleEnhancement(ref, field) {
      if (ref.hasAttribute("disabled")) return;
      var asset = self.getSetupAsset();
      if (!asset) return;
      asset[field] = !asset[field];
      ref.classList.toggle("is-on", asset[field]);
      ref.setAttribute("aria-checked", String(asset[field]));
      // Live-preview the popup thumbnail so the user can see the
      // toggle take effect immediately. Only handles the rmbg→OFF
      // direction (snap back to original); rmbg→ON requires the
      // backend pipeline which still runs at Save time.
      if (field === "removeBackground" && !asset.removeBackground && asset.originalPreviewUrl && self.refs.setupPreview) {
        var img = self.refs.setupPreview.querySelector("img");
        if (img && img.src !== asset.originalPreviewUrl) {
          console.log("[ZUG][DTF][ASSET_EDIT] live-preview swap → original (rmbg toggled off)", asset.filename);
          img.src = asset.originalPreviewUrl;
        }
      }
      // Re-evaluate contextual visibility after the toggle flips so
      // turning Upscale OFF on a good_quality asset hides the row
      // immediately without waiting for a size change or re-render.
      if (field === "upscale") {
        self.applySetupQualityState(asset);
        // Prewarm the dtf-builder-fast container the moment the
        // user opts into upscale — gives the backend a head start
        // on the (slow) cold-boot path before they hit "Save". The
        // helper carries its own 60s sessionStorage cooldown so
        // repeated toggles can't spam the warm endpoint.
        if (asset.upscale === true && typeof window !== "undefined" && typeof window.ZugDtfPrewarm === "function") {
          try { window.ZugDtfPrewarm("upscale-toggle-on"); } catch (e) { /* swallow */ }
        }
      }
      // Show/hide the "Background removal style" segmented control
      // the moment rmbg flips, without waiting for a popup re-render.
      if (field === "removeBackground") {
        self.applyBgRemovalStyleUI(asset);
      }
    }

    this._on(this.refs.setupRmbg, "click", function (e) {
      e.stopPropagation();
      toggleEnhancement(self.refs.setupRmbg, "removeBackground");
    });

    var rmbgRow = this.root.querySelector("[data-dtf-setup-rmbg-row]");
    if (rmbgRow) {
      this._on(rmbgRow, "click", function (e) {
        if (e.target.closest("button")) return;
        toggleEnhancement(self.refs.setupRmbg, "removeBackground");
      });
    }

    this._on(this.refs.setupUpscale, "click", function (e) {
      e.stopPropagation();
      toggleEnhancement(self.refs.setupUpscale, "upscale");
    });

    // "Background removal style" — selection cards now live inside
    // the picker sub-modal at [data-dtf-bg-style-picker], but they
    // keep the same data-dtf-bg-style attribute on the same parent
    // [data-dtf-setup-bg-style-segments] container, so this
    // delegated handler still fires identically. Selecting a card
    // also closes the picker so the user returns to the main
    // controls without an extra dismiss step.
    // Only the three bg_style values supported by the
    // dtf-builder-fast endpoint are accepted. Anything else is
    // ignored so a stale data-dtf-bg-style attribute can't smuggle
    // an unsupported value through to the backend.
    var ALLOWED_BG_STYLES = { balanced: 1, clean_cut: 1, sticker_border: 1 };
    if (this.refs.setupBgStyleSegments) {
      this._on(this.refs.setupBgStyleSegments, "click", function (e) {
        var btn = e.target.closest("[data-dtf-bg-style]");
        if (!btn) return;
        e.stopPropagation();
        var asset = self.getSetupAsset();
        if (!asset) { self.closeBgStylePicker(); return; }
        var nextStyle = btn.getAttribute("data-dtf-bg-style");
        if (!ALLOWED_BG_STYLES[nextStyle]) return;
        var changed = asset.bgRemovalStyle !== nextStyle;
        if (changed) {
          asset.bgRemovalStyle = nextStyle;
          console.log("[ZUG][DTF_BG_STYLE] selected:", nextStyle);
          self.applyBgRemovalStyleUI(asset);
        }
        // Always close the picker on a tap, even if the selection
        // didn't change — matches user mental model of "I picked
        // one, the sheet should close".
        self.closeBgStylePicker();
      });
    }

    // Picker trigger button — opens the bg-style sub-modal.
    if (this.refs.setupBgStyleTrigger) {
      this._on(this.refs.setupBgStyleTrigger, "click", function (e) {
        e.stopPropagation();
        self.openBgStylePicker();
      });
    }

    // Picker close affordances: header X, footer Done, and scrim
    // tap. Each carries [data-dtf-bg-style-picker-close]; one
    // delegated listener on the picker root handles all three.
    if (this.refs.bgStylePicker) {
      this._on(this.refs.bgStylePicker, "click", function (e) {
        if (e.target.closest("[data-dtf-bg-style-picker-close]")) {
          e.stopPropagation();
          self.closeBgStylePicker();
        }
      });
    }

    var upscaleRow = this.root.querySelector("[data-dtf-setup-upscale-row]");
    if (upscaleRow) {
      this._on(upscaleRow, "click", function (e) {
        if (e.target.closest("button")) return;
        toggleEnhancement(self.refs.setupUpscale, "upscale");
      });
    }

    if (this.refs.setupCautionOriginal) {
      this._on(this.refs.setupCautionOriginal, "click", function (e) {
        e.preventDefault();
        self.handleCautionUseOriginal();
      });
    }

    if (this.refs.setupCautionPreview) {
      this._on(this.refs.setupCautionPreview, "click", function (e) {
        e.preventDefault();
        self.handleCautionPreview();
      });
    }

    this._on(this.refs.placedWidth, "input", function () {
      self.handlePlacedSizeChange("width");
    });

    this._on(this.refs.placedHeight, "input", function () {
      self.handlePlacedSizeChange("height");
    });

    this._on(this.refs.lockRatio, "click", function () {
      self.state.aspectRatioLocked = !self.state.aspectRatioLocked;
      self.refs.lockRatio.classList.toggle("is-locked", self.state.aspectRatioLocked);
      var lockPath = self.refs.lockRatio.querySelector("[data-dtf-lock-path]");
      if (lockPath) {
        lockPath.setAttribute("d", self.state.aspectRatioLocked
          ? "M7 11V7a5 5 0 0 1 10 0v4"
          : "M7 11V7a5 5 0 0 1 9.9-.5");
      }
    });

    this._on(this.refs.copyQuantity, "input", function () {
      self.state.copyQuantity = Math.max(1, Number(self.refs.copyQuantity.value) || 1);
    });

    this._on(this.refs.addCopies, "click", function () {
      self.placeSelectedAssetCopies(self.state.copyQuantity);
    });

    this._on(this.refs.duplicateGroup, "click", function () {
      self.duplicateSelectedObject();
    });

    this._on(this.refs.spacingInput, "blur", function () {
      var val = parseFloat(self.refs.spacingInput.value);
      if (isNaN(val)) val = 0;
      self.state.spacingInches = clamp(round(val, 2), 0, 4);
      self.render();
    });

    this._on(this.refs.spacingInput, "keydown", function (e) {
      if (e.key === "Enter") {
        e.preventDefault();
        self.refs.spacingInput.blur();
      }
    });

    this._on(this.refs.spacingDec, "click", function () {
      self.state.spacingInches = clamp(round(self.state.spacingInches - 0.25, 2), 0, 4);
      self.render();
    });

    this._on(this.refs.spacingInc, "click", function () {
      self.state.spacingInches = clamp(round(self.state.spacingInches + 0.25, 2), 0, 4);
      self.render();
    });

    this._on(this.refs.autoFill, "click", function () {
      self.autoFillSelectedAsset();
    });

    this._on(this.refs.autoArrange, "click", function () {
      self.autoArrangeAll(false);
    });

    this._on(this.refs.autoArrangeRotate, "click", function () {
      self.autoArrangeAll(true);
    });

    this._on(this.refs.rotateSelected, "click", function () {
      self.rotateSelected90();
    });

    this._on(this.refs.resetLayout, "click", function () {
      self.autoArrangeAll(false);
    });

    this._on(this.refs.deleteSelected, "click", function () {
      self.deleteSelectedObject();
    });

    this._on(this.refs.clearAll, "click", function () {
      self.clearAllPlacements();
    });

    this._on(this.refs.advancedToggle, "click", function () {
      if (!self.refs.advancedPanel || !self.refs.advancedToggle) return;
      var isHidden = self.refs.advancedPanel.classList.toggle("is-hidden");
      self.refs.advancedToggle.setAttribute("aria-expanded", isHidden ? "false" : "true");
    });

    this._on(this.refs.backButton, "click", function () {
      self.goBack();
    });

    this._on(this.refs.primaryButton, "click", function () {
      self.handlePrimaryAction();
    });

    if (this.refs.closeButton) {
      this._on(this.refs.closeButton, "click", function () {
        document.dispatchEvent(new CustomEvent("zug:dtf-builder:close", {
          detail: {
            sectionId: self.sectionId
          }
        }));
      });
    }

    if (this.refs.canvasActionDuplicate) {
      this._on(this.refs.canvasActionDuplicate, "click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        console.log("[ZUG][DTF][CANVAS_ACTIONS] duplicate clicked");
        self.duplicateSelectedObject();
      });
    }

    if (this.refs.canvasActionAddCopies) {
      this._on(this.refs.canvasActionAddCopies, "click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        var active = self.getActiveObject();
        var assetId = active && active.zugMeta ? active.zugMeta.assetId : null;
        var asset = assetId ? self.state.assets.find(function (a) { return a.id === assetId; }) : null;
        if (asset) self.state.selectedAssetId = asset.id;
        console.log("[ZUG][DTF][CANVAS_ACTIONS] add-copies clicked, opening qty popup", { assetId: assetId });
        self.openCopiesPopup();
      });
    }

    if (this.refs.canvasActionDelete) {
      this._on(this.refs.canvasActionDelete, "click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        console.log("[ZUG][DTF][CANVAS_ACTIONS] delete clicked");
        self.deleteSelectedObject();
      });
    }

    this._on(this.refs.refreshExport, "click", function () {
      self.refreshReviewArtifacts(true);
    });

    this._on(this.refs.copyExport, "click", function () {
      self.copyExportToClipboard();
    });

    // Mobile drawer state helpers. Centralised so every entry point
    // (rail tap, Done button, ESC, backdrop) flows through the same
    // class toggle and produces consistent [MOBILE][DTF] logs.
    function openDrawer(reason) {
      if (!self.refs.builderMain) return;
      self.refs.builderMain.classList.add("is-drawer-open");
      console.log("[MOBILE][DTF] drawer open:", reason || "(unspecified)");
    }
    function closeDrawer(reason) {
      if (!self.refs.builderMain) return;
      self.refs.builderMain.classList.remove("is-drawer-open");
      console.log("[MOBILE][DTF] drawer close:", reason || "(unspecified)");
    }
    self._mobileOpenDrawer = openDrawer;
    self._mobileCloseDrawer = closeDrawer;

    this.refs.railButtons.forEach(function (button) {
      self._on(button, "click", function () {
        var mode = button.getAttribute("data-mode");
        console.log("[MOBILE][DTF] rail tab tapped:", mode);
        self.setMode(mode);
        // Always open drawer when a tab is tapped. Re-tapping the
        // active tab is a no-op (the class is idempotent).
        openDrawer("rail:" + mode);
      });
    });

    // Done / drawer-close — bound on the document so it survives
    // any future re-renders of the panel and never silently breaks
    // due to event-listener loss. Capture-phase so nothing
    // upstream can swallow the click.
    self._on(document, "click", function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var closer = target.closest("[data-dtf-drawer-close]");
      if (!closer || !self.root || !self.root.contains(closer)) return;
      event.preventDefault();
      event.stopPropagation();
      console.log("[MOBILE][DTF] done button click");
      closeDrawer("done-button");
    }, true);

    // ESC key also closes the drawer.
    self._on(document, "keydown", function (event) {
      if (event.key === "Escape" && self.refs.builderMain &&
          self.refs.builderMain.classList.contains("is-drawer-open")) {
        console.log("[MOBILE][DTF] drawer close via ESC");
        closeDrawer("escape");
      }
    });

    // Tap outside the drawer (i.e. on the canvas / stage area)
    // closes the drawer. We can't simply listen on the workspace
    // because the drawer is `position: fixed` overlaying it; instead
    // we listen at the document level in capture phase and close
    // when the click landed on something that is NOT inside the
    // panel, the rail, the setup popup, the lightbox, or the FAB.
    self._on(document, "click", function (event) {
      if (window.innerWidth > 768) return;
      if (!self.refs.builderMain ||
          !self.refs.builderMain.classList.contains("is-drawer-open")) return;
      var t = event.target;
      if (!t || !t.closest) return;
      // If click is inside any of these, it's NOT a "tap-outside".
      if (t.closest(".dtf-builder__panel") ||
          t.closest(".dtf-builder__rail") ||
          t.closest(".dtf-builder__setup-overlay") ||
          t.closest(".dtf-builder__art-zoom") ||
          t.closest(".dtf-builder__canvas-fab") ||
          t.closest(".dtf-builder__drawer-close") ||
          t.closest("[data-dtf-canvas-action]") ||
          t.closest(".dtf-builder__copies-popup")) return;
      // Only close if click landed inside the modal/builder root —
      // ignore clicks on Shopify chrome, chat bubble, etc.
      if (!self.root || !self.root.contains(t)) return;
      console.log("[MOBILE][DTF] tap outside drawer — closing");
      closeDrawer("tap-outside");
    }, true);

    // DIAGNOSTIC: log every click that lands inside the modal root,
    // with the topmost element actually under the pointer. If the
    // user reports a "dead" button, the log will reveal which
    // element is on top — the suspected click-eater. Mobile only.
    self._on(self.root, "click", function (event) {
      if (window.innerWidth > 768) return;
      var topEl = document.elementFromPoint(event.clientX, event.clientY);
      var topDesc = topEl
        ? (topEl.tagName.toLowerCase() +
           (topEl.id ? "#" + topEl.id : "") +
           (topEl.className && typeof topEl.className === "string"
             ? "." + topEl.className.split(/\s+/).slice(0, 3).join(".")
             : ""))
        : "(none)";
      var origDesc = event.target
        ? (event.target.tagName.toLowerCase() +
           (event.target.getAttribute && event.target.getAttribute("data-dtf-asset-action")
             ? "[action=" + event.target.getAttribute("data-dtf-asset-action") + "]"
             : ""))
        : "(none)";
      console.log("[MOBILE][DTF][CLICK]",
        "x=" + Math.round(event.clientX),
        "y=" + Math.round(event.clientY),
        "top=" + topDesc,
        "target=" + origDesc);
    }, true);

    // Floating canvas controls (mobile only — buttons are
    // display:none on desktop). Zoom uses a CSS transform on
    // the canvas-shell via the --dtf-mobile-zoom custom property.
    // Fabric's logical coordinates are NOT touched, so all
    // placement/snap/scale logic is preserved exactly.
    var ZOOM_MIN = 0.5;
    var ZOOM_MAX = 4;
    var ZOOM_STEP = 1.25;
    function getZoom() {
      if (!self.refs.builderMain) return 1;
      var v = parseFloat(self.refs.builderMain.style.getPropertyValue("--dtf-mobile-zoom"));
      return isNaN(v) || v <= 0 ? 1 : v;
    }
    function setZoom(next) {
      if (!self.refs.builderMain) return;
      var clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, next));
      self.refs.builderMain.style.setProperty("--dtf-mobile-zoom", String(clamped));
    }
    var zoomInBtn = self.root.querySelector("[data-dtf-zoom-in]");
    var zoomOutBtn = self.root.querySelector("[data-dtf-zoom-out]");
    var zoomResetBtn = self.root.querySelector("[data-dtf-zoom-reset]");
    if (zoomInBtn) {
      self._on(zoomInBtn, "click", function () { setZoom(getZoom() * ZOOM_STEP); });
    }
    if (zoomOutBtn) {
      self._on(zoomOutBtn, "click", function () { setZoom(getZoom() / ZOOM_STEP); });
    }
    // ---- Pinch-to-zoom (2-finger gesture on the stage viewport) ----
    // Native single-finger panning is already enabled via
    // `touch-action: pan-x pan-y` on .dtf-builder__stage-wrap. The
    // browser's native pinch-zoom is suppressed by that touch-action
    // value, so we wire up our own: two-finger touchstart captures the
    // initial finger distance and current zoom; touchmove computes the
    // ratio and updates --dtf-mobile-zoom; touchend resets state.
    // Single-finger touches are passed through untouched so Fabric's
    // own object-drag handlers and native scroll panning still work.
    if (self.refs.stageWrap) {
      var pinchState = null; // {startDist, startZoom}
      function touchDist(t1, t2) {
        var dx = t2.clientX - t1.clientX;
        var dy = t2.clientY - t1.clientY;
        return Math.hypot(dx, dy);
      }
      self._on(self.refs.stageWrap, "touchstart", function (event) {
        if (event.touches.length === 2) {
          pinchState = {
            startDist: touchDist(event.touches[0], event.touches[1]),
            startZoom: getZoom()
          };
          // Cancel any in-flight Fabric drag so the second finger
          // doesn't get interpreted as object-rotation.
          if (self.canvas) self.canvas.discardActiveObject();
          console.log("[MOBILE][DTF] pinch start", pinchState);
          event.preventDefault();
        } else {
          pinchState = null;
        }
      }, { passive: false });
      self._on(self.refs.stageWrap, "touchmove", function (event) {
        if (!pinchState || event.touches.length !== 2) return;
        var dist = touchDist(event.touches[0], event.touches[1]);
        if (dist <= 0 || pinchState.startDist <= 0) return;
        var next = pinchState.startZoom * (dist / pinchState.startDist);
        setZoom(next);
        event.preventDefault();
      }, { passive: false });
      function endPinch() {
        if (pinchState) {
          console.log("[MOBILE][DTF] pinch end → zoom=" + getZoom().toFixed(3));
          pinchState = null;
        }
      }
      self._on(self.refs.stageWrap, "touchend", endPinch);
      self._on(self.refs.stageWrap, "touchcancel", endPinch);
    }

    if (zoomResetBtn) {
      // "Fit whole sheet" — compute the largest --dtf-mobile-zoom
      // that lets the entire canvas-shell (sheet) fit inside the
      // stage-wrap viewport on BOTH axes, then apply it. The
      // shell's natural size is its bounding rect at scale=1, so
      // we measure with zoom temporarily reset.
      self._on(zoomResetBtn, "click", function () {
        if (!self.refs.builderMain || !self.refs.stageWrap || !self.refs.canvasShell) {
          setZoom(1);
          return;
        }
        var prev = getZoom();
        // Measure shell at scale=1 by snapping zoom to 1, reading
        // size, then restoring. (transform: scale doesn't affect
        // offsetWidth/Height, so this is actually a no-op for
        // measurement, but we keep it explicit for clarity.)
        self.refs.builderMain.style.setProperty("--dtf-mobile-zoom", "1");
        var wrap = self.refs.stageWrap.getBoundingClientRect();
        var shellW = self.refs.canvasShell.offsetWidth;
        var shellH = self.refs.canvasShell.offsetHeight;
        if (!shellW || !shellH || !wrap.width || !wrap.height) {
          setZoom(prev);
          return;
        }
        // Generous breathing room (24px each side + 4% safety
        // factor) so rounding / sub-pixel scrollbars / ruler
        // gutters never push the scaled sheet past the viewport
        // edge on either axis.
        var ratio = Math.min(
          (wrap.width - 48) / shellW,
          (wrap.height - 48) / shellH
        ) * 0.96;
        var clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, ratio));
        console.log("[MOBILE][DTF] fit-whole-sheet:", {
          wrap: { w: Math.round(wrap.width), h: Math.round(wrap.height) },
          shell: { w: shellW, h: shellH },
          ratio: ratio.toFixed(3),
          applied: clamped.toFixed(3)
        });
        setZoom(clamped);
        // After fit, scroll the wrap back to the top-left so the
        // sheet origin is visible (sheet is anchored top-left via
        // transform-origin).
        try {
          self.refs.stageWrap.scrollTo({ left: 0, top: 0, behavior: "instant" });
        } catch (e) {
          self.refs.stageWrap.scrollLeft = 0;
          self.refs.stageWrap.scrollTop = 0;
        }
      });
    }

    if (this.variantInput) {
      this._on(this.variantInput, "change", function () {
        self.syncSheetFromVariant();
        self.render();
      });
    }

    this._on(this.refs.stageWrap, "scroll", function () {
      if (self.canvas) self.canvas.calcOffset();
    });

    // X close (save & exit) on the setup popup header.
    if (this.refs.setupClose) {
      this._on(this.refs.setupClose, "click", function () {
        self.handleSetupCloseAndSave();
      });
    }

    // +Copies popup (number + confirm) for the on-canvas action bar.
    if (this.refs.copiesInc) {
      this._on(this.refs.copiesInc, "click", function () {
        var v = Math.max(1, Number(self.refs.copiesInput.value) || 1);
        self.refs.copiesInput.value = v + 1;
      });
    }
    if (this.refs.copiesDec) {
      this._on(this.refs.copiesDec, "click", function () {
        var v = Math.max(1, Number(self.refs.copiesInput.value) || 1);
        self.refs.copiesInput.value = Math.max(1, v - 1);
      });
    }
    if (this.refs.copiesInput) {
      this._on(this.refs.copiesInput, "keydown", function (e) {
        if (e.key === "Enter") {
          e.preventDefault();
          self.confirmCopiesPopup();
        } else if (e.key === "Escape") {
          self.closeCopiesPopup();
        }
      });
    }
    if (this.refs.copiesConfirm) {
      this._on(this.refs.copiesConfirm, "click", function () {
        self.confirmCopiesPopup();
      });
    }
    if (this.refs.copiesCancel) {
      this._on(this.refs.copiesCancel, "click", function () {
        self.closeCopiesPopup();
      });
    }
    // Click-outside dismiss for the copies popup.
    this._on(document, "click", function (e) {
      if (!self.refs.copiesPopup) return;
      if (self.refs.copiesPopup.classList.contains("is-hidden")) return;
      if (self.refs.copiesPopup.contains(e.target)) return;
      // Don't close when the click was on the +Copies action that opened us.
      if (e.target.closest && e.target.closest("[data-dtf-canvas-action='add-copies']")) return;
      self.closeCopiesPopup();
    });

    window.addEventListener("resize", this.resizeHandler);
    document.addEventListener("keydown", this.keydownHandler);
  };

  DtfGangsheetBuilder.prototype.handleSetupCloseAndSave = function () {
    // Treat the X button as "save current edits and exit the queue".
    if (this.state.setupMode === "edit") {
      this.handleSetupAdd();
    } else {
      this.closeSetupPopup();
    }
  };

  DtfGangsheetBuilder.prototype.openCopiesPopup = function () {
    var popup = this.refs.copiesPopup;
    var actions = this.refs.canvasActions;
    if (!popup || !actions) return;
    var asset = this.getSelectedAsset();
    var defaultQty = Math.max(1, (asset && asset.lastQuantity) || 1);
    if (this.refs.copiesInput) this.refs.copiesInput.value = defaultQty;

    popup.removeAttribute("hidden");
    popup.classList.remove("is-hidden");
    // Anchor immediately under the canvas-action bar.
    var stage = this.refs.stageWrap;
    if (stage && actions) {
      var actionsLeft = parseFloat(actions.style.left) || 0;
      var actionsTop = parseFloat(actions.style.top) || 0;
      var isAbove = actions.classList.contains("is-above");
      popup.style.left = actionsLeft + "px";
      popup.style.top = isAbove
        ? Math.max(0, actionsTop - 12 - popup.offsetHeight) + "px"
        : (actionsTop + actions.offsetHeight + 8) + "px";
    }
    if (this.refs.copiesInput) {
      try { this.refs.copiesInput.focus(); this.refs.copiesInput.select(); } catch (e) {}
    }
  };

  DtfGangsheetBuilder.prototype.closeCopiesPopup = function () {
    var popup = this.refs.copiesPopup;
    if (!popup) return;
    popup.classList.add("is-hidden");
    popup.setAttribute("hidden", "");
  };

  DtfGangsheetBuilder.prototype.confirmCopiesPopup = function () {
    var qty = Math.max(1, Number(this.refs.copiesInput && this.refs.copiesInput.value) || 1);
    var asset = this.getSelectedAsset();
    if (asset) asset.lastQuantity = qty;
    this.closeCopiesPopup();
    this.placeSelectedAssetCopies(qty);
  };

  DtfGangsheetBuilder.prototype.handleFiles = async function (fileList) {
    var self = this;
    var files = Array.prototype.slice.call(fileList || []).filter(function (file) {
      return file && file.type && file.type.indexOf("image/") === 0;
    });

    if (!files.length) {
      this.pushNotice("warning", "Please upload image files for the gang sheet builder.");
      this.render();
      return;
    }

    var assumedDpi = Number(self.config.defaults.assumedInputDpi) || 300;

    var assets = await Promise.all(
      files.map(async function (file) {
        var previewUrl = URL.createObjectURL(file);
        var dimensions = await readImageDimensions(previewUrl);
        var defaultWidthIn = round(dimensions.width / assumedDpi, 2);
        var defaultHeightIn = round(dimensions.height / assumedDpi, 2);

        return {
          id: uid("asset"),
          filename: file.name,
          file: file,
          previewUrl: previewUrl,
          intrinsicWidth: dimensions.width,
          intrinsicHeight: dimensions.height,
          originalIntrinsicWidth: dimensions.width,
          originalIntrinsicHeight: dimensions.height,
          estimatedDpi: assumedDpi,
          placedWidthInches: defaultWidthIn,
          placedHeightInches: defaultHeightIn,
          lockAspectRatio: true,
          lastQuantity: 1,
          removeBackground: false,
          // Background removal style. Determines which result the
          // customer wants when "Remove background" is enabled.
          // Allowed values (matches the dtf-builder-fast endpoint):
          // "balanced", "clean_cut", "sticker_border".
          bgRemovalStyle: "balanced",
          upscale: false,
          originalPreviewUrl: previewUrl,
          processedUrl: null,
          processedMaskUrl: null,
          processedPreviewUrl: null,
          dtfSafe: null,
          originalAlphaMode: null,
          dtfMaskSettings: null,
          upscaleEngine: null,
          enhancementStatus: "idle",
          enhancementError: null,
          // dtfMask / dtfMaskAfterUpscale = "currently-applied" cache
          // slots (downstream readers read from these directly).
          // dtfMaskByMode / dtfMaskAfterUpscaleByMode = per-mode
          // caches keyed by backend mode (balanced / clean_logo /
          // sticker_border) so toggling Background removal style
          // between values doesn't recompute a previously-fetched
          // result. The currently-applied slot is mirrored into
          // both stores on each successful run.
          processed: {
            dtfMask: null,
            upscale: null,
            dtfMaskAfterUpscale: null,
            dtfMaskByMode: {},
            dtfMaskAfterUpscaleByMode: {}
          },
          trimBounds: null,
          trimmedPreviewUrl: null,
          preflight: {
            status: "idle",
            warnings: [],
            diagnostics: null,
            lastCheckedKey: null,
            lastSource: null,
            lastWidthIn: null,
            lastHeightIn: null,
            error: null
          },
          uploadedAt: new Date().toISOString()
        };
      })
    );

    this.state.assets = this.state.assets.concat(assets);

    if (!this.state.selectedAssetId && assets[0]) {
      this.state.selectedAssetId = assets[0].id;
    }

    this.showToast(assets.length + " artwork file" + (assets.length > 1 ? "s" : "") + " added.");
    this.render();

    this.openSetupPopup(assets);
  };

  DtfGangsheetBuilder.prototype.openSetupPopup = function (assets, options) {
    if (!assets || !assets.length || !this.refs.setupOverlay) return;
    options = options || {};
    var mode = options.mode === "edit" ? "edit" : "queue";

    this.state.setupQueue = assets.slice();
    this.state.setupIndex = 0;
    this.state.setupMode = mode;

    var firstAsset = assets[0];
    if (mode === "edit") {
      this.state.setupQty = Math.max(1, firstAsset.lastQuantity || 1);
      this.state.setupLocked = firstAsset.lockAspectRatio !== false;
      this.state.setupOriginalSnapshot = {
        removeBackground: !!firstAsset.removeBackground,
        upscale: !!firstAsset.upscale,
        placedWidthInches: firstAsset.placedWidthInches,
        placedHeightInches: firstAsset.placedHeightInches
      };
      console.log("[ZUG][DTF][ASSET_EDIT] open edit popup for", firstAsset.filename, this.state.setupOriginalSnapshot);
    } else {
      this.state.setupQty = 1;
      this.state.setupLocked = true;
      this.state.setupOriginalSnapshot = null;
    }

    this.refs.setupLock.classList.toggle("is-locked", this.state.setupLocked);
    var lockPath = this.refs.setupLock.querySelector("[data-dtf-setup-lock-path]");
    if (lockPath) lockPath.setAttribute("d", this.state.setupLocked
      ? "M7 11V7a5 5 0 0 1 10 0v4"
      : "M7 11V7a5 5 0 0 1 9.9-.5");

    this.refs.setupOverlay.classList.remove("is-hidden");
    this.renderSetupPopup();
  };

  DtfGangsheetBuilder.prototype.closeSetupPopup = function () {
    if (!this.refs.setupOverlay) return;
    if (this._setupProgressTimer) {
      clearInterval(this._setupProgressTimer);
      this._setupProgressTimer = null;
    }
    // Same cleanup as destroy() — popup close shouldn't leave a
    // smooth-completion frame loop running against a hidden UI.
    if (this._snapRaf && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(this._snapRaf);
      this._snapRaf = null;
    }
    // Tear down the bg-style picker too — otherwise re-opening
    // the setup popup later would briefly flash the picker over
    // the new asset's controls.
    if (this.closeBgStylePicker) this.closeBgStylePicker();
    this.refs.setupOverlay.classList.add("is-hidden");
    this.state.setupQueue = [];
    this.state.setupIndex = 0;
    this.state.setupMode = "queue";
    this.state.setupOriginalSnapshot = null;
  };

  DtfGangsheetBuilder.prototype.getSetupAsset = function () {
    return this.state.setupQueue[this.state.setupIndex] || null;
  };

  // Human-readable labels for the three bg-removal styles supported
  // by the dtf-builder-fast endpoint. Used in the picker trigger
  // button's "current value" chip and in any log copy that wants a
  // friendly name.
  var BG_STYLE_LABELS = {
    balanced: "Balanced",
    clean_cut: "Clean Cut",
    sticker_border: "Sticker Border"
  };

  // Drive the new bg-style trigger row + picker card UI:
  //   - hide the trigger row when rmbg is off
  //   - show the trigger row + reflect the current style label
  //     when rmbg is on
  //   - mark the matching card inside the picker as active so the
  //     selection is visible the next time the user opens the sheet
  //
  // The old in-stack segmented control was removed from the DOM in
  // the modal refactor; the picker now houses the four selection
  // buttons but keeps the same data-dtf-bg-style attribute, so the
  // delegated click handler on [data-dtf-setup-bg-style-segments]
  // still fires identically. This method is UI-only — no backend
  // request, cache key, or pipeline behavior changes here.
  DtfGangsheetBuilder.prototype.applyBgRemovalStyleUI = function (asset) {
    var triggerRow = this.refs.setupBgStyleTriggerRow;
    var triggerLabel = this.refs.setupBgStyleCurrent;
    var segments = this.refs.setupBgStyleSegments;

    if (!asset || !asset.removeBackground) {
      if (triggerRow) triggerRow.setAttribute("hidden", "");
      // When rmbg is toggled off mid-flow, also dismiss the picker
      // so it doesn't linger over a now-irrelevant control.
      this.closeBgStylePicker();
      return;
    }

    // Backfill default for assets uploaded before this field
    // existed, so re-opening Edit on an old asset shows a sensible
    // selection instead of nothing.
    if (!asset.bgRemovalStyle) asset.bgRemovalStyle = "balanced";

    if (triggerRow) triggerRow.removeAttribute("hidden");
    if (triggerLabel) {
      triggerLabel.textContent = BG_STYLE_LABELS[asset.bgRemovalStyle] || "Balanced";
    }

    if (segments) {
      var current = asset.bgRemovalStyle;
      var buttons = segments.querySelectorAll("[data-dtf-bg-style]");
      for (var i = 0; i < buttons.length; i += 1) {
        var btn = buttons[i];
        var isActive = btn.getAttribute("data-dtf-bg-style") === current;
        btn.classList.toggle("is-active", isActive);
        btn.setAttribute("aria-checked", String(isActive));
        btn.setAttribute("tabindex", isActive ? "0" : "-1");
      }
    }
  };

  // Picker open / close. The picker is a position:absolute overlay
  // anchored inside the setup popup, so showing it just toggles
  // the `is-hidden` class. We also flip the trigger's
  // aria-expanded for a11y so screen readers know the popover state.
  DtfGangsheetBuilder.prototype.openBgStylePicker = function () {
    if (!this.refs.bgStylePicker) return;
    this.refs.bgStylePicker.classList.remove("is-hidden");
    if (this.refs.setupBgStyleTrigger) {
      this.refs.setupBgStyleTrigger.setAttribute("aria-expanded", "true");
    }
  };
  DtfGangsheetBuilder.prototype.closeBgStylePicker = function () {
    if (!this.refs.bgStylePicker) return;
    this.refs.bgStylePicker.classList.add("is-hidden");
    if (this.refs.setupBgStyleTrigger) {
      this.refs.setupBgStyleTrigger.setAttribute("aria-expanded", "false");
    }
  };

  DtfGangsheetBuilder.prototype.renderSetupPopup = function () {
    var asset = this.getSetupAsset();
    if (!asset) { this.closeSetupPopup(); return; }

    var isEdit = this.state.setupMode === "edit";
    var total = this.state.setupQueue.length;
    var idx = this.state.setupIndex + 1;

    var titleEl = this.root.querySelector("[data-dtf-setup-title]");
    if (titleEl) titleEl.textContent = isEdit ? "Edit artwork" : "Set up artwork";

    if (this.refs.setupCounter) {
      this.refs.setupCounter.textContent = idx + " of " + total;
      this.refs.setupCounter.style.display = isEdit ? "none" : "";
    }
    if (this.refs.setupSkip) {
      this.refs.setupSkip.style.display = isEdit ? "none" : "";
    }
    if (this.refs.setupAdd) {
      this.refs.setupAdd.textContent = isEdit ? "Save changes" : "Add to sheet";
    }

    console.log("[ZUG][DTF][SRC_TRACE] setup popup preview render", {
      assetId: asset.id,
      filename: asset.filename,
      mode: this.state.setupMode,
      removeBackground: !!asset.removeBackground,
      upscale: !!asset.upscale,
      enhancementStatus: asset.enhancementStatus,
      previewUrl: asset.previewUrl ? asset.previewUrl.substring(0, 100) : null,
      processedUrl: asset.processedUrl ? asset.processedUrl.substring(0, 100) : null,
      finalSrcUsed: asset.previewUrl ? asset.previewUrl.substring(0, 100) : null,
      finalSrcField: "previewUrl"
    });
    this.refs.setupPreview.innerHTML = "<img src=\"" + asset.previewUrl + "\" alt=\"" + escapeHtml(asset.filename) + "\">";
    // Single-line meta: "IMG_2015.JPG • 1329 × 1220 px".
    // Replaces the previous two-line stacked treatment so the
    // metadata reads as quiet supporting text under the preview
    // and stops competing for vertical space with the controls.
    this.refs.setupMeta.innerHTML =
      "<span class=\"dtf-builder__setup-meta-name\">" + escapeHtml(asset.filename) + "</span>" +
      "<span class=\"dtf-builder__setup-meta-sep\" aria-hidden=\"true\">\u2022</span>" +
      "<span class=\"dtf-builder__setup-meta-dims\">" + formatPixels(asset.intrinsicWidth, asset.intrinsicHeight) + "</span>";

    this.refs.setupWidth.value = round(asset.placedWidthInches, 2);
    this.refs.setupHeight.value = round(asset.placedHeightInches, 2);
    this.refs.setupQty.value = this.state.setupQty;

    this.refs.setupRmbg.classList.toggle("is-on", !!asset.removeBackground);
    this.refs.setupRmbg.setAttribute("aria-checked", String(!!asset.removeBackground));
    this.refs.setupRmbg.removeAttribute("disabled");
    this.refs.setupUpscale.classList.toggle("is-on", !!asset.upscale);
    this.refs.setupUpscale.setAttribute("aria-checked", String(!!asset.upscale));

    // Background removal style segmented control: show only when
    // rmbg is enabled, reflect the current asset.bgRemovalStyle on
    // the matching segment. Step 1 — UI/state only, no payload
    // change yet.
    this.applyBgRemovalStyleUI(asset);

    // Apply contextual AI Upscale visibility + helper copy based on
    // the current placed size. Re-runs on every size keystroke via
    // applySetupQualityState (see handleSetupSizeChange).
    this.applySetupQualityState(asset);

    this.refs.setupProcessing.classList.add("is-hidden");
    if (this.refs.setupCaution) this.refs.setupCaution.classList.add("is-hidden");
    this.refs.setupAdd.disabled = false;

    this.renderSetupDpi(asset);
    this.renderSetupPreflight(asset);

    // Initial preflight on popup open: kick off if size is valid and we don't already
    // have a fresh cached result. Enhancements may not have run yet (queue mode); the
    // post-enhancement trigger in handleSetupAdd will refresh as needed.
    if (asset.placedWidthInches && asset.placedHeightInches) {
      this.schedulePreflight(asset, 50);
    }
  };

  DtfGangsheetBuilder.prototype.renderSetupPreflight = function (asset) {
    if (!this.refs.setupPreflight || !this.refs.setupPreflightBody) return;
    // SETUP MODAL POLICY: this slot is reserved for the transient
    // "Checking print quality…" indicator only. The DPI line is
    // the canonical quality message and warnings are surfaced
    // contextually (e.g. AI Upscale hint mirrors recommendations).
    // We never stack preflight warning cards under the preview
    // here — that's the noisy regression we're fixing.
    var pf = asset && asset.preflight ? asset.preflight : null;
    if (pf && pf.status === "loading") {
      this.refs.setupPreflightBody.innerHTML =
        "<span class=\"dtf-builder__preflight-loading\">Checking print quality\u2026</span>";
      this.refs.setupPreflight.removeAttribute("hidden");
    } else {
      this.refs.setupPreflightBody.innerHTML = "";
      this.refs.setupPreflight.setAttribute("hidden", "");
    }
  };

  DtfGangsheetBuilder.prototype.renderPreflightBodyHtml = function (pf, options, asset) {
    options = options || {};
    if (!pf) pf = { status: "idle", warnings: [] };

    function itemHtml(w) {
      var copy = preflightCopy(w.code);
      var critical = isCriticalPreflightCode(w.code);
      var itemClass = "dtf-builder__preflight-item" + (critical ? " dtf-builder__preflight-item--critical" : "");
      var fixHtml = copy.fix
        ? "<span class=\"dtf-builder__preflight-fix\"><span class=\"dtf-builder__preflight-fix-label\">How to fix:</span> " + escapeHtml(copy.fix) + "</span>"
        : "";
      return [
        "<li class=\"" + itemClass + "\">",
        "<span class=\"dtf-builder__preflight-dot\" aria-hidden=\"true\"></span>",
        "<span class=\"dtf-builder__preflight-text\">",
        "<span class=\"dtf-builder__preflight-label\">", escapeHtml(copy.label), "</span>",
        "<span class=\"dtf-builder__preflight-message\">", escapeHtml(copy.message), "</span>",
        fixHtml,
        "</span>",
        "</li>"
      ].join("");
    }

    function warningsListHtml(warnings) {
      var sorted = sortPreflightWarnings(warnings);
      // Single warning: show inline (no collapse needed).
      if (sorted.length <= 1) {
        return "<ul class=\"dtf-builder__preflight-list\">" + sorted.map(itemHtml).join("") + "</ul>";
      }
      // Multiple: prioritize, surface a soft summary chip, collapse the rest under "View details".
      var leadCopy = preflightCopy(sorted[0].code);
      var hasCritical = sorted.some(function (w) { return isCriticalPreflightCode(w.code); });
      var summaryClass = "dtf-builder__preflight-summary" + (hasCritical ? " dtf-builder__preflight-summary--critical" : "");
      var icon = hasCritical ? "\u26A0\uFE0F" : "\u26A0\uFE0F"; // ⚠️ for both; tone differentiated via class
      var summaryHtml = [
        "<button type=\"button\" class=\"" + summaryClass + "\" aria-expanded=\"false\" data-dtf-preflight-toggle>",
        "<span class=\"dtf-builder__preflight-summary-icon\" aria-hidden=\"true\">", icon, "</span>",
        "<span class=\"dtf-builder__preflight-summary-text\">",
        sorted.length, " potential print issues",
        "<span class=\"dtf-builder__preflight-summary-lead\">Top: ", escapeHtml(leadCopy.label), "</span>",
        "</span>",
        "<span class=\"dtf-builder__preflight-summary-toggle\" data-dtf-preflight-toggle-label>View details</span>",
        "</button>"
      ].join("");
      var listHtml = "<ul class=\"dtf-builder__preflight-list dtf-builder__preflight-list--collapsible is-collapsed\" data-dtf-preflight-list>"
        + sorted.map(itemHtml).join("")
        + "</ul>";
      return "<div class=\"dtf-builder__preflight-group\">" + summaryHtml + listHtml + "</div>";
    }

    var staleWarnings = pf.warnings || [];
    // Merge in the client-side low_dpi warning so DPI and preflight messaging stay consistent.
    var mergedWarnings = asset ? this.mergeLowDpiWarning(staleWarnings, asset) : staleWarnings;

    if (pf.status === "loading") {
      var loadingHtml = "<p class=\"dtf-builder__preflight-loading\">Checking print quality\u2026</p>";
      if (mergedWarnings.length) loadingHtml += warningsListHtml(mergedWarnings);
      return loadingHtml;
    }
    if (pf.status === "error") {
      var errorHtml = "<p class=\"dtf-builder__preflight-error\">Preflight unavailable. Your artwork can still be added.</p>";
      // Preserve previously-known warnings (do not silently clear) — but never imply "no issues".
      if (mergedWarnings.length) errorHtml += warningsListHtml(mergedWarnings);
      return errorHtml;
    }
    if (pf.status === "ok") {
      if (!mergedWarnings.length) {
        if (options.compact) return "<p class=\"dtf-builder__preflight-clear\">No print quality issues detected.</p>";
        return "<p class=\"dtf-builder__preflight-clear\">No DTF preflight issues detected at the placed size.</p>";
      }
      return warningsListHtml(mergedWarnings);
    }
    // idle / unknown — still surface a low_dpi warning if applicable so the user
    // sees the resolution issue even before DCF preflight has run.
    if (mergedWarnings.length) return warningsListHtml(mergedWarnings);
    return "";
  };

  DtfGangsheetBuilder.prototype.renderReviewPreflight = function () {
    if (!this.refs.reviewPreflightBody) return;

    var asset = null;
    var heading = null;
    var activeObject = this.getActiveObject ? this.getActiveObject() : null;
    if (activeObject && activeObject.zugMeta && activeObject.zugMeta.assetId) {
      asset = this.state.assets.find(function (a) { return a.id === activeObject.zugMeta.assetId; }) || null;
      if (asset) heading = "Selected: " + asset.filename;
    }
    if (!asset) {
      asset = this.getSelectedAsset();
      if (asset) heading = asset.filename;
    }

    if (!asset) {
      this.refs.reviewPreflightBody.innerHTML = "<p class=\"dtf-builder__preflight-empty\">Select a placed artwork or asset to see its print quality check.</p>";
      return;
    }

    var html = this.renderPreflightBodyHtml(asset.preflight || { status: "idle", warnings: [] }, { compact: false }, asset);
    if (!html) {
      html = "<p class=\"dtf-builder__preflight-empty\">No print quality check yet for " + escapeHtml(asset.filename) + ".</p>";
    } else if (heading) {
      html = "<p class=\"dtf-builder__preflight-empty\" style=\"margin-bottom:6px;\">" + escapeHtml(heading) + "</p>" + html;
    }
    this.refs.reviewPreflightBody.innerHTML = html;
  };

  DtfGangsheetBuilder.prototype.renderSetupDpi = function (asset) {
    if (!asset) asset = this.getSetupAsset();
    if (!asset || !this.refs.setupDpi) return;

    var info = this.getAssetDpiInfo(asset);
    if (!info) return;

    var minimum = this.getMinimumDpi();
    var originalDpi = info.originalDpi;
    var level, primaryLabel;

    // 4-tier DPI presentation. Tiers are PRESENTATION ONLY — the
    // underlying DPI numbers and getQualityState() classifier are
    // unchanged. The "poor" cutoff (100) is the point at which the
    // result has crossed below DCF's recoverable range, where we
    // want a stronger (but still compact) visual treatment.
    //
    //   >= 300                     → great        (good level, plain)
    //   >= getMinimumDpi()         → looks good   (good level, plain)
    //   >= 100                     → enhancement  (warning level, plain amber)
    //   <  100                     → may be poor  (danger level, compact tinted pill)
    var POOR_DPI_PRESENTATION = 100;
    if (originalDpi >= 300) {
      level = "good";
      primaryLabel = originalDpi + " DPI \u2014 Print quality looks great";
    } else if (originalDpi >= minimum) {
      level = "good";
      primaryLabel = originalDpi + " DPI \u2014 Print quality looks good";
    } else if (originalDpi >= POOR_DPI_PRESENTATION) {
      level = "warning";
      primaryLabel = originalDpi + " DPI \u2014 Enhancement recommended";
    } else {
      level = "danger";
      primaryLabel = originalDpi + " DPI \u2014 Print quality may be poor";
    }

    var html = "<span class=\"dtf-builder__setup-dpi-primary\">" + escapeHtml(primaryLabel) + "</span>";
    if (info.isUpscaled && info.processedDpi && info.processedDpi !== originalDpi) {
      html += "<span class=\"dtf-builder__setup-dpi-secondary\">(Upscaled to " + info.processedDpi + " DPI)</span>";
    }

    this.refs.setupDpi.innerHTML = html;
    this.refs.setupDpi.className = "dtf-builder__setup-dpi is-" + level;

    this.logDpiInfo(asset, "renderSetupDpi");
  };

  // Show / hide the AI Upscale row + render the contextual helper
  // line based on the asset's effective DPI at its current placed
  // size. Safe to call repeatedly (idempotent DOM updates).
  DtfGangsheetBuilder.prototype.applySetupQualityState = function (asset) {
    // The standalone qualityHelper card has been removed from the
    // setup modal layout — recommendations now live inline under
    // the AI Upscale toggle (via setupUpscaleHint) so we surface a
    // single, contextual helper instead of a global warning card.
    // The helper-element branch below stays intact and gracefully
    // skips when the element is absent so other contexts (and
    // future use-sites) keep working.
    if (!asset || !this.refs.setupUpscaleRow) return null;

    var qs = getAssetQualityState(asset, asset.placedWidthInches, asset.placedHeightInches);
    asset.qualityState = qs;

    // Toggle row visibility. Use the [hidden] attribute so the
    // companion CSS can collapse the row without leaving a gap.
    var wasHidden = this.refs.setupUpscaleRow.hasAttribute("hidden");
    if (qs.should_show_upscale) {
      this.refs.setupUpscaleRow.removeAttribute("hidden");
      // Hidden→visible transition means the customer just landed on
      // an asset where Upscale is offered (low DPI). Fire the
      // backend prewarm so the slow cold-boot path is already
      // warming when they decide to enable it. Cooldown is owned
      // by ZugDtfPrewarm itself (60s sessionStorage), so repeated
      // size edits can't spam the warm endpoint.
      if (wasHidden && typeof window !== "undefined" && typeof window.ZugDtfPrewarm === "function") {
        try { window.ZugDtfPrewarm("upscale-row-visible"); } catch (e) { /* swallow */ }
      }
    } else {
      this.refs.setupUpscaleRow.setAttribute("hidden", "");
    }

    // Optional helper element — kept working for any consumer that
    // still mounts it; the setup modal no longer does.
    var helper = this.refs.qualityHelper;
    if (helper) {
      var titleEl = this.refs.qualityHelperTitle;
      var bodyEl = this.refs.qualityHelperBody;
      var modClasses = ["dtf-builder__quality-helper--good", "dtf-builder__quality-helper--recommended", "dtf-builder__quality-helper--too-large"];
      for (var i = 0; i < modClasses.length; i++) helper.classList.remove(modClasses[i]);

      var showHelper = false;
      if (qs.quality_state === "enhancement_recommended") {
        helper.classList.add("dtf-builder__quality-helper--recommended");
        showHelper = true;
      } else if (qs.quality_state === "too_large_for_best_quality") {
        helper.classList.add("dtf-builder__quality-helper--too-large");
        showHelper = true;
      } else if (asset.upscale) {
        helper.classList.add("dtf-builder__quality-helper--good");
        showHelper = true;
      }

      if (showHelper) {
        if (titleEl) titleEl.textContent = qs.helper_title || "";
        if (bodyEl) bodyEl.textContent = qs.helper_body || "";
        helper.removeAttribute("hidden");
      } else {
        helper.setAttribute("hidden", "");
      }
    }

    // Reset the upscale hint deterministically so stale recommendation
    // copy never leaks across state transitions. When we have body
    // copy from the helper, mirror it directly under the AI Upscale
    // toggle (this is the only place the recommendation now lives);
    // otherwise restore the neutral default so re-opening the popup
    // looks the same as a fresh open.
    if (this.refs.setupUpscaleHint) {
      if (qs.should_show_upscale && qs.helper_body) {
        this.refs.setupUpscaleHint.textContent = qs.helper_body;
      } else {
        this.refs.setupUpscaleHint.textContent = "Optimizes artwork for cleaner print quality";
      }
    }

    return qs;
  };

  DtfGangsheetBuilder.prototype.handleSetupSizeChange = function (axis) {
    var asset = this.getSetupAsset();
    if (!asset) return;

    var newW = Number(this.refs.setupWidth.value) || 0.5;
    var newH = Number(this.refs.setupHeight.value) || 0.5;
    // Use trimmed-bounds aspect ratio when available so the locked W/H pair
    // matches the visible artwork shown in the preview/lightbox.
    var ratio;
    if (asset.trimBounds && asset.trimBounds.width && asset.trimBounds.height) {
      ratio = asset.trimBounds.width / asset.trimBounds.height;
    } else {
      ratio = asset.intrinsicWidth / Math.max(asset.intrinsicHeight, 1);
    }

    if (this.state.setupLocked) {
      if (axis === "width") {
        newH = round(newW / ratio, 2);
        this.refs.setupHeight.value = newH;
      } else {
        newW = round(newH * ratio, 2);
        this.refs.setupWidth.value = newW;
      }
    }

    var prevW = asset.placedWidthInches;
    var prevH = asset.placedHeightInches;
    asset.placedWidthInches = Math.max(newW, 0.5);
    asset.placedHeightInches = Math.max(newH, 0.5);
    var dpiX = asset.intrinsicWidth / asset.placedWidthInches;
    var dpiY = asset.intrinsicHeight / asset.placedHeightInches;
    asset.estimatedDpi = round(Math.min(dpiX, dpiY), 0);
    this.renderSetupDpi(asset);
    // Live re-evaluation: as the user types width/height, re-show or
    // hide the AI Upscale toggle and update helper copy.
    this.applySetupQualityState(asset);

    if (prevW !== asset.placedWidthInches || prevH !== asset.placedHeightInches) {
      // Size change invalidates the preflight cache (size is part of the cache key);
      // also forces the merged low_dpi warning to re-evaluate at the new size.
      // Debounce so dragging the steppers doesn't spam the endpoint.
      if (asset.preflight) asset.preflight.lastCheckedKey = null;
      this.renderSetupPreflight(asset);
      this.schedulePreflight(asset, 450);
    }
  };

  DtfGangsheetBuilder.prototype.handleSetupAdd = async function () {
    var asset = this.getSetupAsset();
    if (!asset) { this.closeSetupPopup(); return; }

    if (this.refs.setupCaution) this.refs.setupCaution.classList.add("is-hidden");

    var isEdit = this.state.setupMode === "edit";
    var snapshot = this.state.setupOriginalSnapshot;
    if (isEdit && snapshot) {
      var rmbgChanged = !!snapshot.removeBackground !== !!asset.removeBackground;
      var upscaleChanged = !!snapshot.upscale !== !!asset.upscale;
      var sizeChanged = snapshot.placedWidthInches !== asset.placedWidthInches
                     || snapshot.placedHeightInches !== asset.placedHeightInches;

      if (rmbgChanged || upscaleChanged) {
        console.log("[ZUG][DTF][ASSET_EDIT] edit-transition", {
          assetId: asset.id,
          filename: asset.filename,
          prev: {
            removeBackground: !!snapshot.removeBackground,
            upscale: !!snapshot.upscale,
            placedWidthInches: snapshot.placedWidthInches,
            placedHeightInches: snapshot.placedHeightInches
          },
          next: {
            removeBackground: !!asset.removeBackground,
            upscale: !!asset.upscale,
            placedWidthInches: asset.placedWidthInches,
            placedHeightInches: asset.placedHeightInches
          },
          willReRunEnhancements: !!(asset.removeBackground || asset.upscale),
          futurePlacementsUseUpdatedDefaults: true
        });

        // Always revoke trimmed preview blob — it'll be regenerated if needed.
        if (asset.trimmedPreviewUrl) {
          try { URL.revokeObjectURL(asset.trimmedPreviewUrl); } catch (e) {}
        }
        asset.trimmedPreviewUrl = null;
        asset.trimBounds = null;
        asset.enhancementStatus = "idle";
        asset.enhancementError = null;

        // BG-removal toggled: invalidate rmbg-derived state. Both
        // mask cache slots are rmbg-output, so both become stale.
        // (dtfMaskAfterUpscale is the chained "upscale → mask"
        // result; it's still rmbg output and depends on rmbg state.)
        if (rmbgChanged) {
          asset.processedMaskUrl = null;
          asset.dtfSafe = null;
          asset.originalAlphaMode = null;
          asset.dtfMaskSettings = null;
          asset.processed.dtfMask = null;
          asset.processed.dtfMaskAfterUpscale = null;
          // Per-mode caches are tied to rmbg output; flipping rmbg
          // off→on (or on→off) invalidates every style's cached
          // result, not just the active one.
          asset.processed.dtfMaskByMode = {};
          asset.processed.dtfMaskAfterUpscaleByMode = {};
        }

        // Upscale toggled: invalidate upscale-derived state, AND
        // invalidate the chained "upscale → mask" cache because its
        // input was the upscaled image. Restore intrinsic dims to
        // original; if upscale is still on it'll re-run from cache
        // (or fresh) and re-set them.
        if (upscaleChanged) {
          asset.processed.upscale = null;
          asset.processed.dtfMaskAfterUpscale = null;
          // Every style's cached "upscale → mask" result fed off
          // the now-invalid upscale output, so the entire chained
          // per-mode store must go too. The non-chained per-mode
          // store (dtfMaskByMode) is still valid — its inputs
          // didn't change.
          asset.processed.dtfMaskAfterUpscaleByMode = {};
          asset.upscaleEngine = null;
          asset.upscaledBlob = null;
          if (asset.originalIntrinsicWidth) asset.intrinsicWidth = asset.originalIntrinsicWidth;
          if (asset.originalIntrinsicHeight) asset.intrinsicHeight = asset.originalIntrinsicHeight;
        }

        // Pick the best non-stale source for previewUrl/processedUrl
        // based on what remains enabled. The pipeline below will
        // re-apply enabled toggles (using cache where still valid)
        // and overwrite these as needed.
        //
        // New chain order: original → upscale → mask. So:
        //   * upscale only → use processed.upscale
        //   * mask only    → use processed.dtfMask
        //   * both         → use processed.dtfMaskAfterUpscale
        if (!asset.removeBackground && !asset.upscale) {
          asset.processedUrl = null;
          asset.processedPreviewUrl = null;
          asset.previewUrl = asset.originalPreviewUrl;
        } else if (!asset.removeBackground && asset.upscale) {
          var upCache = asset.processed.upscale;
          if (upCache && upCache.output_url) {
            asset.processedUrl = upCache.output_url;
            asset.previewUrl = upCache.output_url;
          } else {
            asset.processedUrl = null;
            asset.previewUrl = asset.originalPreviewUrl;
          }
          asset.processedPreviewUrl = null;
        } else {
          // rmbg is on (with or without upscale) → null processed
          // and let the pipeline run upscale first (if enabled),
          // then mask the upscaled result. Whichever of dtfMask /
          // dtfMaskAfterUpscale matches will be picked up there.
          asset.processedUrl = null;
          asset.processedPreviewUrl = null;
          asset.previewUrl = asset.originalPreviewUrl;
        }

        asset.estimatedDpi = round(Math.min(
          asset.intrinsicWidth / Math.max(asset.placedWidthInches, 0.01),
          asset.intrinsicHeight / Math.max(asset.placedHeightInches, 0.01)
        ), 0);
      }

      // Size-only edits also invalidate trim bounds, future preflight
      // cache, AND the auto-enhance decision cache. The image bytes
      // themselves don't change with size, but the backend's
      // should_upscale decision is keyed off the print-target dims
      // we send (print_width_in / print_height_in) — so a size change
      // can flip should_upscale from false→true (or vice versa).
      // Without this, applyUpscale would hit the cached output for
      // the OLD size and never re-ask the backend.
      if (sizeChanged && !rmbgChanged && !upscaleChanged) {
        console.log("[ZUG][DTF][ASSET_EDIT] size changed, invalidating trim/preflight + upscale decision", {
          filename: asset.filename,
          from: { w: snapshot.placedWidthInches, h: snapshot.placedHeightInches },
          to:   { w: asset.placedWidthInches,   h: asset.placedHeightInches }
        });
        if (asset.trimmedPreviewUrl) {
          try { URL.revokeObjectURL(asset.trimmedPreviewUrl); } catch (e) {}
        }
        asset.trimmedPreviewUrl = null;
        asset.trimBounds = null;
        // Drop the cached auto-enhance result so the next pipeline
        // run re-asks /api/enhance/auto-enhance with the new
        // print_width_in / print_height_in. The chained "upscale →
        // mask" cache is also invalidated because its input would
        // change if the upscale decision flips.
        if (asset.upscale) {
          asset.processed.upscale = null;
          asset.processed.dtfMaskAfterUpscale = null;
          asset.processed.dtfMaskAfterUpscaleByMode = {};
          asset.upscaleEngine = null;
          asset.upscaledBlob = null;
          if (asset.originalIntrinsicWidth) asset.intrinsicWidth = asset.originalIntrinsicWidth;
          if (asset.originalIntrinsicHeight) asset.intrinsicHeight = asset.originalIntrinsicHeight;
        }
        if (asset.preflight) asset.preflight = null;
      }

      // Any edit invalidates per-asset preflight cache (forward-compat with the
      // dtf-preflight-warnings task; no-op if asset.preflight isn't set yet).
      if ((rmbgChanged || upscaleChanged || sizeChanged) && asset.preflight) {
        console.log("[ZUG][DTF][ASSET_EDIT] invalidating preflight cache for", asset.filename);
        asset.preflight = null;
      }
    }

    // ─── ENHANCE GATE (strict, single source of truth) ───
    // Forensic fix: previously the gate was a loose `||` on the two
    // toggle properties. That let truthy-non-boolean values (e.g. a
    // legacy "true" string, a number, or an object survivor from an
    // older state shape) silently pass and fire /dtf-builder-fast
    // even when the SRC_TRACE rendered them as falsy. From now on:
    //
    //   shouldEnhance ⇔ (asset.upscale === true) ∨ (asset.removeBackground === true)
    //
    // No coercion. If the value isn't strictly the boolean `true`,
    // it does NOT count as "user wants enhancement". The endpoint
    // is not called and the original artwork is placed as-is.
    var upscaleOn = (asset.upscale === true);
    var rmbgOn = (asset.removeBackground === true);
    var shouldEnhance = upscaleOn || rmbgOn;
    console.log("[ZUG][DTF][ENHANCE][GATE]", {
      assetId: asset.id,
      upscale: asset.upscale,
      removeBackground: asset.removeBackground,
      shouldEnhance: shouldEnhance
    });

    var bgFailed = false;
    var bgError = "";
    // Tracks "upscale was ON for this run AND failed". Used to short-
    // circuit the rmbg/dtf-mask block below so we don't fire a second
    // ~45s backend call after the first one already failed (typically
    // the same backend / network condition that broke upscale will
    // also break dtf-mask). The user's toggle intent ("I want
    // upscale+rmbg") is incompatible with the broken chain, so the
    // safe fallback is to drop both and place the original.
    var upscaleFailed = false;

    if (shouldEnhance) {
      this.refs.setupAdd.disabled = true;
      this.refs.setupProcessing.classList.remove("is-hidden");
      asset.enhancementStatus = "processing";
      asset.enhancementError = null;

      // ─── SINGLE-CALL FAST PATH (Apr 2026 cutover) ───
      // The prior two-call chain (auto-enhance → dtf-mask) is
      // replaced by ONE call to /api/enhance/dtf-builder-fast,
      // which performs upscale + background removal server-side
      // and returns the final + mask + display preview in one
      // response. The user's two toggles (Upscale, Background
      // Removal) collapse to a single trigger here: if either is
      // on, fire the fast endpoint once. The endpoint is locked
      // server-side — no mode / profile / engine knobs are sent.
      try {
        // Staged progress: instead of one "Enhancing artwork…" bar
        // that stalls at 95% on cold upscale paths, drive the bar
        // through named stages (Preparing → Upscaling → Removing
        // background → Finalizing) sized for the actual toggle
        // combo. Each stage has its own asymptotic easing curve
        // that keeps inching forward past its nominal budget, so
        // a slow backend never freezes the UI at a single number.
        // The new function sets the initial text label itself.
        this.startSetupProgressStaged(this.buildEnhanceProgressStages(asset));
        await this.applyDtfBuilderFast(asset);
        if (this._initCancelled) return;
        // Smooth-snap to 100% (with a brief "Finalizing…" beat
        // when the request took ≥1.5s) so warm-path responses
        // that land mid-stage don't pop straight from low double
        // digits to 100%. Awaiting keeps the processing UI on
        // screen until the animation completes.
        await this.completeSetupProgress({ smooth: true });
        if (this._initCancelled) return;
      } catch (err) {
        if (this._initCancelled) return;
        this.completeSetupProgress();
        console.error("[ZUG][DTF][ENHANCE][DEBUG] dtf-builder-fast failed:", err);
        // Treat the single call's failure as both "upscale failed"
        // and "bg failed" for downstream UX (caution banner, notice).
        upscaleFailed = true;
        bgFailed = !!asset.removeBackground;
        bgError = err.message || "AI enhancement couldn\u2019t be completed right now. Please try again.";
        asset.upscale = false;
        asset.removeBackground = false;
        asset.enhancementError = (asset.enhancementError ? asset.enhancementError + " | " : "") + bgError;
        this.pushNotice("warning", bgError + " (" + asset.filename + ")");
        // Deterministic visual-state reset — see prior chain
        // implementation for rationale (stale processed artifacts
        // from a previous successful run must not leak into the
        // fallback placement).
        asset.processedUrl = null;
        asset.processedMaskUrl = null;
        asset.processedPreviewUrl = null;
        asset.maskUrl = null;
        asset.previewUrl = asset.originalPreviewUrl;
        asset.dtfSafe = null;
        asset.upscaleEngine = null;
        asset.bgEngineUsed = null;
        asset.fallbackUsed = null;
        if (asset.trimmedPreviewUrl) {
          try { URL.revokeObjectURL(asset.trimmedPreviewUrl); } catch (e) {}
          asset.trimmedPreviewUrl = null;
        }
        asset.trimBounds = null;
      }

      if (asset.dtfSafe === false) {
        this.refs.setupProcessing.classList.add("is-hidden");
        this.showSetupCaution("info", "We recommend reviewing this artwork after placement — the background removal result may need a touch-up.");
        console.warn("[ZUG][DTF][ENHANCE][DEBUG] dtf_safe=false (soft warning, not blocking) for", asset.filename);
      }

      if (asset.enhancementStatus === "processing") {
        asset.enhancementStatus = bgFailed ? "error" : "idle";
      }

      if (this._initCancelled) return;
      this.refs.setupProcessing.classList.add("is-hidden");
      this.refs.setupAdd.disabled = false;

      if (bgFailed) {
        this.showSetupCaution("error", "Background removal failed — using the original image instead.");
        this.pushNotice("warning", "Background removal didn't succeed for \"" + asset.filename + "\". The original image was placed.");
      }
    }

    if (this._initCancelled || !this.canvas) return;

    // Enhancement-state and processed-source changes invalidate any prior preflight
    // result, so we always re-check after the pipeline (or after a no-op skip).
    if (asset.preflight) asset.preflight.lastCheckedKey = null;
    this.schedulePreflight(asset, 0);

    this.finalizePlacement(asset);
  };

  DtfGangsheetBuilder.prototype.startSetupProgress = function (estimatedMs) {
    var self = this;
    if (this._setupProgressTimer) {
      clearInterval(this._setupProgressTimer);
      this._setupProgressTimer = null;
    }
    var startedAt = Date.now();
    var duration = Math.max(estimatedMs || 10000, 1000);
    this._setProgressUI(0);
    this._setupProgressTimer = setInterval(function () {
      var elapsed = Date.now() - startedAt;
      var t = Math.min(elapsed / duration, 1);
      var pct = Math.min(95, Math.round(95 * (1 - Math.pow(1 - t, 2))));
      self._setProgressUI(pct);
    }, 200);
  };

  // Build the stage list for the dtf-builder-fast progress bar
  // based on which enhancement toggles the user has on. Each
  // stage owns a slice of the 0–96 range plus a real-time budget
  // sized for the (slow) cold path; the easing curve handles the
  // warm path automatically by reaching the stage cap quickly.
  // The final ceiling is 96% (not 95%) so the asymptotic stalls
  // happen *inside* a stage's range, not at the global "stuck at
  // 95%" position the prior single-bar version landed on.
  DtfGangsheetBuilder.prototype.buildEnhanceProgressStages = function (asset) {
    var upscale = asset && asset.upscale === true;
    var rmbg = asset && asset.removeBackground === true;
    if (upscale && rmbg) {
      return [
        { label: "Preparing artwork\u2026",   fromPct: 0,  toPct: 8,  budgetMs: 1500 },
        { label: "Upscaling artwork\u2026",   fromPct: 8,  toPct: 55, budgetMs: 35000 },
        { label: "Removing background\u2026", fromPct: 55, toPct: 88, budgetMs: 25000 },
        { label: "Finalizing\u2026",          fromPct: 88, toPct: 96, budgetMs: 8000 }
      ];
    }
    if (upscale) {
      return [
        { label: "Preparing artwork\u2026",   fromPct: 0,  toPct: 10, budgetMs: 1500 },
        { label: "Upscaling artwork\u2026",   fromPct: 10, toPct: 88, budgetMs: 40000 },
        { label: "Finalizing\u2026",          fromPct: 88, toPct: 96, budgetMs: 6000 }
      ];
    }
    // Background removal only (no upscale) — the fast endpoint
    // typically returns in 8–18s for this combo.
    return [
      { label: "Preparing artwork\u2026",     fromPct: 0,  toPct: 12, budgetMs: 1500 },
      { label: "Removing background\u2026",   fromPct: 12, toPct: 88, budgetMs: 18000 },
      { label: "Finalizing\u2026",            fromPct: 88, toPct: 96, budgetMs: 4000 }
    ];
  };

  // Drive the progress UI through a list of named stages. Within
  // each stage the percentage eases asymptotically toward the
  // stage's cap (1 - e^-1.2t — reaches ~70% of span at budget
  // time, ~95% at ~2.5x budget) so even a stage that runs long
  // keeps inching forward instead of freezing. Stage advance is
  // strictly time-driven (no backend signal), so the LAST stage
  // never auto-advances — it just keeps creeping toward its cap
  // until completeSetupProgress() fires and snaps to 100%.
  DtfGangsheetBuilder.prototype.startSetupProgressStaged = function (stages) {
    var self = this;
    if (this._setupProgressTimer) {
      clearInterval(this._setupProgressTimer);
      this._setupProgressTimer = null;
    }
    if (!stages || !stages.length) return;
    var startedAt = Date.now();
    // Track the overall start so completeSetupProgress can decide
    // whether the request was long enough to warrant the
    // "Finalizing…" beat (skip on ultra-fast responses to avoid
    // adding any perceptible delay).
    this._setupProgressStartedAt = startedAt;
    var stageStartedAt = startedAt;
    var currentIdx = 0;
    this._setProgressUI(stages[0].fromPct);
    if (this.refs.setupProcessingText && stages[0].label) {
      this.refs.setupProcessingText.textContent = stages[0].label;
    }
    console.log("[ZUG][DTF][PROGRESS] start", {
      stages: stages.map(function (s) { return s.label + " " + s.fromPct + "->" + s.toPct + " (" + s.budgetMs + "ms)"; })
    });
    this._setupProgressTimer = setInterval(function () {
      var stage = stages[currentIdx];
      var elapsedInStage = Date.now() - stageStartedAt;
      var span = stage.toPct - stage.fromPct;
      var t = elapsedInStage / Math.max(stage.budgetMs, 1);
      var eased = 1 - Math.exp(-1.2 * t);
      var pct = stage.fromPct + span * eased;
      // Always leave a hair of headroom below the stage cap so
      // the bar is visibly *moving* rather than parked on the cap.
      if (pct > stage.toPct - 0.4) pct = stage.toPct - 0.4;
      self._setProgressUI(pct);
      // Time-driven stage advance — only when there's a next
      // stage to advance into. The final stage stays put and
      // keeps easing toward its own cap until completion.
      if (elapsedInStage >= stage.budgetMs && currentIdx < stages.length - 1) {
        currentIdx += 1;
        stageStartedAt = Date.now();
        var next = stages[currentIdx];
        // Snap to the new stage's fromPct so the next easing
        // curve starts from a clean baseline (no overshoot).
        self._setProgressUI(next.fromPct);
        if (self.refs.setupProcessingText && next.label) {
          self.refs.setupProcessingText.textContent = next.label;
        }
        console.log("[ZUG][DTF][PROGRESS] stage advance", {
          idx: currentIdx,
          label: next.label,
          elapsedTotalMs: Date.now() - startedAt,
          fromPct: next.fromPct,
          toPct: next.toPct,
          budgetMs: next.budgetMs
        });
      }
    }, 200);
  };

  // Two completion modes:
  //   completeSetupProgress()                  → instant snap to 100% (default; used by the failure path so errors surface immediately)
  //   completeSetupProgress({ smooth: true })  → optional "Finalizing…" beat (when request ≥1.5s) followed by an ease-out animation from the current pct to 100% (~300ms). Returns a Promise the caller awaits so the processing UI doesn't tear down mid-animation.
  // The smooth path solves two warm-path UX issues: (1) responses
  // that land mid-stage no longer pop from ~25% straight to 100%,
  // and (2) the user always sees a brief closing beat regardless
  // of which stage the response actually interrupted.
  DtfGangsheetBuilder.prototype.completeSetupProgress = function (opts) {
    var self = this;
    if (this._setupProgressTimer) {
      clearInterval(this._setupProgressTimer);
      this._setupProgressTimer = null;
    }
    // If a previous smooth-completion is still painting (rapid
    // retry edge case), cancel its frame loop before starting a
    // new one so we don't get two RAFs fighting over the bar.
    if (this._snapRaf && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(this._snapRaf);
      this._snapRaf = null;
    }
    var smooth = !!(opts && opts.smooth);
    if (!smooth) {
      this._setProgressUI(100);
      return Promise.resolve();
    }

    return new Promise(function (resolve) {
      var startedAt = self._setupProgressStartedAt || Date.now();
      var totalElapsed = Date.now() - startedAt;
      var currentPct = (typeof self._currentProgressPct === "number") ? self._currentProgressPct : 0;

      // "Finalizing…" beat is only worth showing when:
      //   - the request took long enough that ~150ms is invisible (≥1.5s)
      //   - the label isn't already "Finalizing…" (no-op otherwise)
      //   - the processing-text DOM hook actually exists
      // Ultra-fast responses (cached / sub-second warm) skip the
      // beat entirely so we add zero perceptible delay there.
      var FINALIZING = "Finalizing\u2026";
      var hasTextRef = !!(self.refs && self.refs.setupProcessingText);
      var currentLabel = hasTextRef ? self.refs.setupProcessingText.textContent : "";
      var shouldShowBeat = totalElapsed >= 1500 && hasTextRef && currentLabel !== FINALIZING;
      var BEAT_MS = shouldShowBeat ? 150 : 0;
      var SNAP_DURATION_MS = 300;

      if (shouldShowBeat) {
        self.refs.setupProcessingText.textContent = FINALIZING;
        console.log("[ZUG][DTF][PROGRESS] finalizing beat", {
          fromLabel: currentLabel,
          fromPct: currentPct,
          totalElapsedMs: totalElapsed,
          beatMs: BEAT_MS
        });
      }

      var beatTimer = setTimeout(function () {
        if (self._initCancelled) { resolve(); return; }
        // Eased animation from currentPct → 100 using ease-out
        // cubic for a natural deceleration into the final value.
        var snapStartedAt = Date.now();
        var snapFromPct = currentPct;
        var snapSpan = 100 - snapFromPct;
        function tick() {
          if (self._initCancelled) {
            self._snapRaf = null;
            resolve();
            return;
          }
          var t = Math.min((Date.now() - snapStartedAt) / SNAP_DURATION_MS, 1);
          var eased = 1 - Math.pow(1 - t, 3);
          self._setProgressUI(snapFromPct + snapSpan * eased);
          if (t < 1 && typeof requestAnimationFrame === "function") {
            self._snapRaf = requestAnimationFrame(tick);
          } else {
            self._setProgressUI(100);
            self._snapRaf = null;
            resolve();
          }
        }
        if (typeof requestAnimationFrame === "function") {
          tick();
        } else {
          // Defensive fallback for environments without RAF —
          // skip the animation, snap to 100, resolve.
          self._setProgressUI(100);
          resolve();
        }
      }, BEAT_MS);

      // Stash the beat timer so a teardown mid-beat can clear it.
      self._snapBeatTimer = beatTimer;
    });
  };

  DtfGangsheetBuilder.prototype._setProgressUI = function (pct) {
    var clamped = Math.max(0, Math.min(100, Math.round(pct)));
    if (this.refs.setupProgressFill) {
      this.refs.setupProgressFill.style.width = clamped + "%";
    }
    if (this.refs.setupProgressPercent) {
      this.refs.setupProgressPercent.textContent = clamped + "%";
    }
    // Stash for the smooth-completion path: completeSetupProgress
    // needs to know where to start its eased animation from.
    this._currentProgressPct = clamped;
  };

  DtfGangsheetBuilder.prototype.showSetupCaution = function (level, message) {
    if (!this.refs.setupCaution) return;
    this.refs.setupCaution.classList.remove("is-hidden", "is-error", "is-warning");
    this.refs.setupCaution.classList.add(level === "error" ? "is-error" : "is-warning");
    if (this.refs.setupCautionText) this.refs.setupCautionText.textContent = message;
  };

  DtfGangsheetBuilder.prototype.handleCautionUseOriginal = async function () {
    var asset = this.getSetupAsset();
    if (!asset) return;

    console.log("[ZUG][DTF][ENHANCE][DEBUG] Use original clicked for", asset.filename);

    asset.removeBackground = false;
    asset.processedUrl = null;
    asset.processedMaskUrl = null;
    asset.processedPreviewUrl = null;
    asset.previewUrl = asset.originalPreviewUrl;
    asset.dtfSafe = null;
    asset.originalAlphaMode = null;
    asset.dtfMaskSettings = null;
    asset.trimBounds = null;
    asset.processed.dtfMask = null;
    asset.processed.dtfMaskAfterUpscale = null;
    // Full asset reset: every per-mode cached mask result is now
    // stale (the source we'd compare against has been wiped).
    asset.processed.dtfMaskByMode = {};
    asset.processed.dtfMaskAfterUpscaleByMode = {};
    // Drop the cached upscaled bytes too. Without this, a later
    // re-enable of rmbg+upscale could feed a stale blob into the
    // chained dtf-mask call and produce a result inconsistent with
    // the (now reset) asset state.
    asset.upscaledBlob = null;
    if (asset.trimmedPreviewUrl) {
      try { URL.revokeObjectURL(asset.trimmedPreviewUrl); } catch (e) {}
      asset.trimmedPreviewUrl = null;
    }
    asset.enhancementStatus = "idle";
    asset.enhancementError = null;

    if (this.refs.setupCaution) this.refs.setupCaution.classList.add("is-hidden");
    this.refs.setupRmbg.classList.remove("is-on");
    this.refs.setupRmbg.setAttribute("aria-checked", "false");
    this.refs.setupRmbg.setAttribute("disabled", "true");

    this.refs.setupPreview.innerHTML = "<img src=\"" + asset.previewUrl + "\" alt=\"" + escapeHtml(asset.filename) + "\">";

    // Apr 2026 cutover: the live DTF builder enhancement path is a
    // single locked-server-side call (/api/enhance/dtf-builder-fast)
    // that ALWAYS performs both upscale + background removal. There
    // is no "upscale only" mode any more. The user just clicked
    // "Use original" specifically to avoid the bg-removal result —
    // re-firing the fast endpoint here would silently re-introduce
    // bg removal and contradict that intent. So we drop the upscale
    // toggle and place the original as-is.
    if (asset.upscale) {
      asset.upscale = false;
      var upscaleToggle = this.refs.setupUpscale;
      if (upscaleToggle) {
        upscaleToggle.classList.remove("is-on");
        upscaleToggle.setAttribute("aria-checked", "false");
      }
    }

    if (this._initCancelled || !this.canvas) return;

    if (asset.preflight) asset.preflight.lastCheckedKey = null;
    this.schedulePreflight(asset, 0);

    this.finalizePlacement(asset);
  };

  DtfGangsheetBuilder.prototype.handleCautionPreview = function () {
    var asset = this.getSetupAsset();
    if (!asset) return;
    var url = asset.processedUrl || asset.processedPreviewUrl || asset.previewUrl;
    if (url) {
      window.open(url, "_blank", "noopener");
    }
  };

  DtfGangsheetBuilder.prototype.finalizePlacement = async function (asset) {
    console.log("[ZUG][DTF][ENHANCE][DEBUG] finalizePlacement for", asset.filename, {
      removeBackground: asset.removeBackground,
      processedUrl: asset.processedUrl ? asset.processedUrl.substring(0, 80) : null,
      previewUrl: asset.previewUrl ? asset.previewUrl.substring(0, 80) : null,
      enhancementStatus: asset.enhancementStatus
    });

    if (this._initCancelled || !this.canvas) return;

    if (asset.removeBackground && asset.processedUrl) {
      try {
        var bounds = await computeAlphaTrimBounds(asset.processedUrl);
        if (this._initCancelled || !this.canvas) return;
        if (bounds) {
          var trimPreview = await createTrimmedPreview(asset.processedUrl, bounds);
          if (this._initCancelled || !this.canvas) return;
          if (trimPreview) {
            if (asset.trimmedPreviewUrl) {
              try { URL.revokeObjectURL(asset.trimmedPreviewUrl); } catch (e) {}
            }
            asset.trimBounds = bounds;
            asset.trimmedPreviewUrl = trimPreview;
            console.log("[ZUG][DTF][ENHANCE][DEBUG] alpha trim applied for", asset.filename, "bounds:", bounds);
          } else {
            if (asset.trimmedPreviewUrl) {
              try { URL.revokeObjectURL(asset.trimmedPreviewUrl); } catch (e) {}
              asset.trimmedPreviewUrl = null;
            }
            asset.trimBounds = null;
            console.warn("[ZUG][DTF][ENHANCE][DEBUG] trim preview creation failed for", asset.filename);
          }
        } else {
          if (asset.trimmedPreviewUrl) {
            try { URL.revokeObjectURL(asset.trimmedPreviewUrl); } catch (e) {}
            asset.trimmedPreviewUrl = null;
          }
          asset.trimBounds = null;
          console.warn("[ZUG][DTF][ENHANCE][DEBUG] no alpha bounds computed for", asset.filename, "(possibly tainted canvas or fully transparent)");
        }
      } catch (trimErr) {
        if (asset.trimmedPreviewUrl) {
          try { URL.revokeObjectURL(asset.trimmedPreviewUrl); } catch (e) {}
          asset.trimmedPreviewUrl = null;
        }
        asset.trimBounds = null;
        console.warn("[ZUG][DTF][ENHANCE][DEBUG] alpha trim failed:", trimErr);
      }
    }

    if (this._initCancelled || !this.canvas) return;
    this.state.selectedAssetId = asset.id;
    this.render();

    if (this.state.setupMode === "edit") {
      console.log("[ZUG][DTF][ASSET_EDIT] save complete, closing popup (no auto-place)", asset.filename);
      asset.lastQuantity = this.state.setupQty;
      asset.lockAspectRatio = this.state.setupLocked;
      // Refresh any existing fabric placements for this asset so
      // the canvas reflects the new image (e.g. background-removal
      // toggled off → placements should swap back to the original
      // upload). Without this, the fabric Image objects keep their
      // previously-loaded src and the change is invisible until
      // the artwork is re-placed manually.
      await this.refreshPlacementsForAsset(asset);
      this.closeSetupPopup();
      this.render();
      return;
    }

    asset.lastQuantity = this.state.setupQty;
    asset.lockAspectRatio = this.state.setupLocked;
    await this.placeSelectedAssetCopies(this.state.setupQty);
    if (this._initCancelled || !this.canvas) return;
    this.advanceSetupQueue();
  };

  DtfGangsheetBuilder.prototype.handleSetupSkip = function () {
    this.advanceSetupQueue();
  };

  DtfGangsheetBuilder.prototype.advanceSetupQueue = function () {
    this.state.setupIndex++;
    this.state.setupQty = 1;

    if (this.state.setupIndex >= this.state.setupQueue.length) {
      this.closeSetupPopup();
      this.render();
      // Initial Add-to-Sheet flow finished. On mobile, drop the
      // user back to the canvas view so they can see what they
      // just placed instead of landing on the Arrange drawer.
      if (window.innerWidth <= 768 && this._mobileCloseDrawer) {
        this._mobileCloseDrawer("setup-add-complete");
      }
      return;
    }

    this.renderSetupPopup();
  };

  // Public entry point — thin in-flight dedupe wrapper around the
  // real implementation. If a dtf-mask request is already in flight
  // for the SAME (chain × mode) pair on this asset, callers reuse
  // the existing promise instead of starting a second concurrent
  // request. Different chain or mode = legitimately different
  // backend call, so we don't dedupe across keys (the per-mode
  // cache slots in the impl already handle that case correctly).
  //
  // This guards against:
  //   - User clicking Save in setup popup faster than the mask call
  //     can complete.
  //   - Style segmented-control taps that cause the popup re-render
  //     path to call into the pipeline before the prior call settled.
  //   - Any future preflight/refresh-style trigger that might land
  //     while a setup-flow mask is mid-air.
  //
  // Pure rmbg-only and upscale-only flows are unaffected — the guard
  // is keyed on this single function and only short-circuits when an
  // identical request is already running.
  DtfGangsheetBuilder.prototype.applyBackgroundRemoval = function (asset) {
    var hasUpscaledInputForKey = !!(asset.processed && asset.processed.upscale && asset.processed.upscale.output_url);
    // Use the same composite (mode + removal_method) key the cache
    // uses, so a Clean Cut request (clean_logo|color_key_global)
    // and a hypothetical plain clean_logo request never collapse
    // onto the same in-flight slot.
    var reqConfigForKey = getBgMaskRequestConfig(asset.bgRemovalStyle);
    var maskKeyForInflight = getBgMaskCacheKey(reqConfigForKey);
    var inflightKey = maskKeyForInflight + "|" + (hasUpscaledInputForKey ? "chained" : "original");

    if (asset._dtfMaskInflightPromise && asset._dtfMaskInflightKey === inflightKey) {
      console.log("[ZUG][DTF][ENHANCE][DEBUG] dtf-mask IN-FLIGHT GUARD — reusing existing promise", {
        filename: asset.filename,
        assetId: asset.id,
        inflightKey: inflightKey
      });
      return asset._dtfMaskInflightPromise;
    }

    var self = this;
    var localPromise;
    localPromise = (async function () {
      try {
        return await self._applyBackgroundRemovalImpl(asset);
      } finally {
        // Ownership-checked clear. Without this guard, a long-running
        // request A (key1) finishing AFTER a newer request B (key2)
        // started would null out B's bookkeeping and let a third
        // duplicate call for key2 escape the dedupe.
        // Only clear if the slot still belongs to this exact promise.
        if (asset._dtfMaskInflightPromise === localPromise) {
          asset._dtfMaskInflightPromise = null;
          asset._dtfMaskInflightKey = null;
        }
      }
    })();
    asset._dtfMaskInflightKey = inflightKey;
    asset._dtfMaskInflightPromise = localPromise;
    return localPromise;
  };

  DtfGangsheetBuilder.prototype._applyBackgroundRemovalImpl = async function (asset) {
    // ─── Chain-aware + style-aware cache slot selection ───
    // After the April 2026 reorder, the mask step can run against
    // either the original file (rmbg-only path) or the upscaled
    // bytes (rmbg+upscale path). We cache each (chain × backend mode)
    // pair in its own slot so toggling either rmbg/upscale OR the
    // "Background removal style" doesn't poison sibling caches.
    //
    //   asset.processed.dtfMaskByMode[mode]              — rmbg-only
    //   asset.processed.dtfMaskAfterUpscaleByMode[mode]  — chained
    //
    // The legacy `dtfMask` / `dtfMaskAfterUpscale` slots are kept as
    // "currently-applied" pointers so the ~14 downstream readers
    // (asset chips, DPI badge, preflight, etc.) keep working without
    // change. We mirror the per-mode result into them on hit/write.
    var hasUpscaledInput = !!(asset.processed.upscale && asset.processed.upscale.output_url);
    var legacySlotKey = hasUpscaledInput ? "dtfMaskAfterUpscale" : "dtfMask";
    var perModeStoreKey = hasUpscaledInput ? "dtfMaskAfterUpscaleByMode" : "dtfMaskByMode";

    var reqConfig = getBgMaskRequestConfig(asset.bgRemovalStyle);
    var currentMode = reqConfig.mode;
    // Composite cache key: pure mode for default styles, mode +
    // removal_method for Clean Cut so its color-key-global result
    // never collides with a mode-only cached entry.
    var cacheKey = getBgMaskCacheKey(reqConfig);

    // Defensive: per-mode containers may be missing on assets
    // created before this field existed (or after a rmbg-flip
    // invalidation that re-initialized them as null).
    if (!asset.processed[perModeStoreKey] || typeof asset.processed[perModeStoreKey] !== "object") {
      asset.processed[perModeStoreKey] = {};
    }
    var cacheSlot = asset.processed[perModeStoreKey][cacheKey] || null;

    if (cacheSlot) {
      console.log("[ZUG][DTF][ENHANCE][DEBUG] dtf-mask CACHE HIT for", asset.filename,
        "store=" + perModeStoreKey, "cacheKey=" + cacheKey, "mode=" + currentMode, "bgStyle=" + (asset.bgRemovalStyle || "(default)"), {
        output_url: cacheSlot.output_url ? cacheSlot.output_url.substring(0, 60) : null,
        dtf_safe: cacheSlot.dtf_safe
      });
      var cached = cacheSlot;
      // Mirror to legacy "currently-applied" slot so downstream
      // readers (asset chips, DPI badge, preflight diagnostics, etc.)
      // see the right result for the active style.
      asset.processed[legacySlotKey] = cached;
      asset.processedUrl = cached.output_url;
      asset.processedMaskUrl = cached.output_url;
      asset.processedPreviewUrl = cached.preview_url || null;
      asset.previewUrl = cached.output_url;
      asset.dtfSafe = cached.dtf_safe;
      asset.originalAlphaMode = cached.original_alpha_mode || null;
      asset.dtfMaskSettings = cached.settings_used || null;
      asset.enhancementStatus = "done";
      asset.enhancementError = null;
      return;
    }

    // Resolve the actual bytes/URL we'll send to dtf-mask. Priority:
    //   1. asset.processedBlob (cached upscaled bytes, ideal — no
    //      re-fetch, no Backblaze 403 risk, hi-res input)
    //   2. asset.processed.upscale.output_url (upscaled URL — fall
    //      back to image_url passthrough if blob fetch fails)
    //   3. asset.file (rmbg-only path — original source file)
    var maskInputBlob = null;
    var maskInputName = (asset.filename || "input").replace(/\.[^.]+$/, "") + ".png";
    var maskInputUrlPassthrough = null;

    if (hasUpscaledInput) {
      // Prefer the dedicated upscaledBlob slot — it is ONLY ever set
      // to upscaled bytes by applyUpscale, so its provenance is
      // unambiguous even on reruns where the mask cache missed but
      // the upscale cache hit.
      if (asset.upscaledBlob) {
        maskInputBlob = asset.upscaledBlob;
      } else {
        try {
          var upResp = await fetch(asset.processed.upscale.output_url, { mode: "cors" });
          if (upResp.ok) {
            maskInputBlob = await upResp.blob();
            // Backfill the slot for any subsequent mask cache miss
            // in the same session.
            asset.upscaledBlob = maskInputBlob;
          } else {
            console.warn("[ZUG][DTF][ENHANCE][DEBUG] dtf-mask: upscaled-input fetch non-OK:", upResp.status, "→ image_url passthrough");
            maskInputUrlPassthrough = asset.processed.upscale.output_url;
          }
        } catch (upFetchErr) {
          console.warn("[ZUG][DTF][ENHANCE][DEBUG] dtf-mask: upscaled-input fetch failed:", upFetchErr.message, "→ image_url passthrough");
          maskInputUrlPassthrough = asset.processed.upscale.output_url;
        }
      }
    } else {
      if (!asset.file) {
        throw new Error("No source file available for background removal.");
      }
      maskInputBlob = asset.file;
      maskInputName = asset.filename;
    }

    console.log("[ZUG][DTF][ENHANCE][DEBUG] dtf-mask API REQUEST start", {
      filename: asset.filename,
      assetId: asset.id,
      legacySlotKey: legacySlotKey,
      perModeStoreKey: perModeStoreKey,
      cacheKey: cacheKey,
      backendMode: currentMode,
      bgRemovalStyle: asset.bgRemovalStyle || null,
      removal_method: reqConfig.removal_method || null,
      force_bg_removal: reqConfig.force_bg_removal === true,
      color_threshold: typeof reqConfig.color_threshold === "number" ? reqConfig.color_threshold : null,
      chainedFromUpscale: hasUpscaledInput,
      inputSource: maskInputBlob === asset.file
        ? "asset.file (original)"
        : (maskInputBlob ? "upscaled blob (chained)" : "upscaled image_url (chained, passthrough)"),
      inputSize: maskInputBlob ? maskInputBlob.size : null,
      endpoint: DCF_BASE + "/api/enhance/dtf-mask"
    });

    // DTF-specific dtf-mask request.
    //
    // `mode` is driven by the user's "Background removal style"
    // selection (asset.bgRemovalStyle) via mapBgRemovalStyleToMode()
    // with a 1:1 mapping:
    //   balanced       → balanced
    //   clean_cut      → clean_logo  + removal_method=color_key_global
    //                                + force_bg_removal=true
    //                                + color_threshold=25
    //   sticker_border → sticker_border
    //   missing/unknown → balanced (safe default)
    //
    // Clean Cut explicitly opts into the global color-key path so
    // a solid background colour is removed EVERYWHERE (including
    // inside enclosed interior regions of the artwork — e.g. the
    // inside of a shield logo). Without these overrides the
    // backend safety guard downgrades to a rembg silhouette mask
    // and preserves the interior colour, defeating the user's
    // intent. All other styles continue to send `mode` only and
    // let the backend pick its modern rembg / masking pipeline.
    var formData = new FormData();
    if (maskInputBlob) {
      formData.append("file", maskInputBlob, maskInputName);
    } else if (maskInputUrlPassthrough) {
      formData.append("image_url", maskInputUrlPassthrough);
    } else {
      throw new Error("No source available for background removal.");
    }
    formData.append("mode", reqConfig.mode);
    if (reqConfig.removal_method) {
      formData.append("removal_method", reqConfig.removal_method);
    }
    if (reqConfig.force_bg_removal === true) {
      // multipart strings — the backend coerces "true" → bool true.
      formData.append("force_bg_removal", "true");
    }
    if (typeof reqConfig.color_threshold === "number") {
      formData.append("color_threshold", String(reqConfig.color_threshold));
    }
    console.log("[ZUG][DTF][MASK] using DTF-specific backend path", {
      filename: asset.filename,
      assetId: asset.id,
      legacySlotKey: legacySlotKey,
      perModeStoreKey: perModeStoreKey,
      cacheKey: cacheKey,
      backendMode: currentMode,
      bgRemovalStyle: asset.bgRemovalStyle || null,
      removal_method: reqConfig.removal_method || null,
      force_bg_removal: reqConfig.force_bg_removal === true,
      color_threshold: typeof reqConfig.color_threshold === "number" ? reqConfig.color_threshold : null,
      chainedFromUpscale: hasUpscaledInput,
      mode: currentMode
    });

    // Single-retry-on-transient-network-error wrapper for the dtf-mask
    // POST. DCF confirmed that iOS / WKWebView occasionally drops the
    // proxy connection mid-request, surfacing as one of several error
    // shapes BEFORE any HTTP response is received:
    //   - `TypeError: Load failed`               (Safari)
    //   - `TypeError: Failed to fetch`           (Chromium)
    //   - `NetworkError: The network connection was lost` (older WK)
    //   - DOMException name="NetworkError"       (some WK versions)
    //
    // PRIOR BUG: the previous condition narrowly required either
    // `name === "TypeError"` OR a regex match against a fixed set of
    // message strings. This missed at least one live iOS shape on the
    // chained upscale → mask path and the retry never executed,
    // matching the symptom of "NO second dtf-mask request appears in
    // the logs". The pipeline then fell back to the upscaled image
    // after a single failed attempt.
    //
    // FIX: per the WHATWG fetch spec, fetch() only rejects on
    // (a) AbortError or (b) a transport-layer network/CORS failure —
    // it NEVER rejects on a 4xx/5xx HTTP response (those become a
    // resolved Response with `ok=false`). So the safest tight rule
    // that catches every iOS WKWebView variant without ever retrying
    // an HTTP error is simply:
    //
    //     attempts === 1 && err.name !== "AbortError"
    //
    // Retry rules (after fix):
    //   - retry ONLY when fetch() rejects (no HTTP response received)
    //   - retry on ANY non-Abort rejection (transport-layer only by
    //     spec, regardless of name/message wording)
    //   - DO NOT retry on AbortError (our 90s per-attempt timeout)
    //   - DO NOT retry on 4xx/5xx HTTP responses (those resolve, not
    //     reject — they reach the `response.json()` / status checks
    //     below and surface as normal backend errors)
    //   - exactly ONE retry, never more
    //
    // Each attempt gets its own AbortController + 90s budget, so a
    // transient drop early in attempt 1 doesn't eat into attempt 2's
    // time. FormData is reusable across fetch() calls in modern
    // browsers since the underlying Blob/File is still alive.
    var response;
    var attempts = 0;
    while (true) {
      attempts += 1;
      var attemptController = new AbortController();
      var attemptTimeoutId = setTimeout(function () { attemptController.abort(); }, 90000);
      try {
        response = await fetch(DCF_BASE + "/api/enhance/dtf-mask", {
          method: "POST",
          body: formData,
          signal: attemptController.signal
        });
        clearTimeout(attemptTimeoutId);
        break; // got an HTTP response (any status) — exit retry loop
      } catch (fetchErr) {
        clearTimeout(attemptTimeoutId);
        var fErrMsg = String((fetchErr && fetchErr.message) || "");
        var fErrName = (fetchErr && fetchErr.name) || "";
        // ALWAYS log the exact error shape we caught, so live captures
        // can definitively show whether retry was attempted, skipped,
        // or never reached. This was the missing diagnostic in the
        // prior round — the symptom "no second dtf-mask request" was
        // ambiguous between "retry condition false" and "code never
        // ran". With this log, future captures will pinpoint which.
        console.warn("[ZUG][DTF][ENHANCE][DEBUG] dtf-mask fetch() rejected", {
          attempt: attempts,
          name: fErrName,
          message: fErrMsg,
          filename: asset.filename,
          chainedFromUpscale: hasUpscaledInput
        });

        // Per WHATWG fetch spec, fetch() only ever rejects on
        // (a) AbortError or (b) a transport-layer network/CORS
        // failure. 4xx/5xx HTTP responses RESOLVE (with ok=false),
        // they don't reject. So "any non-Abort rejection" is by
        // definition a transient transport failure that the user's
        // requirements explicitly allow us to retry. This broadening
        // fixes the prior bug where iOS WKWebView shapes outside the
        // narrow regex (e.g. DOMException name="NetworkError")
        // silently fell through without retrying.
        var isTransientNetwork = (fErrName !== "AbortError");
        if (attempts === 1 && isTransientNetwork) {
          console.warn("[ZUG][DTF][ENHANCE][DEBUG] dtf-mask RETRYING (attempt 2 of 2) after 600ms backoff", {
            filename: asset.filename,
            firstAttemptError: fErrName + ": " + fErrMsg
          });
          // Brief backoff so we're not racing the same dropped
          // connection still being torn down on the iOS side.
          await new Promise(function (r) { setTimeout(r, 600); });
          continue;
        }
        console.error("[ZUG][DTF][ENHANCE][DEBUG] dtf-mask fetch() NETWORK ERROR (final, attempts=" + attempts + "):", fErrMsg, fetchErr);
        if (fErrName === "AbortError") {
          throw new Error("Background removal timed out after 90 seconds.");
        }
        throw new Error("Network error: " + (fErrMsg || "Load failed"));
      }
    }

    console.log("[ZUG][DTF][ENHANCE][DEBUG] dtf-mask response status:", response.status, response.statusText);

    var data = null;
    try {
      data = await response.json();
    } catch (parseErr) {
      console.error("[ZUG][DTF][ENHANCE][DEBUG] dtf-mask JSON parse error:", parseErr);
      if (!response.ok) {
        throw new Error("DTF mask failed (HTTP " + response.status + ").");
      }
      throw new Error("DTF mask response not valid JSON");
    }

    console.log("[ZUG][DTF][ENHANCE][DEBUG] dtf-mask response payload:", {
      status: data.status || null,
      output_url: data.output_url ? data.output_url.substring(0, 80) : null,
      preview_url: data.preview_url ? data.preview_url.substring(0, 80) : null,
      error: data.error || null,
      hint: data.hint || null,
      diagnostics: data.diagnostics || null
    });

    if (data.status === "error" || (!response.ok && data.error)) {
      var hint = data.hint ? " (" + data.hint + ")" : "";
      throw new Error(data.error || ("DTF mask failed (HTTP " + response.status + ")") + hint);
    }

    if (data.status === "fallback") {
      var diagF = data.diagnostics || {};
      console.warn("[ZUG][DTF][ENHANCE][DEBUG] dtf-mask FALLBACK for", asset.filename, "stage_failed=", diagF.processing_stage_failed);
      throw new Error("Background removal couldn't process this artwork.");
    }

    var outputUrl = data.output_url || data.processed_url || null;

    if (!outputUrl) {
      throw new Error("DTF mask response missing output URL.");
    }

    var diag = data.diagnostics || {};
    var dtfSafe = diag.dtf_safe !== undefined ? !!diag.dtf_safe : null;

    // Write to BOTH:
    //   1. the per-mode store keyed by backend mode, so a future
    //      switch back to the same Background removal style hits
    //      cache instead of re-fetching;
    //   2. the legacy "currently-applied" slot (dtfMask /
    //      dtfMaskAfterUpscale) so the ~14 downstream readers see
    //      the new result without changes.
    var maskRecord = {
      output_url: outputUrl,
      preview_url: data.preview_url || null,
      dtf_safe: dtfSafe,
      original_alpha_mode: diag.original_alpha_mode || null,
      settings_used: data.settings_used || null,
      mode: currentMode,
      bg_removal_style: asset.bgRemovalStyle || null
    };
    // NOTE: cache writes are deliberately deferred until AFTER the
    // reachability probe below succeeds. Writing here would poison
    // the per-mode cache with an unreachable Backblaze URL — the
    // next render would hit "cache HIT" on a broken record.

    asset.processedUrl = outputUrl;
    asset.processedMaskUrl = outputUrl;
    asset.processedPreviewUrl = data.preview_url || null;
    asset.previewUrl = outputUrl;
    asset.dtfSafe = dtfSafe;
    asset.originalAlphaMode = diag.original_alpha_mode || null;
    asset.dtfMaskSettings = data.settings_used || null;

    console.log("[ZUG][DTF][SRC_TRACE] dtf-mask SUCCESS — assigning processedUrl/previewUrl from backend output", {
      assetId: asset.id,
      filename: asset.filename,
      dtfMaskOutputUrl: outputUrl.substring(0, 100),
      dtfMaskPreviewUrl: data.preview_url ? data.preview_url.substring(0, 100) : null,
      assignedTo: ["asset.processedUrl", "asset.processedMaskUrl", "asset.previewUrl"],
      previousPreviewUrl: asset.originalPreviewUrl ? "(original blob URL — overwritten)" : null,
      dtfSafe: dtfSafe,
      originalAlphaMode: diag.original_alpha_mode || null
    });

    if (asset.dtfSafe === false) {
      console.warn("[ZUG][DTF][ENHANCE][DEBUG] dtf_safe=false for", asset.filename, diag);
    }

    // Verify the returned storage URL is actually reachable by the browser.
    // The DCF backend has been observed to return Backblaze URLs that 403 to
    // every client (private bucket / expired signed URL), which silently breaks
    // both the thumbnail preview and the downstream upscale step. Surface a
    // clear, actionable error here instead.
    try {
      var probe = await fetch(outputUrl, { method: "GET", mode: "cors", cache: "no-store" });
      if (!probe.ok) {
        throw new Error("HTTP " + probe.status);
      }
      // Cache the bytes so the upscale step doesn't have to refetch.
      asset.processedBlob = await probe.blob();
      // Probe succeeded — NOW commit the mask record to the per-mode
      // cache + legacy slot. Doing this before the probe would
      // poison the cache with unreachable URLs (next call would hit
      // "cache HIT" on a 403 record and silently break the preview).
      if (!asset.processed[perModeStoreKey] || typeof asset.processed[perModeStoreKey] !== "object") {
        asset.processed[perModeStoreKey] = {};
      }
      // Use the composite cacheKey (mode + removal_method when
      // present), so Clean Cut's color-key-global result is stored
      // in its own slot and never collapses onto a plain
      // "clean_logo" cache entry left by an earlier code path.
      asset.processed[perModeStoreKey][cacheKey] = maskRecord;
      asset.processed[legacySlotKey] = maskRecord;
    } catch (probeErr) {
      console.error("[ZUG][DTF][ENHANCE][DEBUG] dtf-mask output URL UNREACHABLE:", outputUrl, probeErr.message);
      asset.enhancementStatus = "error";
      asset.enhancementError = "Background-removal storage is unreachable (the DCF backend returned a Backblaze URL that returns 403). Ask the DCF maintainer to make zm-dcf-images/enhance/dtf-mask/* publicly readable or return a longer-lived signed URL.";
      // Wipe the broken URL so the thumbnail falls back to the original file.
      asset.processedUrl = null;
      asset.processedMaskUrl = null;
      asset.previewUrl = null;
      throw new Error(asset.enhancementError);
    }

    asset.enhancementStatus = "done";
    asset.enhancementError = null;

    // Surface the backend's actual choice of removal method so we
    // can confirm — in live logs — that Clean Cut's
    // color_key_global override took effect (vs. being silently
    // downgraded to a rembg silhouette by the safety guard).
    var diagFinal = data.diagnostics || {};
    console.log("[ZUG][DTF][ENHANCE][DEBUG] dtf-mask COMPLETE", asset.filename, {
      dtfSafe: asset.dtfSafe,
      alphaMode: asset.originalAlphaMode,
      backendMode: currentMode,
      bgRemovalStyle: asset.bgRemovalStyle || null,
      removal_method_requested: reqConfig.removal_method || null,
      removal_method_used: diagFinal.removal_method_used || null,
      cacheKey: cacheKey,
      outputUrl: outputUrl.substring(0, 80)
    });
  };

  // ─── DTF Builder Fast (single-call cutover, Apr 2026) ───
  // POST {DCF_BASE}/api/enhance/dtf-builder-fast
  //
  // The endpoint is locked server-side: send ONLY `file`. No
  // pipeline_profile, no mode, no print dims, no remove_background
  // / upscale knobs. Response carries:
  //   { status:"ok", job_id, output_url, mask_url, preview_url,
  //     upscale_engine, bg_engine_used, fallback_used,
  //     bad_mask_reason, input, final, preview, mask_quality,
  //     timings, elapsed_seconds }
  // On (res.ok && data.status === "ok") we treat as success;
  // anything else throws.
  //
  // Cached per-asset in `asset.processed.fast` so re-entries
  // (edit popup, save/cancel cycle, sheet rebuilds) reuse the
  // existing artifacts instead of re-hitting DCF.
  DtfGangsheetBuilder.prototype.applyDtfBuilderFast = async function (asset) {
    if (!asset || !asset.file) {
      throw new Error("No source file available for enhancement.");
    }

    // Defense-in-depth gate: even though the call site (handleSetupAdd)
    // already checks shouldEnhance, also refuse here. Any future caller
    // that forgets the gate would otherwise silently re-introduce the
    // April-2026 regression where /dtf-builder-fast fired against an
    // asset whose user toggles were both off.
    if (asset.upscale !== true && asset.removeBackground !== true) {
      console.warn("[ZUG][DTF][ENHANCE][GATE] applyDtfBuilderFast called with both toggles off — refusing", {
        assetId: asset.id,
        upscale: asset.upscale,
        removeBackground: asset.removeBackground
      });
      return null;
    }

    // Cache key encodes the toggle combo because the backend now
    // honors them — same file with different toggles produces a
    // different result (upscale_only vs bg_only vs upscale_and_bg)
    // and must NOT replay across combos.
    var upscaleFlag = (asset.upscale === true);
    var rmbgFlag = (asset.removeBackground === true);
    // bg_style is per-asset (set from the Background style picker).
    // The dtf-builder-fast endpoint supports exactly three values:
    // "balanced", "clean_cut", "sticker_border". Anything else
    // (including missing or a stale legacy mode) collapses to
    // "balanced" — matching the picker's default and the prior
    // request contract.
    var BG_STYLE_FAST_ALLOWED = { balanced: 1, clean_cut: 1, sticker_border: 1 };
    var bgStyle = BG_STYLE_FAST_ALLOWED[asset.bgRemovalStyle] ? asset.bgRemovalStyle : "balanced";
    // Include bg_style in the cache key — each style produces a
    // visibly different output server-side, so they must not
    // share a cache slot. b=balanced, c=clean_cut, s=sticker_border.
    var bgKeyChar = bgStyle === "clean_cut" ? "c" : (bgStyle === "sticker_border" ? "s" : "b");
    var cacheKey = (upscaleFlag ? "u1" : "u0") + "_" + (rmbgFlag ? "r1" : "r0") + "_b" + bgKeyChar;

    if (asset.processed && asset.processed.fast && asset.processed.fast.cacheKey === cacheKey && asset.processed.fast.output_url) {
      var cached = asset.processed.fast;
      console.log("[ZUG][DTF][ENHANCE][DEBUG] dtf-builder-fast CACHE HIT for", asset.filename, "key=" + cacheKey);
      asset.processedUrl = cached.output_url;
      asset.maskUrl = cached.mask_url || null;
      asset.processedMaskUrl = cached.mask_url || null;
      asset.previewUrl = cached.preview_url || cached.output_url;
      asset.processedPreviewUrl = cached.preview_url || null;
      asset.upscaleEngine = cached.upscale_engine || null;
      asset.bgEngineUsed = cached.bg_engine_used || null;
      asset.fallbackUsed = cached.fallback_used === true;
      asset.finalWidthPx = cached.final && cached.final.width_px || null;
      asset.finalHeightPx = cached.final && cached.final.height_px || null;
      asset.enhanceTimings = cached.timings || null;
      asset.modeUsed = cached.mode_used || null;
      asset.intrinsicWidth = asset.finalWidthPx || asset.intrinsicWidth;
      asset.intrinsicHeight = asset.finalHeightPx || asset.intrinsicHeight;
      asset.dtfSafe = true;
      asset.enhancementStatus = "done";
      asset.enhancementError = null;
      return cached;
    }

    var endpoint = DCF_BASE + "/api/enhance/dtf-builder-fast";
    var fd = new FormData();
    fd.append("file", asset.file, asset.filename || "artwork.png");
    // Honor the new toggle contract — backend reads these strings.
    fd.append("upscale", upscaleFlag ? "true" : "false");
    fd.append("remove_background", rmbgFlag ? "true" : "false");
    // bg_style routes the bg-removal pipeline server-side. The
    // dtf-builder-fast endpoint supports exactly three values:
    //   "balanced"       → rembg (alpha matting, soft edges)
    //   "clean_cut"      → color_key_global (hard edges, removes interior matches)
    //   "sticker_border" → clean cut + white sticker outline
    // Default "balanced" if missing so behavior matches the prior contract.
    fd.append("bg_style", bgStyle);

    var controller = new AbortController();
    // Conditional timeout: upscale+bg cold starts on DCF can take
    // 60–90s, and Safari/some browsers were dropping the connection
    // around 46s on the prior 120s ceiling because nothing was
    // streaming back. Bump to 180s only when upscale is requested;
    // bg-only / upscale-off requests stay on the shorter 120s
    // ceiling so timeout-driven failures still surface quickly
    // for the fast path.
    var TIMEOUT_MS = (asset.upscale === true) ? 180000 : 120000;
    var timeoutId = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);

    var startedAt = (typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now();
    console.log("[ZUG][DTF][ENHANCE][DEBUG] dtf-builder-fast POST about to fire", {
      filename: asset.filename,
      assetId: asset.id,
      endpoint: endpoint,
      bodyMB: asset.file && asset.file.size ? +(asset.file.size / 1048576).toFixed(2) : null,
      timeout_ms: TIMEOUT_MS,
      upscale: upscaleFlag,
      remove_background: rmbgFlag,
      bg_style: bgStyle
    });
    console.log("[ZUG][DTF][REQUEST]", {
      bg_style: bgStyle,
      remove_background: rmbgFlag,
      upscale: upscaleFlag
    });

    var res;
    try {
      res = await fetch(endpoint, { method: "POST", body: fd, signal: controller.signal });
    } catch (fetchErr) {
      clearTimeout(timeoutId);
      var elapsed = ((typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now()) - startedAt;
      // Richer failure context — when this fires we want to know
      // exactly which toggle combo + bg_style + how-close-to-the-
      // ceiling the request was, so we can tell a real backend
      // failure from a frontend timeout race.
      console.error("[ZUG][DTF][ENHANCE][DEBUG] dtf-builder-fast fetch() failed", {
        message: fetchErr.message,
        name: fetchErr.name,
        elapsedMs: Math.round(elapsed),
        timeout_ms: TIMEOUT_MS,
        upscale: asset.upscale === true,
        removeBackground: asset.removeBackground === true,
        bgRemovalStyle: asset.bgRemovalStyle || null,
        endpoint: endpoint
      });
      if (fetchErr.name === "AbortError") {
        throw new Error("AI enhancement is taking longer than expected. Please try again.");
      }
      throw new Error("AI enhancement couldn\u2019t be completed right now. Please try again.");
    }
    clearTimeout(timeoutId);

    var data = null;
    try {
      data = await res.json();
    } catch (parseErr) {
      console.error("[ZUG][DTF][ENHANCE][DEBUG] dtf-builder-fast JSON parse error:", parseErr);
      throw new Error("AI enhancement couldn\u2019t be completed right now. Please try again.");
    }

    if (!res.ok || !data || data.status !== "ok") {
      console.error("[ZUG][DTF][ENHANCE][DEBUG] dtf-builder-fast non-ok response", {
        httpStatus: res.status,
        bodyStatus: data && data.status,
        stage: data && data.stage,
        detail: data && (data.detail || data.error),
        job_id: data && data.job_id
      });
      var calmMsg = (data && data.detail) ? data.detail : ("AI enhancement couldn\u2019t be completed right now. Please try again.");
      throw new Error(calmMsg);
    }

    if (!data.output_url || !data.preview_url) {
      console.error("[ZUG][DTF][ENHANCE][DEBUG] dtf-builder-fast missing required URLs", data);
      throw new Error("AI enhancement couldn\u2019t be completed right now. Please try again.");
    }

    console.log("[ZUG][DTF_FAST]", {
      bgStyleUsed: data.bg_style_used,
      removalMethodUsed: data.removal_method_used
    });

    // bg_style integrity check — verify the backend actually ran
    // the style we asked for. DCF can silently fall back to
    // "balanced" if it receives an unrecognised value, and we
    // hit exactly that bug recently when "sticker_border" was
    // being silently rewritten on the wire. Logging only — never
    // interrupts the user, never mutates the request, never
    // shows UI. asset._bgStyleMismatch is left for future debug
    // tooling to consume.
    var bgStyleRequested = data.bg_style_requested;
    var bgStyleUsed = data.bg_style_used;
    // When bg removal is disabled the backend correctly reports
    // bg_style_used = "n/a (bg removal disabled)" (or any "n/a"
    // shaped sentinel). That's expected, not a regression — skip
    // the mismatch comparison entirely so we don't spam warnings
    // on the upscale-only path.
    var rmbgDisabled = (data.remove_background === false) || (asset.removeBackground === false);
    var usedIsNotApplicable = typeof bgStyleUsed === "string" && /^n\/a\b/i.test(bgStyleUsed);
    if (rmbgDisabled && usedIsNotApplicable) {
      console.log("[ZUG][DTF_BG_STYLE] skipped (bg removal disabled)", {
        requested: bgStyleRequested,
        used: bgStyleUsed,
        remove_background: data.remove_background
      });
      asset._bgStyleMismatch = false;
    } else if (bgStyleRequested && bgStyleUsed) {
      if (bgStyleRequested !== bgStyleUsed) {
        console.warn("[ZUG][DTF_BG_STYLE] mismatch", {
          requested: bgStyleRequested,
          used: bgStyleUsed,
          assetBgStyle: asset.bgRemovalStyle
        });
        asset._bgStyleMismatch = true;
      } else {
        console.log("[ZUG][DTF_BG_STYLE] confirmed", {
          requested: bgStyleRequested,
          used: bgStyleUsed
        });
        asset._bgStyleMismatch = false;
      }
    }

    // Persist on the asset. mask_url being null is VALID for the
    // upscale_only mode — the backend deliberately omits a mask
    // when bg removal wasn't requested. Downstream UI must not
    // surface a "missing mask" warning for that case.
    asset.processedUrl = data.output_url;
    asset.maskUrl = data.mask_url || null;
    asset.processedMaskUrl = data.mask_url || null;
    asset.previewUrl = data.preview_url;
    asset.processedPreviewUrl = data.preview_url;
    asset.upscaleEngine = data.upscale_engine || null;
    asset.bgEngineUsed = data.bg_engine_used || null;
    asset.fallbackUsed = data.fallback_used === true;
    asset.finalWidthPx = data.final && data.final.width_px || null;
    asset.finalHeightPx = data.final && data.final.height_px || null;
    asset.enhanceTimings = data.timings || null;
    asset.modeUsed = data.mode_used || null;
    if (asset.finalWidthPx) asset.intrinsicWidth = asset.finalWidthPx;
    if (asset.finalHeightPx) asset.intrinsicHeight = asset.finalHeightPx;
    asset.dtfSafe = true;
    asset.enhancementStatus = "done";
    asset.enhancementError = null;

    if (asset.placedWidthInches && asset.placedHeightInches) {
      var dpiX = asset.intrinsicWidth / Math.max(asset.placedWidthInches, 0.01);
      var dpiY = asset.intrinsicHeight / Math.max(asset.placedHeightInches, 0.01);
      asset.estimatedDpi = round(Math.min(dpiX, dpiY), 0);
    }

    if (!asset.processed) asset.processed = {};
    asset.processed.fast = {
      cacheKey: cacheKey,
      job_id: data.job_id || null,
      output_url: data.output_url,
      mask_url: data.mask_url || null,
      preview_url: data.preview_url,
      upscale_engine: data.upscale_engine || null,
      bg_engine_used: data.bg_engine_used || null,
      fallback_used: data.fallback_used === true,
      mode_used: data.mode_used || null,
      final: data.final || null,
      preview: data.preview || null,
      timings: data.timings || null
    };

    console.log("[ZUG][DTF_FAST]", {
      assetId: asset.id,
      modeUsed: data.mode_used,
      upscaleEngine: data.upscale_engine,
      bgEngineUsed: data.bg_engine_used,
      fallbackUsed: data.fallback_used,
      final: (data.final && data.final.width_px) + "x" + (data.final && data.final.height_px),
      total: data.elapsed_seconds
    });

    return data;
  };

  DtfGangsheetBuilder.prototype.applyUpscale = async function (asset) {
    // April 2026 chain reorder: upscale always runs against the
    // original source first (no rmbg precedes it anymore), so the
    // cache key is unconditional. The previous "upscale_rmbg" slot
    // (= upscale of the masked image) is gone — its successor is
    // asset.processed.dtfMaskAfterUpscale, which lives on the mask
    // side of the pipeline.
    var cacheKey = "upscale";
    if (asset.processed[cacheKey]) {
      console.log("[ZUG][DTF][ENHANCE][DEBUG] upscale CACHE HIT for", asset.filename, "key=" + cacheKey);
      var cached = asset.processed[cacheKey];
      asset.processedUrl = cached.output_url;
      // Restore the display sidecar from cache so cache-hit reruns
      // (edit popup re-open, pipeline re-runs, etc.) keep painting
      // canvas / card thumb / popup with the lightweight display
      // render instead of regressing to the heavy full-final URL.
      asset.displayUrl = cached.display_url || null;
      asset.engineUsed = cached.engine_used || cached.engine || null;
      asset.usedAi = cached.used_ai_engine === true;
      asset.usedBicubicFallback = cached.used_bicubic_fallback === true;
      asset.previewUrl = asset.displayUrl || cached.output_url;
      asset.intrinsicWidth = cached.output_width;
      asset.intrinsicHeight = cached.output_height;
      var dpiXc = asset.intrinsicWidth / Math.max(asset.placedWidthInches, 0.01);
      var dpiYc = asset.intrinsicHeight / Math.max(asset.placedHeightInches, 0.01);
      asset.estimatedDpi = round(Math.min(dpiXc, dpiYc), 0);
      asset.upscaleEngine = cached.engine || null;
      asset.enhancementStatus = "done";
      asset.enhancementError = null;
      console.log("[ZUG][DTF][SRC_TRACE][CACHE_HIT]", {
        assetId: asset.id,
        filename: asset.filename,
        processedUrl: asset.processedUrl ? asset.processedUrl.substring(0, 120) : null,
        displayUrl: asset.displayUrl ? asset.displayUrl.substring(0, 120) : null,
        engineUsed: asset.engineUsed,
        usedAi: asset.usedAi,
        usedBicubicFallback: asset.usedBicubicFallback,
        sourceUsedFor: {
          canvas: asset.previewUrl === asset.displayUrl ? "displayUrl" : "processedUrl",
          popup: asset.previewUrl === asset.displayUrl ? "displayUrl" : "processedUrl",
          cardThumbnail: asset.previewUrl === asset.displayUrl ? "displayUrl" : "processedUrl",
          lightbox: "processedUrl"
        }
      });
      if (asset.usedBicubicFallback) {
        console.warn("[ZUG][DTF][SRC_TRACE][BICUBIC_FALLBACK][CACHE_HIT]", asset.filename, {
          assetId: asset.id,
          engineUsed: asset.engineUsed
        });
      }
      return;
    }

    // AI Upscale is now wired to the new DCF auto-enhance endpoint.
    // The legacy /api/enhance/upscale path was prone to "Load failed"
    // on Safari for multi-MB iPhone JPGs (proxy idle drops + long-
    // running CPU upscales). auto-enhance returns faster, applies a
    // recommended scale rather than blindly 4×, caps output, and is
    // streaming-friendlier through the same edge.
    //
    // remove_background is intentionally NOT sent here — the rmbg
    // toggle is a separate user choice and runs through dtf-mask
    // earlier in applyEnhancementPipeline.
    var inputMode = asset.processedUrl ? "url" : "file";
    var UPSCALE_ENDPOINT = DCF_BASE + "/api/enhance/auto-enhance";
    console.log("[ZUG][DTF][ENHANCE][DEBUG] upscale API REQUEST start", {
      filename: asset.filename,
      assetId: asset.id,
      cacheKey: cacheKey,
      inputMode: inputMode,
      inputUrl: asset.processedUrl ? asset.processedUrl.substring(0, 80) : null,
      placedWidthInches: asset.placedWidthInches,
      placedHeightInches: asset.placedHeightInches,
      endpoint: UPSCALE_ENDPOINT
    });

    if (!asset.file && !asset.processedUrl) {
      throw new Error("No source file or processed URL available for upscaling.");
    }

    // After the chain reorder, upscale always operates on the
    // original source file. There is no upstream-processed input
    // here anymore (rmbg runs AFTER upscale, not before).
    var formData = new FormData();
    formData.append("file", asset.file, asset.filename);
    // auto-enhance picks a recommended scale rather than always 4×.
    // mode=auto + a sane output cap keeps the response payload small
    // and the connection short — the two factors that were producing
    // "Load failed" on the legacy /upscale path.
    formData.append("mode", "auto");
    formData.append("max_output_dimension", "4000");
    // DTF profile + display sidecar. The DCF auto-enhance backend
    // returns a lightweight `display.display_url` resized to
    // display_max_dim alongside the full final.output_url, so
    // canvas / card / popup previews can paint instantly while the
    // lightbox + production export keep using the full file.
    // pipeline_profile=dtf scopes the profile-specific tuning DCF
    // applies (sharpening, format, etc.) to DTF-only behaviour.
    formData.append("pipeline_profile", "dtf");
    formData.append("display_max_dim", "2400");
    // Interactive latency cap. Without this, DCF runs the slow
    // default DTF path and the request reliably times out around
    // ~46s on Safari ("Load failed"). engine_input_max_dim=400
    // tells DCF to downscale the engine input before AI upscale,
    // which is the only configuration the backend has tuned for
    // interactive (sub-30s) responses.
    var ENGINE_INPUT_MAX_DIM = "400";
    formData.append("engine_input_max_dim", ENGINE_INPUT_MAX_DIM);

    console.log("[ZUG][DTF][AUTO_ENHANCE_REQUEST]", {
      assetId: asset.id,
      filename: asset.filename,
      pipeline_profile: "dtf",
      display_max_dim: "2400",
      engine_input_max_dim: ENGINE_INPUT_MAX_DIM
    });

    // Tell the backend the artwork's INTENDED print size at the
    // current placed dimensions. Without these, /api/enhance/auto-
    // enhance has no way to compute effective DPI, so it returns:
    //   decision.should_upscale = false
    //   reason = "sufficient_quality"
    //   diagnostics.effective_dpi = null
    //   final.engine = null
    // …regardless of how oversized the placement actually is. That
    // breaks the entire "auto" intelligence — every image looks
    // "good enough" because the backend can't see the print target.
    //
    // placedWidthInches / placedHeightInches are set on the asset
    // at upload (defaults from sheet width) and updated by both the
    // setup popup and the edit popup before applyUpscale is called.
    var printWidthIn = Math.max(asset.placedWidthInches || 0, 0.01);
    var printHeightIn = Math.max(asset.placedHeightInches || 0, 0.01);
    formData.append("print_width_in", printWidthIn.toFixed(4));
    formData.append("print_height_in", printHeightIn.toFixed(4));

    var upscaleController = new AbortController();
    // Safety cap on the auto-enhance fetch. With engine_input_max_dim=400
    // the backend should respond well under 30s, but we keep a generous
    // ceiling so transient network slowness doesn't trip the abort.
    var UPSCALE_TIMEOUT_MS = 120000;
    console.log("[ZUG][DTF][AUTO_ENHANCE_TIMEOUT]", {
      assetId: asset.id,
      filename: asset.filename,
      timeout_ms: UPSCALE_TIMEOUT_MS
    });
    var upscaleTimeoutId = setTimeout(function () { upscaleController.abort(); }, UPSCALE_TIMEOUT_MS);

    // Brief pre-flight context. Lighter than the prior TEMP-DEBUG block
    // since the migration itself is the fix; this is verification-only.
    var __upscaleStartedAt = (typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now();
    var __upscaleBodyBytes = (asset.file && asset.file.size) || null;
    console.log("[ZUG][DTF][ENHANCE][DEBUG] upscale POST about to fire", {
      filename: asset.filename,
      endpoint: UPSCALE_ENDPOINT,
      bodyMB: __upscaleBodyBytes ? +(__upscaleBodyBytes / 1048576).toFixed(2) : null,
      sourceField: "asset.file (original — chain reorder April 2026)"
    });

    var response;
    try {
      response = await fetch(UPSCALE_ENDPOINT, {
        method: "POST",
        body: formData,
        signal: upscaleController.signal
      });
    } catch (fetchErr) {
      clearTimeout(upscaleTimeoutId);
      var __upscaleElapsedMs = ((typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now()) - __upscaleStartedAt;
      console.error("[ZUG][DTF][ENHANCE][DEBUG] upscale fetch() failed", {
        message: fetchErr.message,
        name: fetchErr.name,
        elapsedMs: Math.round(__upscaleElapsedMs),
        endpoint: UPSCALE_ENDPOINT
      });
      if (fetchErr.name === "AbortError") {
        // Calm, customer-safe wording — no raw transport jargon.
        throw new Error("AI enhancement is taking longer than expected. Please try again.");
      }
      throw new Error("AI enhancement couldn\u2019t be completed right now. Please try again.");
    }
    clearTimeout(upscaleTimeoutId);

    console.log("[ZUG][DTF][ENHANCE][DEBUG] upscale response status:", response.status, response.statusText);

    var upscaleData = null;
    try {
      upscaleData = await response.json();
    } catch (parseErr) {
      console.error("[ZUG][DTF][ENHANCE][DEBUG] upscale JSON parse error:", parseErr);
      if (!response.ok) {
        throw new Error("DCF upscale failed (HTTP " + response.status + ").");
      }
      throw new Error("Upscale response not valid JSON");
    }

    if (upscaleData.status === "error" || (!response.ok && upscaleData.error)) {
      console.error("[ZUG][DTF][ENHANCE][DEBUG] auto-enhance returned error payload", upscaleData);
      throw new Error("AI enhancement couldn\u2019t be completed right now. Please try again.");
    }

    var data = upscaleData;
    // auto-enhance returns a nested `final` block (output_url, dims,
    // engine). Fall back to the legacy flat shape so this code keeps
    // working if the backend ever switches back or A/Bs the response.
    var finalBlock = data.final || data;
    // `display` is a new sidecar (Apr 2026) that ships a smaller,
    // display-only render of the same final pixels (resized to
    // display_max_dim). Treat as optional so the code is forward-
    // and backward-compatible with older DCF responses.
    var displayBlock = data.display || null;

    // Surface decision intelligence (effective_dpi, should_upscale,
    // reason) directly in the log so we can verify the print-size
    // payload is reaching the backend and producing real numbers
    // instead of the null/sufficient_quality default we get when
    // print_width_in / print_height_in are missing.
    var diagBlock = data.diagnostics || {};
    console.log("[ZUG][DTF][ENHANCE][DEBUG] auto-enhance response payload:", {
      status: data.status,
      decision: data.decision || null,
      should_upscale: data.decision && data.decision.should_upscale,
      reason: data.decision && data.decision.reason,
      effective_dpi: diagBlock.effective_dpi,
      input: data.input || null,
      scale: data.scale || null,
      steps: data.steps || null,
      output_url: finalBlock.output_url ? finalBlock.output_url.substring(0, 80) : null,
      output_width: finalBlock.output_width,
      output_height: finalBlock.output_height,
      engine: finalBlock.engine,
      elapsed_seconds: data.elapsed_seconds || null
    });

    if (data.status && data.status !== "ok") {
      throw new Error("AI enhancement couldn\u2019t be completed right now. Please try again.");
    }

    var outputUrl = finalBlock.output_url || finalBlock.upscaled_url || null;

    if (!outputUrl) {
      console.error("[ZUG][DTF][ENHANCE][DEBUG] auto-enhance missing output URL", data);
      throw new Error("AI enhancement couldn\u2019t be completed right now. Please try again.");
    }

    // auto-enhance may decide NOT to upscale (small deficit / already
    // good quality). We still treat it as success — the asset just
    // gets the cleaned/final output URL with whatever dims came back.
    var newWidth = finalBlock.output_width || asset.intrinsicWidth;
    var newHeight = finalBlock.output_height || asset.intrinsicHeight;

    asset.processed[cacheKey] = {
      output_url: outputUrl,
      output_width: newWidth,
      output_height: newHeight,
      engine: finalBlock.engine || null,
      // Cache the new auto-enhance sidecar fields so subsequent
      // cache-hit reruns can rehydrate displayUrl + AI/bicubic
      // diagnostics without re-hitting DCF.
      display_url: displayBlock && displayBlock.display_url ? displayBlock.display_url : null,
      engine_used: finalBlock.engine_used != null ? finalBlock.engine_used : (finalBlock.engine || null),
      used_ai_engine: finalBlock.used_ai_engine === true,
      used_bicubic_fallback: finalBlock.used_bicubic_fallback === true
    };

    var preUpscaleProcessed = asset.processedUrl;
    asset.processedUrl = outputUrl;
    // Prefer the lightweight `display.display_url` for previews
    // (canvas, sheet, asset card thumbnail, setup popup main
    // preview) so the swap from "original upload" → "enhanced
    // preview" paints instantly even when the full final file is
    // multi-MB. The lightbox + production export keep using
    // asset.processedUrl below (full file truth source).
    var displayUrl = displayBlock && displayBlock.display_url ? displayBlock.display_url : null;
    asset.displayUrl = displayUrl;
    asset.engineUsed = (finalBlock.engine_used != null ? finalBlock.engine_used : finalBlock.engine) || null;
    asset.usedAi = finalBlock.used_ai_engine === true;
    asset.usedBicubicFallback = finalBlock.used_bicubic_fallback === true;
    // previewUrl is the workhorse field every renderer reads
    // (canvas, card thumb, setup popup, edit popup, sheet preview).
    // Point it at the display sidecar when present so the swap is
    // immediate and lightweight; fall back to the full final URL
    // when DCF didn't ship a display block (older backend or
    // display generation was skipped).
    asset.previewUrl = displayUrl || outputUrl;
    asset.intrinsicWidth = newWidth;
    asset.intrinsicHeight = newHeight;

    // SRC_TRACE — explicit map of which URL each surface uses, so
    // a single log line answers "what did the canvas see vs. what
    // did the lightbox see vs. what's actually in the export?".
    console.log("[ZUG][DTF][SRC_TRACE]", {
      assetId: asset.id,
      filename: asset.filename,
      originalUrl: asset.originalPreviewUrl || null,
      processedUrl: asset.processedUrl ? asset.processedUrl.substring(0, 120) : null,
      displayUrl: asset.displayUrl ? asset.displayUrl.substring(0, 120) : null,
      engineUsed: asset.engineUsed,
      usedAi: asset.usedAi,
      usedBicubicFallback: asset.usedBicubicFallback,
      pipelineProfile: finalBlock.pipeline_profile || null,
      displayResizedFromFinal: displayBlock ? displayBlock.resized_from_final === true : null,
      displayDims: displayBlock ? { w: displayBlock.display_width || null, h: displayBlock.display_height || null } : null,
      sourceUsedFor: {
        canvas: asset.previewUrl ? (asset.previewUrl === asset.displayUrl ? "displayUrl" : "processedUrl") : null,
        popup: asset.previewUrl ? (asset.previewUrl === asset.displayUrl ? "displayUrl" : "processedUrl") : null,
        cardThumbnail: asset.previewUrl ? (asset.previewUrl === asset.displayUrl ? "displayUrl" : "processedUrl") : null,
        lightbox: asset.processedUrl ? "processedUrl" : (asset.previewUrl ? "previewUrl" : "originalUrl")
      },
      preUpscaleProcessedUrl: preUpscaleProcessed ? preUpscaleProcessed.substring(0, 100) : null,
      newIntrinsic: { width: asset.intrinsicWidth, height: asset.intrinsicHeight },
      cacheKey: cacheKey,
      decision: data.decision || null,
      appliedScale: data.scale ? data.scale.applied : null
    });

    // Bicubic-fallback diagnostic. DCF marks this true when the AI
    // engine was unavailable / vetoed and the upscale fell back to
    // a deterministic bicubic resample. We don't break the flow —
    // the asset still gets a usable upscale — but we log loudly so
    // it's obvious in field captures when fallback is happening.
    if (asset.usedBicubicFallback) {
      console.warn("[ZUG][DTF][SRC_TRACE][BICUBIC_FALLBACK] DCF auto-enhance fell back to bicubic for", asset.filename, {
        assetId: asset.id,
        engineUsed: asset.engineUsed,
        usedAi: asset.usedAi,
        decision: data.decision || null,
        reason: data.decision && data.decision.reason
      });
    }

    var dpiX = asset.intrinsicWidth / Math.max(asset.placedWidthInches, 0.01);
    var dpiY = asset.intrinsicHeight / Math.max(asset.placedHeightInches, 0.01);
    asset.estimatedDpi = round(Math.min(dpiX, dpiY), 0);

    asset.upscaleEngine = finalBlock.engine || null;
    asset.enhancementStatus = "done";
    asset.enhancementError = null;

    // Pre-fetch the upscaled bytes into a dedicated slot
    // (`asset.upscaledBlob`) so the next pipeline step (mask) can
    // submit them directly. This is kept SEPARATE from
    // `asset.processedBlob`, which is reused by the mask step's own
    // post-probe and would otherwise have ambiguous provenance on
    // reruns. Best-effort: if this fails, the mask step falls back
    // to image_url passthrough.
    asset.upscaledBlob = null;
    if (asset.removeBackground) {
      try {
        var upProbe = await fetch(outputUrl, { method: "GET", mode: "cors", cache: "no-store" });
        if (upProbe.ok) {
          asset.upscaledBlob = await upProbe.blob();
        } else {
          console.warn("[ZUG][DTF][ENHANCE][DEBUG] auto-enhance output probe non-OK:", upProbe.status, "(mask will fall back to image_url)");
        }
      } catch (upProbeErr) {
        console.warn("[ZUG][DTF][ENHANCE][DEBUG] auto-enhance output probe failed:", upProbeErr.message, "(mask will fall back to image_url)");
      }
    }

    console.log("[ZUG][DTF][ENHANCE][DEBUG] auto-enhance COMPLETE", asset.filename, newWidth + "x" + newHeight, "engine=" + finalBlock.engine, "upscaledBlobCached=" + !!asset.upscaledBlob);

    // Re-tint any already-placed copies of this asset on the canvas.
    // Their outline / DPI badge was based on the pre-upscale dims;
    // now that intrinsicWidth/Height reflect the upscaled image,
    // the outline can drop from red → amber/purple as appropriate.
    // No-op if nothing's placed yet (initial setup flow).
    var refreshed = this.refreshPlacedObjectQualityForAsset(asset);
    if (refreshed) {
      console.log("[ZUG][DTF][DPI_SYNC] post-upscale refresh:", refreshed, "placed object(s) for", asset.filename);
    }
  };

  DtfGangsheetBuilder.prototype.handlePlacedSizeChange = function (axis) {
    var asset = this.getSelectedAsset();
    if (!asset) return;

    var newW = Number(this.refs.placedWidth.value) || 0.5;
    var newH = Number(this.refs.placedHeight.value) || 0.5;
    var ratio = asset.intrinsicWidth / Math.max(asset.intrinsicHeight, 1);

    if (this.state.aspectRatioLocked) {
      if (axis === "width") {
        newH = round(newW / ratio, 2);
        this.refs.placedHeight.value = newH;
      } else {
        newW = round(newH * ratio, 2);
        this.refs.placedWidth.value = newW;
      }
    }

    asset.placedWidthInches = Math.max(newW, 0.5);
    asset.placedHeightInches = Math.max(newH, 0.5);
    var dpiX = asset.intrinsicWidth / asset.placedWidthInches;
    var dpiY = asset.intrinsicHeight / asset.placedHeightInches;
    asset.estimatedDpi = round(Math.min(dpiX, dpiY), 0);
  };

  DtfGangsheetBuilder.prototype.syncPlacedSizeInputs = function () {
    var asset = this.getSelectedAsset();
    if (!asset || !this.refs.placedWidth || !this.refs.placedHeight) return;
    this.refs.placedWidth.value = round(asset.placedWidthInches, 2);
    this.refs.placedHeight.value = round(asset.placedHeightInches, 2);
  };

  DtfGangsheetBuilder.prototype.setMode = function (mode) {
    if (MODE_ORDER.indexOf(mode) === -1) {
      return;
    }

    this.state.currentMode = mode;

    if (mode === "review") {
      this.refreshReviewArtifacts(true);
    }

    this.render();
  };

  DtfGangsheetBuilder.prototype.goBack = function () {
    var currentIndex = MODE_ORDER.indexOf(this.state.currentMode);
    if (currentIndex <= 0) {
      return;
    }

    this.setMode(MODE_ORDER[currentIndex - 1]);
  };

  DtfGangsheetBuilder.prototype.handlePrimaryAction = function () {
    var idx = MODE_ORDER.indexOf(this.state.currentMode);
    if (this.state.currentMode === "review") {
      this.addToCart();
      return;
    }
    if (idx >= 0 && idx < MODE_ORDER.length - 1) {
      this.setMode(MODE_ORDER[idx + 1]);
    }
  };

  DtfGangsheetBuilder.prototype.applyObjectControlStyle = function (object) {
    object.set({
      cornerStyle: "circle",
      cornerColor: "#6d4cf5",
      borderColor: "#6d4cf5",
      cornerStrokeColor: "#ffffff",
      transparentCorners: false,
      cornerSize: 14,
      touchCornerSize: 24,
      borderScaleFactor: 1.6,
      borderDashArray: [6, 4],
      padding: 6,
      centeredRotation: true,
      lockRotation: true,
      hasRotatingPoint: false,
      hoverCursor: "move",
      moveCursor: "move",
      lockScalingFlip: true
    });
  };

  // Tint the object outline + corners according to the live quality
  // state at the object's current scaled size. Called on placement,
  // during scaling, and on scale-finish.
  DtfGangsheetBuilder.prototype.applyQualityOutline = function (object, qualityState) {
    if (!object) return;
    var key = (qualityState && qualityState.quality_state) || "good_quality";
    var tone = QUALITY_OUTLINE[key] || QUALITY_OUTLINE.good_quality;
    object.set({
      borderColor: tone.border,
      cornerColor: tone.corner,
      borderDashArray: tone.dash
    });
    if (object.zugMeta) object.zugMeta.qualityState = qualityState || null;
  };

  // Convert a fabric object's current scaled bounds back to inches
  // and compute its quality state. Mirrors getEffectiveDpi but
  // returns the full state object so callers can drive outline + chip.
  DtfGangsheetBuilder.prototype.computeObjectQualityState = function (object) {
    if (!object || !object.zugMeta) return null;
    var assetId = object.zugMeta.assetId;
    var asset = this.state.assets.find(function (a) { return a.id === assetId; });
    var widthInches = object.getScaledWidth() / UNITS_PER_INCH;
    var heightInches = object.getScaledHeight() / UNITS_PER_INCH;
    if (asset) {
      // Canvas outline must reflect the PROCESSED (post-upscale)
      // dimensions of the actual placed image — not the original
      // upload dims. Otherwise a successful AI enhance still reads
      // as red because the calculation kept staring at the raw
      // upload's pixel count.
      return getAssetQualityStateForPlacement(asset, widthInches, heightInches);
    }
    // Fallback when the asset has been removed from the panel:
    // zugMeta.sourceWidth/Height was captured at placement time, so
    // it already reflects whatever was the latest processed dims at
    // that point.
    var srcW = object.zugMeta.sourceWidth || 1;
    var srcH = object.zugMeta.sourceHeight || 1;
    var dpi = Math.min(srcW / Math.max(widthInches, 0.01), srcH / Math.max(heightInches, 0.01));
    return buildQualityState(classifyDpi(dpi), dpi, {});
  };

  // Re-tint every placed object for `asset` to reflect the asset's
  // current PROCESSED dimensions, and refresh the cached
  // sourceWidth/Height on zugMeta so the asset-removed fallback
  // also stays current. Called after AI enhance completes (which
  // updates asset.intrinsicWidth/Height) and after placements get
  // rebuilt with a swapped image source. Logs one
  // [ZUG][DTF][DPI_SYNC] entry per affected object.
  DtfGangsheetBuilder.prototype.refreshPlacedObjectQualityForAsset = function (asset) {
    if (!asset || !this.canvas) return 0;
    var matches = this.getPlacedObjects().filter(function (o) {
      return o.zugMeta && o.zugMeta.assetId === asset.id;
    });
    if (!matches.length) return 0;

    var origW = asset.originalIntrinsicWidth || asset.intrinsicWidth || 0;
    var origH = asset.originalIntrinsicHeight || asset.intrinsicHeight || 0;
    var procW = asset.intrinsicWidth || 0;
    var procH = asset.intrinsicHeight || 0;
    var self = this;

    matches.forEach(function (object) {
      // Compute placed-size in inches from the live fabric scale.
      var widthInches = object.getScaledWidth() / UNITS_PER_INCH;
      var heightInches = object.getScaledHeight() / UNITS_PER_INCH;

      var newState = getAssetQualityStateForPlacement(asset, widthInches, heightInches);
      var prevStateKey = (object.zugMeta && object.zugMeta.qualityState && object.zugMeta.qualityState.quality_state) || null;

      // Source-of-truth refresh: keep zugMeta.sourceWidth/Height in
      // sync with the asset's processed dims so the asset-removed
      // fallback path uses the right basis too.
      if (object.zugMeta) {
        object.zugMeta.sourceWidth = procW || object.zugMeta.sourceWidth;
        object.zugMeta.sourceHeight = procH || object.zugMeta.sourceHeight;
        object.zugMeta.sourceDpi = newState.effective_dpi;
      }

      self.applyQualityOutline(object, newState);

      // Per-object DPI reconciliation log. One line per placement
      // makes it easy to see exactly which objects changed state.
      var origDpi = (origW && widthInches && origH && heightInches)
        ? Math.round(Math.min(origW / Math.max(widthInches, 0.01), origH / Math.max(heightInches, 0.01)))
        : null;
      console.log("[ZUG][DTF][DPI_SYNC]", {
        assetId: asset.id,
        objectId: object.zugMeta && object.zugMeta.id || null,
        filename: asset.filename,
        originalW: origW,
        originalH: origH,
        processedW: procW,
        processedH: procH,
        placedInches: { w: +widthInches.toFixed(2), h: +heightInches.toFixed(2) },
        originalDpi: origDpi,
        processedDpi: newState.effective_dpi,
        dpiUsedForOutline: newState.effective_dpi,
        prevQualityState: prevStateKey,
        newQualityState: newState.quality_state
      });
    });

    this.canvas.requestRenderAll();
    return matches.length;
  };

  DtfGangsheetBuilder.prototype.getSpacingUnits = function () {
    return this.state.spacingInches * UNITS_PER_INCH;
  };

  DtfGangsheetBuilder.prototype.getPlacedObjects = function () {
    return this.canvas.getObjects().filter(function (object) {
      return object.zugRole !== "guide" && object.zugMeta;
    });
  };

  DtfGangsheetBuilder.prototype.getSelectedAsset = function () {
    var assetId = this.state.selectedAssetId;
    return this.state.assets.find(function (asset) {
      return asset.id === assetId;
    }) || null;
  };

  DtfGangsheetBuilder.prototype.getActiveObject = function () {
    var object = this.canvas.getActiveObject();
    return object && object.zugMeta ? object : null;
  };

  DtfGangsheetBuilder.prototype.getOccupiedRects = function () {
    return this.getPlacedObjects().map(function (object) {
      var bounds = object.getBoundingRect(true, true);
      return {
        left: bounds.left,
        top: bounds.top,
        right: bounds.left + bounds.width,
        bottom: bounds.top + bounds.height
      };
    });
  };

  DtfGangsheetBuilder.prototype.findPlacementSlots = function (size, quantity) {
    var printable = this.getPrintableBounds();
    var occupied = this.getOccupiedRects();
    var slots = [];
    var spacing = this.getSpacingUnits();

    for (var index = 0; index < quantity; index += 1) {
      var slot = this.findNextShelfSlot(size, printable, occupied, spacing);
      if (!slot) {
        break;
      }

      slots.push(slot);
      occupied.push({
        left: slot.left,
        top: slot.top,
        right: slot.left + size.width,
        bottom: slot.top + size.height
      });
    }

    return {
      slots: slots,
      unplaced: Math.max(quantity - slots.length, 0)
    };
  };

  DtfGangsheetBuilder.prototype.findNextShelfSlot = function (size, printable, occupied, spacing) {
    var maxLeft = printable.right - size.width;
    var maxTop = printable.bottom - size.height;
    var top = printable.top;

    while (top <= maxTop + 0.5) {
      var left = printable.left;
      var nextRowTop = top + size.height + spacing;

      while (left <= maxLeft + 0.5) {
        var proposal = {
          left: left,
          top: top,
          right: left + size.width,
          bottom: top + size.height
        };

        var collision = null;

        for (var index = 0; index < occupied.length; index += 1) {
          var expanded = {
            left: occupied[index].left - spacing,
            top: occupied[index].top - spacing,
            right: occupied[index].right + spacing,
            bottom: occupied[index].bottom + spacing
          };

          if (rectsIntersect(proposal, expanded)) {
            collision = occupied[index];
            nextRowTop = Math.max(nextRowTop, collision.bottom + spacing);
            left = Math.max(left + spacing, collision.right + spacing);
            break;
          }
        }

        if (!collision) {
          return {
            left: left,
            top: top
          };
        }
      }

      top = nextRowTop;
    }

    return null;
  };

  DtfGangsheetBuilder.prototype.getPlacementUrl = function (asset) {
    var picked = asset.trimmedPreviewUrl || asset.previewUrl;
    var pickedFrom = asset.trimmedPreviewUrl ? "trimmedPreviewUrl" : "previewUrl";
    console.log("[ZUG][DTF][SRC_TRACE] getPlacementUrl (Fabric canvas)", {
      assetId: asset.id,
      filename: asset.filename,
      removeBackground: !!asset.removeBackground,
      upscale: !!asset.upscale,
      enhancementStatus: asset.enhancementStatus,
      dtfMaskOutputUrl: (asset.processed && asset.processed.dtfMask && asset.processed.dtfMask.output_url) ? asset.processed.dtfMask.output_url.substring(0, 100) : null,
      dtfMaskAfterUpscaleOutputUrl: (asset.processed && asset.processed.dtfMaskAfterUpscale && asset.processed.dtfMaskAfterUpscale.output_url) ? asset.processed.dtfMaskAfterUpscale.output_url.substring(0, 100) : null,
      upscaleOutputUrl: (asset.processed && asset.processed.upscale && asset.processed.upscale.output_url) ? asset.processed.upscale.output_url.substring(0, 100) : null,
      previewUrl: asset.previewUrl ? asset.previewUrl.substring(0, 100) : null,
      processedUrl: asset.processedUrl ? asset.processedUrl.substring(0, 100) : null,
      trimmedPreviewUrl: asset.trimmedPreviewUrl ? "(blob URL)" : null,
      originalPreviewUrl: asset.originalPreviewUrl ? "(blob URL)" : null,
      finalSrcUsed: picked ? picked.substring(0, 100) : null,
      finalSrcField: pickedFrom
    });
    return picked;
  };

  DtfGangsheetBuilder.prototype.getPreferredObjectSize = function (asset) {
    var printable = this.getPrintableBounds();
    var wIn = asset.placedWidthInches || 3;
    var hIn = asset.placedHeightInches || 3;

    // The user's print-size input represents the SIZE OF THE VISIBLE ARTWORK
    // they see in the preview. When background removal has trimmed the
    // transparent margins, the placement URL is already the trimmed image, so
    // we place it at the user-specified size directly (no trim-ratio shrink).
    // Previously we shrank the placed object by the trim ratio, which caused
    // the on-canvas artwork to be much smaller than the user requested.
    if (asset.trimBounds) {
      console.log("[ZUG][DTF][TRIM_BOUNDS] placement uses user-specified size (visible artwork = " + round(wIn, 2) + "x" + round(hIn, 2) + " in)");
    }

    var widthUnits = wIn * UNITS_PER_INCH;
    var heightUnits = hIn * UNITS_PER_INCH;
    var fitRatio = Math.min(
      1,
      printable.width / Math.max(widthUnits, 1),
      printable.height / Math.max(heightUnits, 1)
    );

    return {
      width: Math.max(widthUnits * fitRatio, UNITS_PER_INCH * 0.5),
      height: Math.max(heightUnits * fitRatio, UNITS_PER_INCH * 0.5)
    };
  };

  DtfGangsheetBuilder.prototype.buildPlacedObject = async function (asset, slot, copyGroupId, reason) {
    var size = this.getPreferredObjectSize(asset);
    var placementUrl = this.getPlacementUrl(asset);
    var image = await fabricImageFromURL(placementUrl);

    image.set({
      left: slot.left,
      top: slot.top,
      originX: "left",
      originY: "top"
    });
    this.applyObjectControlStyle(image);

    image.scaleToWidth(size.width);
    if (image.getScaledHeight() > size.height) {
      image.scaleToHeight(size.height);
    }

    image.zugMeta = {
      objectId: uid("object"),
      assetId: asset.id,
      filename: asset.filename,
      copyGroupId: copyGroupId,
      sourceWidth: asset.intrinsicWidth,
      sourceHeight: asset.intrinsicHeight,
      sourceDpi: asset.estimatedDpi,
      placedFrom: reason,
      usedTrimBounds: !!asset.trimBounds
    };

    // Initial outline tone reflects the placement's effective DPI
    // right away — no need to wait for the user to grab a handle.
    this.applyQualityOutline(image, this.computeObjectQualityState(image));

    this.fitObjectWithinPrintableBounds(image);
    return image;
  };

  // Swap the image source of every placed fabric object that
  // belongs to `asset` to the asset's current placement URL. Used
  // after Edit Artwork → Save changes when the underlying image
  // changes (most commonly: rmbg toggled off → revert to original
  // upload, or rmbg toggled on → load the dtf-mask result).
  // Position / scale / rotation are preserved.
  DtfGangsheetBuilder.prototype.refreshPlacementsForAsset = async function (asset) {
    if (!asset || !this.canvas) return;
    var newUrl = this.getPlacementUrl(asset);
    if (!newUrl) {
      console.warn("[ZUG][DTF][ASSET_EDIT] refreshPlacementsForAsset: no URL resolved for", asset.filename);
      return;
    }
    var matches = this.getPlacedObjects().filter(function (o) {
      return o.zugMeta && o.zugMeta.assetId === asset.id;
    });
    if (!matches.length) {
      console.log("[ZUG][DTF][ASSET_EDIT] refreshPlacementsForAsset: no canvas placements for", asset.filename, "(nothing to refresh)");
      return;
    }
    var isBlob = newUrl.indexOf("blob:") === 0;
    console.log("[ZUG][DTF][ASSET_EDIT] refreshing", matches.length, "placement(s) for", asset.filename, "→", newUrl.substring(0, 80), "isBlob=" + isBlob);
    var self = this;
    // Rebuild each placement: capture geometry, drop the old
    // fabric.Image, load a fresh one from the new URL, restore
    // geometry + zugMeta, re-add. setSrc() has flaky behaviour
    // with blob: URLs and `crossOrigin`, so a clean rebuild is
    // more reliable.
    var rebuildPromises = matches.map(function (obj, idx) {
      // Capture the DISPLAYED bounding-box size (in canvas
      // units), not the raw scale, because the new image's
      // natural dimensions usually differ from the old one
      // (e.g. trimmed bg-removed = 2660×3076 vs upscaled
      // original = 4348×4144). Keeping displayedW/H constant
      // is what preserves the visual placement.
      var displayedW = obj.width * obj.scaleX;
      var displayedH = obj.height * obj.scaleY;
      var props = {
        left: obj.left,
        top: obj.top,
        angle: obj.angle,
        flipX: obj.flipX,
        flipY: obj.flipY,
        originX: obj.originX,
        originY: obj.originY
      };
      var meta = obj.zugMeta;
      var wasActive = self.canvas.getActiveObject() === obj;
      self.canvas.remove(obj);
      return new Promise(function (resolve) {
        var loadOptions = isBlob ? {} : { crossOrigin: "anonymous" };
        window.fabric.Image.fromURL(newUrl, function (image) {
          if (!image) {
            console.warn("[ZUG][DTF][ASSET_EDIT] refresh[" + idx + "]: fabric.Image.fromURL returned null for", asset.filename);
            resolve();
            return;
          }
          var natW = image.width || image.getElement().naturalWidth || 1;
          var natH = image.height || image.getElement().naturalHeight || 1;
          var newScaleX = displayedW / natW;
          var newScaleY = displayedH / natH;
          image.set(props);
          image.set({ scaleX: newScaleX, scaleY: newScaleY });
          self.applyObjectControlStyle(image);
          image.zugMeta = Object.assign({}, meta, {
            usedTrimBounds: !!asset.trimBounds
          });
          image.setCoords();
          self.canvas.add(image);
          if (wasActive) self.canvas.setActiveObject(image);
          console.log("[ZUG][DTF][ASSET_EDIT] refresh[" + idx + "] OK:", {
            natural: natW + "x" + natH,
            displayed: Math.round(displayedW) + "x" + Math.round(displayedH),
            scale: newScaleX.toFixed(3) + "/" + newScaleY.toFixed(3),
            pos: Math.round(props.left) + "," + Math.round(props.top)
          });
          resolve();
        }, loadOptions);
      });
    });
    await Promise.all(rebuildPromises);
    // After swapping the image source on every placement, reconcile
    // each placement's quality outline against the asset's current
    // PROCESSED dims. Without this the rebuilt objects keep showing
    // whatever outline tone they had before the rebuild — typically
    // the stale red from the pre-upscale calculation.
    self.refreshPlacedObjectQualityForAsset(asset);
    self.canvas.requestRenderAll();
    self.positionCanvasActions();
  };

  DtfGangsheetBuilder.prototype.placeSelectedAssetCopies = async function (quantity) {
    var asset = this.getSelectedAsset();

    if (!asset) {
      this.pushNotice("warning", "Select an uploaded asset before adding copies to the sheet.");
      this.render();
      return;
    }

    var preferredSize = this.getPreferredObjectSize(asset);
    var placements = this.findPlacementSlots(preferredSize, quantity);
    var copyGroupId = uid("group");

    if (!placements.slots.length) {
      this.pushNotice("warning", "That artwork does not fit inside the printable area with the current spacing.");
      this.render();
      return;
    }

    var objects = await Promise.all(
      placements.slots.map(function (slot) {
        return this.buildPlacedObject(asset, slot, copyGroupId, "copy-placement");
      }, this)
    );

    if (this._initCancelled || !this.canvas) return;

    objects.forEach(function (object) {
      this.canvas.add(object);
    }, this);

    this.canvas.setActiveObject(objects[objects.length - 1]);
    if (this.refs.canvasShell) this.refs.canvasShell.focus();

    if (placements.unplaced > 0) {
      this.pushNotice(
        "warning",
        "Only " + placements.slots.length + " of " + quantity + " requested cop" + (quantity === 1 ? "y" : "ies") + " fit on this sheet. The remainder were not placed."
      );
    }

    this.setMode("arrange");
    this.render();
  };

  DtfGangsheetBuilder.prototype.autoFillSelectedAsset = async function () {
    var asset = this.getSelectedAsset();

    if (!asset) {
      this.pushNotice("warning", "Select an asset before using auto fill.");
      this.render();
      return;
    }

    var preferredSize = this.getPreferredObjectSize(asset);
    var placements = this.findPlacementSlots(preferredSize, MAX_AUTOFILL_COPIES);

    if (!placements.slots.length) {
      this.pushNotice("warning", "No free shelf space remains for the selected artwork.");
      this.render();
      return;
    }

    var copyGroupId = uid("group");
    var objects = await Promise.all(
      placements.slots.map(function (slot) {
        return this.buildPlacedObject(asset, slot, copyGroupId, "autofill");
      }, this)
    );

    if (this._initCancelled || !this.canvas) return;

    objects.forEach(function (object) {
      this.canvas.add(object);
    }, this);

    this.canvas.setActiveObject(objects[objects.length - 1]);
    this.showToast("Auto fill placed " + objects.length + " additional cop" + (objects.length === 1 ? "y" : "ies") + " of " + asset.filename + ".");
    this.render();
  };

  DtfGangsheetBuilder.prototype.handleSelectionChange = function () {
    var activeObject = this.getActiveObject();

    if (activeObject) {
      this.state.selectedAssetId = activeObject.zugMeta.assetId;
    }

    this.render();
    this.positionCanvasActions();
  };

  DtfGangsheetBuilder.prototype.handleSelectionClear = function () {
    this.render();
    this.positionCanvasActions();
  };

  DtfGangsheetBuilder.prototype.positionCanvasActions = function () {
    var el = this.refs.canvasActions;
    if (!el) return;
    if (!this.canvas) {
      el.classList.remove("is-visible", "is-above");
      el.setAttribute("aria-hidden", "true");
      return;
    }
    // Read the raw Fabric active object first so we can explicitly detect
    // multi-select (activeSelection) before applying our zugMeta filter.
    var rawActive = this.canvas.getActiveObject();
    var reasonHide = null;
    if (!rawActive) {
      reasonHide = "no selection";
    } else if (rawActive.type === "activeSelection") {
      reasonHide = "multi-select";
    } else if (!rawActive.zugMeta) {
      reasonHide = "non-asset object";
    }
    if (reasonHide) {
      if (el.classList.contains("is-visible")) {
        console.log("[ZUG][DTF][CANVAS_ACTIONS] hide", {
          reason: reasonHide,
          activeType: rawActive ? rawActive.type : null
        });
      }
      el.classList.remove("is-visible", "is-above");
      el.setAttribute("aria-hidden", "true");
      return;
    }
    var obj = rawActive;
    var canvasEl = this.canvas.lowerCanvasEl;
    var stageWrap = this.refs.stageWrap;
    if (!canvasEl || !stageWrap) return;

    var canvasRect = canvasEl.getBoundingClientRect();
    var stageRect = stageWrap.getBoundingClientRect();
    var scale = canvasEl.clientWidth / Math.max(this.canvas.getWidth(), 1);
    var bounds = obj.getBoundingRect(true, true);
    var centerCanvasX = bounds.left + bounds.width / 2;
    var topCanvasY = bounds.top;
    var bottomCanvasY = bounds.top + bounds.height;
    var stageOffsetX = (canvasRect.left - stageRect.left) + stageWrap.scrollLeft;
    var stageOffsetY = (canvasRect.top - stageRect.top) + stageWrap.scrollTop;

    var x = stageOffsetX + centerCanvasX * scale;
    var preferredYBelow = stageOffsetY + bottomCanvasY * scale + 10;
    var preferredYAbove = stageOffsetY + topCanvasY * scale - 10;

    // Measure the action bar (briefly make visible to read size).
    var wasHidden = !el.classList.contains("is-visible");
    if (wasHidden) {
      el.style.visibility = "hidden";
      el.classList.add("is-visible");
    }
    var actionH = el.offsetHeight || 38;
    var actionW = el.offsetWidth || 160;
    if (wasHidden) {
      el.classList.remove("is-visible");
      el.style.visibility = "";
    }

    // Place above if there isn't enough room below within the visible stage.
    var visibleBottomInStage = stageWrap.scrollTop + stageWrap.clientHeight;
    var placeAbove = (preferredYBelow + actionH) > visibleBottomInStage
                  && (preferredYAbove - actionH) >= stageWrap.scrollTop;
    var y = placeAbove ? (preferredYAbove - actionH) : preferredYBelow;

    // Clamp x so the bar stays within the visible stage horizontally.
    var halfW = actionW / 2;
    var minX = stageWrap.scrollLeft + halfW + 4;
    var maxX = stageWrap.scrollLeft + stageWrap.clientWidth - halfW - 4;
    if (maxX > minX) x = Math.min(Math.max(x, minX), maxX);

    el.style.left = x + "px";
    el.style.top = y + "px";
    el.classList.toggle("is-above", placeAbove);

    if (wasHidden) {
      console.log("[ZUG][DTF][CANVAS_ACTIONS] show", {
        x: Math.round(x), y: Math.round(y), placeAbove: placeAbove,
        objectId: obj.zugMeta ? obj.zugMeta.objectId || null : null,
        assetId: obj.zugMeta ? obj.zugMeta.assetId || null : null
      });
    }
    el.classList.add("is-visible");
    el.setAttribute("aria-hidden", "false");
  };

  DtfGangsheetBuilder.prototype.handleObjectTransforming = function (event) {
    if (!event.target || !event.target.zugMeta) {
      return;
    }

    this.fitObjectWithinPrintableBounds(event.target);
    // Live outline tone update while the user is actively scaling.
    var liveState = this.computeObjectQualityState(event.target);
    this.applyQualityOutline(event.target, liveState);
    this.canvas.requestRenderAll();
  };

  DtfGangsheetBuilder.prototype.handleObjectModified = function (event) {
    if (!event.target || !event.target.zugMeta) {
      return;
    }

    this.fitObjectWithinPrintableBounds(event.target);
    // Final outline tone after the scale gesture settles. Side-panel
    // chip refresh happens through render() → renderSelectedAsset.
    var finalState = this.computeObjectQualityState(event.target);
    this.applyQualityOutline(event.target, finalState);
    this.render();
  };

  DtfGangsheetBuilder.prototype.fitObjectWithinPrintableBounds = function (object) {
    var printable = this.getPrintableBounds();
    var bounds = object.getBoundingRect(true, true);

    if (bounds.width > printable.width || bounds.height > printable.height) {
      var fitRatio = Math.min(
        printable.width / Math.max(bounds.width, 1),
        printable.height / Math.max(bounds.height, 1),
        1
      ) * 0.99;

      object.scaleX *= fitRatio;
      object.scaleY *= fitRatio;
      object.setCoords();
      bounds = object.getBoundingRect(true, true);
    }

    var deltaX = 0;
    var deltaY = 0;

    if (bounds.left < printable.left) {
      deltaX = printable.left - bounds.left;
    } else if (bounds.left + bounds.width > printable.right) {
      deltaX = printable.right - (bounds.left + bounds.width);
    }

    if (bounds.top < printable.top) {
      deltaY = printable.top - bounds.top;
    } else if (bounds.top + bounds.height > printable.bottom) {
      deltaY = printable.bottom - (bounds.top + bounds.height);
    }

    if (deltaX || deltaY) {
      object.left += deltaX;
      object.top += deltaY;
      object.setCoords();
    }
  };

  DtfGangsheetBuilder.prototype.constrainAllObjects = function () {
    this.getPlacedObjects().forEach(function (object) {
      this.fitObjectWithinPrintableBounds(object);
    }, this);
    this.canvas.requestRenderAll();
  };

  DtfGangsheetBuilder.prototype.deleteSelectedObject = function () {
    var activeObject = this.getActiveObject();
    if (!activeObject) {
      this.pushNotice("warning", "Select a placed artwork before deleting.");
      this.render();
      return;
    }

    this.canvas.remove(activeObject);
    this.canvas.discardActiveObject();
    this.render();
  };

  DtfGangsheetBuilder.prototype.clearAllPlacements = function () {
    this.getPlacedObjects().forEach(function (object) {
      this.canvas.remove(object);
    }, this);
    this.canvas.discardActiveObject();
    this.pushNotice("good", "All placed artwork has been cleared from the sheet.");
    this.render();
  };

  DtfGangsheetBuilder.prototype.rotateSelected90 = function () {
    var object = this.getActiveObject();
    if (!object) {
      this.pushNotice("warning", "Select a placed item before rotating.");
      this.render();
      return;
    }

    var currentAngle = Math.round(object.angle || 0) % 360;
    var newAngle = currentAngle === 0 ? 90 : 0;
    object.set({ angle: newAngle });
    object.setCoords();
    this.fitObjectWithinPrintableBounds(object);
    this.canvas.requestRenderAll();
    this.render();
  };

  DtfGangsheetBuilder.prototype.autoArrangeAll = function (allowRotate) {
    var placed = this.getPlacedObjects();
    if (!placed.length) {
      this.pushNotice("warning", "No placed items to arrange. Add copies first.");
      this.render();
      return;
    }

    var printable = this.getPrintableBounds();
    var spacing = this.getSpacingUnits();

    var items = placed.map(function (obj) {
      var savedAngle = obj.angle || 0;
      obj.set({ angle: 0 });
      obj.setCoords();
      var bounds0 = obj.getBoundingRect(true, true);
      var uprightW = bounds0.width;
      var uprightH = bounds0.height;

      obj.set({ angle: savedAngle });
      obj.setCoords();

      return {
        object: obj,
        uprightW: uprightW,
        uprightH: uprightH,
        area: uprightW * uprightH
      };
    });

    items.sort(function (a, b) {
      return b.uprightH - a.uprightH || b.area - a.area;
    });

    var cursorLeft = printable.left;
    var cursorTop = printable.top;
    var rowMaxHeight = 0;
    var arranged = 0;
    var unplaced = 0;

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var uw = item.uprightW;
      var uh = item.uprightH;
      var rw = item.uprightH;
      var rh = item.uprightW;
      var useRotated = false;
      var w, h;

      if (!allowRotate) {
        w = uw;
        h = uh;
      } else {
        var uprightFitsHere = cursorLeft + uw <= printable.right + 0.5;
        var rotatedFitsHere = cursorLeft + rw <= printable.right + 0.5;

        if (uprightFitsHere) {
          w = uw; h = uh;
        } else if (rotatedFitsHere) {
          w = rw; h = rh; useRotated = true;
        } else {
          cursorLeft = printable.left;
          cursorTop += rowMaxHeight + spacing;
          rowMaxHeight = 0;

          uprightFitsHere = cursorLeft + uw <= printable.right + 0.5;
          rotatedFitsHere = cursorLeft + rw <= printable.right + 0.5;

          if (uprightFitsHere && cursorTop + uh <= printable.bottom + 0.5) {
            w = uw; h = uh;
          } else if (rotatedFitsHere && cursorTop + rh <= printable.bottom + 0.5) {
            w = rw; h = rh; useRotated = true;
          } else if (uprightFitsHere) {
            w = uw; h = uh;
          } else if (rotatedFitsHere) {
            w = rw; h = rh; useRotated = true;
          } else {
            unplaced++;
            continue;
          }
        }
      }

      if (cursorLeft + w > printable.right + 0.5) {
        cursorLeft = printable.left;
        cursorTop += rowMaxHeight + spacing;
        rowMaxHeight = 0;
      }

      if (cursorTop + h > printable.bottom + 0.5) {
        unplaced++;
        continue;
      }

      var obj = item.object;
      obj.set({ angle: useRotated ? 90 : 0 });
      obj.set({
        left: cursorLeft,
        top: cursorTop,
        originX: "left",
        originY: "top"
      });
      obj.setCoords();

      var br = obj.getBoundingRect(true, true);
      var dxCorrect = cursorLeft - br.left;
      var dyCorrect = cursorTop - br.top;
      if (Math.abs(dxCorrect) > 0.5 || Math.abs(dyCorrect) > 0.5) {
        obj.set({
          left: obj.left + dxCorrect,
          top: obj.top + dyCorrect
        });
        obj.setCoords();
      }

      cursorLeft += w + spacing;
      rowMaxHeight = Math.max(rowMaxHeight, h);
      arranged++;
    }

    this.canvas.discardActiveObject();
    this.canvas.requestRenderAll();

    var msg = "Auto arrange placed " + arranged + " item" + (arranged !== 1 ? "s" : "") + ".";
    if (unplaced > 0) {
      msg += " " + unplaced + " item" + (unplaced !== 1 ? "s" : "") + " did not fit.";
      this.pushNotice("warning", msg);
    } else {
      this.pushNotice("good", msg);
    }
    this.render();
  };

  DtfGangsheetBuilder.prototype.duplicateSelectedObject = async function () {
    var activeObject = this.getActiveObject();

    if (!activeObject) {
      this.pushNotice("warning", "Select a placed artwork before duplicating.");
      this.render();
      return;
    }

    var asset = this.state.assets.find(function (entry) {
      return entry.id === activeObject.zugMeta.assetId;
    });

    if (!asset) {
      this.pushNotice("warning", "The source asset for this object is no longer available.");
      this.render();
      return;
    }

    var placementUrl = this.getPlacementUrl(asset);
    var duplicate = await fabricImageFromURL(placementUrl);
    duplicate.set({
      left: activeObject.left + this.getSpacingUnits(),
      top: activeObject.top + this.getSpacingUnits(),
      originX: "left",
      originY: "top",
      angle: activeObject.angle,
      scaleX: activeObject.scaleX,
      scaleY: activeObject.scaleY
    });
    this.applyObjectControlStyle(duplicate);

    duplicate.zugMeta = {
      objectId: uid("object"),
      assetId: asset.id,
      filename: asset.filename,
      copyGroupId: uid("group"),
      sourceWidth: asset.intrinsicWidth,
      sourceHeight: asset.intrinsicHeight,
      sourceDpi: asset.estimatedDpi,
      placedFrom: "manual-duplicate",
      usedTrimBounds: !!asset.trimBounds
    };

    this.fitObjectWithinPrintableBounds(duplicate);
    this.canvas.add(duplicate);
    this.canvas.setActiveObject(duplicate);
    this.render();
  };

  DtfGangsheetBuilder.prototype.getAssetDpiInfo = function (asset) {
    if (!asset) return null;
    var widthIn = Math.max(Number(asset.placedWidthInches) || 0, 0.01);
    var heightIn = Math.max(Number(asset.placedHeightInches) || 0, 0.01);

    var origW = Number(asset.originalIntrinsicWidth) || Number(asset.intrinsicWidth) || 0;
    var origH = Number(asset.originalIntrinsicHeight) || Number(asset.intrinsicHeight) || 0;
    var procW = Number(asset.intrinsicWidth) || origW;
    var procH = Number(asset.intrinsicHeight) || origH;

    var originalDpi = origW && origH
      ? Math.round(Math.min(origW / widthIn, origH / heightIn))
      : 0;
    var processedDpi = procW && procH
      ? Math.round(Math.min(procW / widthIn, procH / heightIn))
      : 0;
    var isUpscaled = !!asset.upscale && (procW > origW || procH > origH);

    // Persist both DPI values on the asset model so any consumer (UI, export
    // payload, reporting) can read them without recomputing — and so we never
    // again rely solely on inflated processed DPI as if it were "the" DPI.
    asset.dpiOriginal = originalDpi;
    asset.dpiProcessed = processedDpi;

    return {
      originalDpi: originalDpi,
      processedDpi: processedDpi,
      isUpscaled: isUpscaled,
      originalWidth: origW,
      originalHeight: origH,
      processedWidth: procW,
      processedHeight: procH,
      placedWidthInches: widthIn,
      placedHeightInches: heightIn
    };
  };

  DtfGangsheetBuilder.prototype.getMinimumDpi = function () {
    var configured = this.config && this.config.defaults && this.config.defaults.minimumDpi;
    var n = Number(configured);
    return (isFinite(n) && n > 0) ? n : 150;
  };

  DtfGangsheetBuilder.prototype.buildLowDpiWarning = function (asset) {
    var info = this.getAssetDpiInfo(asset);
    if (!info || !info.originalDpi) return null;
    if (info.originalDpi >= this.getMinimumDpi()) return null;
    return {
      code: "low_dpi",
      severity: "warning",
      _injected: true,
      detail: info.originalDpi + " DPI"
    };
  };

  DtfGangsheetBuilder.prototype.mergeLowDpiWarning = function (warnings, asset) {
    var list = Array.isArray(warnings) ? warnings.slice() : [];
    var lowDpi = this.buildLowDpiWarning(asset);
    if (!lowDpi) return list;
    var hasLowDpi = list.some(function (w) { return w && w.code === "low_dpi"; });
    if (hasLowDpi) return list;
    list.unshift(lowDpi);
    return list;
  };

  DtfGangsheetBuilder.prototype.logDpiInfo = function (asset, context) {
    var info = this.getAssetDpiInfo(asset);
    if (!info) return;
    console.log("[ZUG][DTF][DPI]", {
      context: context || "",
      assetId: asset && asset.id,
      filename: asset && asset.filename,
      original_pixels: { width: info.originalWidth, height: info.originalHeight },
      processed_pixels: { width: info.processedWidth, height: info.processedHeight },
      print_size: { width_in: info.placedWidthInches, height_in: info.placedHeightInches },
      original_dpi: info.originalDpi,
      processed_dpi: info.processedDpi,
      is_upscaled: info.isUpscaled,
      minimum_dpi: this.getMinimumDpi()
    });
  };

  DtfGangsheetBuilder.prototype.getEffectiveDpi = function (object) {
    if (!object || !object.zugMeta) {
      return null;
    }

    var widthInches = object.getScaledWidth() / UNITS_PER_INCH;
    var heightInches = object.getScaledHeight() / UNITS_PER_INCH;
    var dpiX = object.zugMeta.sourceWidth / Math.max(widthInches, 0.01);
    var dpiY = object.zugMeta.sourceHeight / Math.max(heightInches, 0.01);

    return Math.min(dpiX, dpiY);
  };

  DtfGangsheetBuilder.prototype.isObjectInsidePrintableBounds = function (object) {
    var printable = this.getPrintableBounds();
    var bounds = object.getBoundingRect(true, true);

    return bounds.left >= printable.left &&
      bounds.top >= printable.top &&
      bounds.left + bounds.width <= printable.right &&
      bounds.top + bounds.height <= printable.bottom;
  };

  DtfGangsheetBuilder.prototype.resolvePreflightSource = function (asset) {
    if (!asset) return { url: null, kind: "none", file: null };
    // Chain order (April 2026): original → upscale → mask. The
    // strongest available output to preflight against is the mask
    // run that was chained off the upscale (dtfMaskAfterUpscale);
    // next best is mask-only or upscale-only; finally the original.
    var maskAfterUp = asset.processed && asset.processed.dtfMaskAfterUpscale && asset.processed.dtfMaskAfterUpscale.output_url;
    var maskPlain = asset.processed && asset.processed.dtfMask && asset.processed.dtfMask.output_url;
    var upPlain = asset.processed && asset.processed.upscale && asset.processed.upscale.output_url;
    var bgOk = !!(asset.removeBackground && (maskAfterUp || maskPlain));
    var upOk = !!(asset.upscale && upPlain);

    if (bgOk && upOk && maskAfterUp) {
      return { url: maskAfterUp, kind: "upscaled+rmbg", file: null };
    }
    if (bgOk) {
      return { url: maskAfterUp || maskPlain, kind: "rmbg", file: null };
    }
    if (upOk && upPlain) {
      return { url: upPlain, kind: "upscaled", file: null };
    }
    // Original: prefer in-memory File/Blob; fall back to original URL if file missing.
    if (asset.file) {
      return { url: null, kind: "original-file", file: asset.file };
    }
    return { url: asset.originalPreviewUrl || asset.previewUrl || null, kind: "original-url", file: null };
  };

  DtfGangsheetBuilder.prototype.computePreflightKey = function (asset, source, mode) {
    return [
      asset.id,
      source.url || source.kind,
      round(asset.placedWidthInches, 3),
      round(asset.placedHeightInches, 3),
      !!asset.removeBackground,
      !!asset.upscale,
      mode,
      "standard"
    ].join("|");
  };

  DtfGangsheetBuilder.prototype.resolvePreflightMode = function (asset) {
    // DTF Builder Fast flow: forward the user's actual chosen
    // bg-removal style verbatim — the new contract uses three
    // first-class styles end-to-end:
    //   "balanced" | "clean_cut" | "sticker_border".
    // No mapping to legacy backend mode names ("clean_logo",
    // "preserve_detail") — those were the dtf-mask endpoint's
    // vocabulary and silently collapsed every rmbg style into a
    // single preflight slot, hiding per-style differences and
    // wedging the post-upscale preflight cache.
    //
    // When background removal is OFF we send the explicit
    // "none" sentinel so the cache key (computePreflightKey)
    // never accidentally inherits a logo-style label from the
    // old dtfSafe/hard-alpha/filename heuristics. Those
    // heuristics were removed because they returned the legacy
    // "clean_logo" mode for assets that hadn't asked for any
    // bg removal at all — exactly the upscale-only scenario the
    // user reported.
    var BG_STYLE_PREFLIGHT_ALLOWED = {
      balanced: 1,
      clean_cut: 1,
      sticker_border: 1
    };
    if (asset.removeBackground) {
      var s = asset.bgRemovalStyle;
      return BG_STYLE_PREFLIGHT_ALLOWED[s] ? s : "balanced";
    }
    return "none";
  };

  DtfGangsheetBuilder.prototype.canRunPreflight = function (asset) {
    if (!asset) return false;
    var w = asset.placedWidthInches;
    var h = asset.placedHeightInches;
    if (typeof w !== "number" || typeof h !== "number") return false;
    if (!isFinite(w) || !isFinite(h)) return false;
    if (w <= 0 || h <= 0) return false;
    var src = this.resolvePreflightSource(asset);
    if (!src.url && !src.file) return false;
    return true;
  };

  DtfGangsheetBuilder.prototype.runPreflight = function (asset) {
    var self = this;
    if (!asset) return Promise.resolve(null);

    if (!this.canRunPreflight(asset)) {
      console.log("[ZUG][DTF][PREFLIGHT] skipped (invalid state)", {
        assetId: asset && asset.id,
        filename: asset && asset.filename,
        placed_width_in: asset && asset.placedWidthInches,
        placed_height_in: asset && asset.placedHeightInches,
        hasSource: !!(asset && (this.resolvePreflightSource(asset).url || this.resolvePreflightSource(asset).file))
      });
      return Promise.resolve(asset.preflight || null);
    }

    var source = this.resolvePreflightSource(asset);
    var mode = this.resolvePreflightMode(asset);
    var key = this.computePreflightKey(asset, source, mode);
    var widthIn = asset.placedWidthInches;
    var heightIn = asset.placedHeightInches;
    var flags = { rmbg: !!asset.removeBackground, upscale: !!asset.upscale };

    if (asset.preflight && asset.preflight.status === "ok" && asset.preflight.lastCheckedKey === key) {
      console.log("[ZUG][DTF][PREFLIGHT] cache hit", {
        assetId: asset.id,
        filename: asset.filename,
        preflightKey: key,
        sourceKind: source.kind,
        resolvedSource: source.url || "(file)",
        placed_width_in: widthIn,
        placed_height_in: heightIn,
        mode: mode,
        flags: flags,
        warningCount: (asset.preflight.warnings || []).length
      });
      return Promise.resolve(asset.preflight);
    }

    this._preflightInflight = this._preflightInflight || {};
    if (this._preflightInflight[key]) {
      return this._preflightInflight[key];
    }

    console.log("[ZUG][DTF][PREFLIGHT] request start", {
      assetId: asset.id,
      filename: asset.filename,
      preflightKey: key,
      sourceKind: source.kind,
      resolvedSource: source.url || "(file " + ((source.file && source.file.name) || asset.filename || "blob") + ")",
      placed_width_in: widthIn,
      placed_height_in: heightIn,
      mode: mode,
      detail_threshold_profile: "standard",
      flags: flags
    });

    var prevPf = asset.preflight || null;
    var prevWarnings = (prevPf && prevPf.status === "ok") ? (prevPf.warnings || []) : (prevPf ? (prevPf.warnings || []) : []);
    var prevDiagnostics = prevPf ? (prevPf.diagnostics || null) : null;
    var prevKey = prevPf ? (prevPf.lastCheckedKey || null) : null;
    asset.preflight = {
      status: "loading",
      warnings: prevWarnings,
      diagnostics: prevDiagnostics,
      lastCheckedKey: prevKey,
      lastSource: source.kind,
      lastWidthIn: widthIn,
      lastHeightIn: heightIn,
      error: null
    };
    this.render();

    var ctrl = (typeof AbortController !== "undefined") ? new AbortController() : null;
    var timeoutId = ctrl ? setTimeout(function () { ctrl.abort(); }, 30000) : null;

    var fd = new FormData();
    var name = asset.filename || "artwork.png";
    if (source.file) {
      fd.append("file", source.file, name);
    } else if (source.url) {
      fd.append("url", source.url);
    }
    fd.append("placed_width_in", String(widthIn));
    fd.append("placed_height_in", String(heightIn));
    fd.append("mode", mode);
    fd.append("detail_threshold_profile", "standard");

    var fetchPromise = fetch(DCF_BASE + "/api/enhance/dtf-preflight", {
      method: "POST",
      body: fd,
      signal: ctrl ? ctrl.signal : undefined
    });

    var promise = fetchPromise.then(function (response) {
      if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
      return response.text().then(function (text) {
        var payload = null;
        try { payload = text ? JSON.parse(text) : null; } catch (e) {}

        if (!response.ok) {
          console.error("[ZUG][DTF][PREFLIGHT] HTTP error", {
            assetId: asset.id,
            filename: asset.filename,
            preflightKey: key,
            status: response.status,
            body: text ? text.substring(0, 400) : null,
            sourceKind: source.kind,
            resolvedSource: source.url || "(file)"
          });
          asset.preflight = {
            status: "error",
            warnings: prevWarnings,
            diagnostics: prevDiagnostics,
            lastCheckedKey: prevKey,
            lastSource: source.kind,
            lastWidthIn: widthIn,
            lastHeightIn: heightIn,
            error: "HTTP " + response.status
          };
        } else {
          var warnings = preflightWarningsFromPayload(payload);
          var diagnostics = (payload && (payload.diagnostics || payload.metrics)) || null;
          console.log("[ZUG][DTF][PREFLIGHT] response", {
            assetId: asset.id,
            filename: asset.filename,
            preflightKey: key,
            warningCount: warnings.length,
            codes: warnings.map(function (w) { return w.code; }),
            payload: payload
          });
          asset.preflight = {
            status: "ok",
            warnings: warnings,
            diagnostics: diagnostics,
            lastCheckedKey: key,
            lastSource: source.kind,
            lastWidthIn: widthIn,
            lastHeightIn: heightIn,
            error: null
          };
        }
        return asset.preflight;
      });
    }).catch(function (err) {
      if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
      console.error("[ZUG][DTF][PREFLIGHT] failure", {
        assetId: asset.id,
        filename: asset.filename,
        preflightKey: key,
        error: err && err.message,
        sourceKind: source.kind,
        resolvedSource: source.url || "(file)"
      });
      asset.preflight = {
        status: "error",
        warnings: prevWarnings,
        diagnostics: prevDiagnostics,
        lastCheckedKey: prevKey,
        lastSource: source.kind,
        lastWidthIn: widthIn,
        lastHeightIn: heightIn,
        error: (err && err.message) || "Preflight failed"
      };
      return asset.preflight;
    }).then(function (result) {
      delete self._preflightInflight[key];
      self.render();
      return result;
    });

    this._preflightInflight[key] = promise;
    return promise;
  };

  DtfGangsheetBuilder.prototype.schedulePreflight = function (asset, delayMs) {
    var self = this;
    if (!asset) return;
    this._preflightTimers = this._preflightTimers || {};
    if (this._preflightTimers[asset.id]) {
      clearTimeout(this._preflightTimers[asset.id]);
    }
    this._preflightTimers[asset.id] = setTimeout(function () {
      delete self._preflightTimers[asset.id];
      if (self._initCancelled) return;
      var stillExists = self.state.assets.some(function (a) { return a.id === asset.id; });
      if (!stillExists) return;
      self.runPreflight(asset);
    }, typeof delayMs === "number" ? delayMs : 350);
  };

  DtfGangsheetBuilder.prototype.composeWarnings = function () {
    var minimumDpi = Number(this.config.defaults.minimumDpi) || 150;
    var warnings = [];

    this.getPlacedObjects().forEach(function (object) {
      var dpi = this.getEffectiveDpi(object);

      if (!this.isObjectInsidePrintableBounds(object)) {
        warnings.push(createNotice("warning", object.zugMeta.filename + " extends outside the printable area."));
      }

      if (dpi && dpi < minimumDpi) {
        // Calm recommendation phrasing instead of a raw warning. The
        // numeric DPI is dropped because customers don't act on it,
        // and the new contextual AI Upscale chip on each card already
        // surfaces the per-asset detail.
        warnings.push(
          createNotice(
            "warning",
            object.zugMeta.filename + " is larger than recommended for best print quality \u2014 use AI enhancement for cleaner results."
          )
        );
      }
    }, this);

    if (!warnings.length && this.getPlacedObjects().length) {
      warnings.push(createNotice("good", "Sheet is inside printable bounds and print quality looks good."));
    }

    if (!warnings.length) {
      warnings.push(createNotice("good", "No warnings yet. Upload artwork and build your sheet to start the review."));
    }

    this.state.warnings = warnings;
    return warnings;
  };

  DtfGangsheetBuilder.prototype.getSummary = function () {
    var objects = this.getPlacedObjects();
    var printable = this.getPrintableBounds();
    var uniqueAssets = new Set(objects.map(function (object) {
      return object.zugMeta.assetId;
    }));

    var usedArea = objects.reduce(function (total, object) {
      return total + (object.getScaledWidth() * object.getScaledHeight());
    }, 0);

    var printableArea = printable.width * printable.height;
    var usedAreaPercent = printableArea ? (usedArea / printableArea) * 100 : 0;

    return {
      placedAssets: uniqueAssets.size,
      totalCopies: objects.length,
      usedAreaPercent: clamp(usedAreaPercent, 0, 100),
      freeAreaPercent: clamp(100 - usedAreaPercent, 0, 100)
    };
  };

  DtfGangsheetBuilder.prototype.serializeState = function () {
    var summary = this.getSummary();
    var warnings = this.composeWarnings().map(function (warning) {
      return {
        level: warning.level,
        message: warning.message
      };
    });

    return {
      timestamp: new Date().toISOString(),
      productId: this.config.product && this.config.product.id,
      variantId: this.getSelectedVariantId(),
      sheet: {
        size: this.state.sheet.label,
        widthInches: round(this.state.sheet.widthInches, 2),
        heightInches: round(this.state.sheet.heightInches, 2),
        printableWidthInches: round(this.state.sheet.printableWidthInches, 2),
        printableHeightInches: round(this.state.sheet.printableHeightInches, 2)
      },
      spacingInches: round(this.state.spacingInches, 2),
      assetManifest: this.state.assets.map(function (asset) {
        return {
          id: asset.id,
          filename: asset.filename,
          intrinsicWidth: asset.intrinsicWidth,
          intrinsicHeight: asset.intrinsicHeight,
          placedWidthInches: round(asset.placedWidthInches, 2),
          placedHeightInches: round(asset.placedHeightInches, 2),
          estimatedDpi: asset.estimatedDpi,
          removeBackground: !!asset.removeBackground,
          // Derive from actual cache-slot presence, not the global
          // enhancementStatus, so that bg-failed-after-upscale
          // recovery (status="error") still reports the upscale
          // result that's actually placed.
          bgRemoved: !!(asset.removeBackground && asset.processed && (asset.processed.dtfMask || asset.processed.dtfMaskAfterUpscale)),
          upscaled: !!(asset.upscale && asset.processed && asset.processed.upscale),
          dtfSafe: asset.dtfSafe,
          originalAlphaMode: asset.originalAlphaMode || null,
          processedUrl: asset.processedUrl || null,
          processedMaskUrl: asset.processedMaskUrl || null,
          processedPreviewUrl: asset.processedPreviewUrl || null,
          dtfMaskSettings: asset.dtfMaskSettings || null,
          upscaleEngine: asset.upscaleEngine || null,
          enhancementStatus: asset.enhancementStatus || "idle",
          enhancementError: asset.enhancementError || null,
          upscale: !!asset.upscale,
          trimBounds: asset.trimBounds || null,
          uploadedAt: asset.uploadedAt
        };
      }),
      objects: this.getPlacedObjects().map(function (object) {
        return {
          assetId: object.zugMeta.assetId,
          filename: object.zugMeta.filename,
          copyGroupId: object.zugMeta.copyGroupId,
          sourceDimensions: {
            width: object.zugMeta.sourceWidth,
            height: object.zugMeta.sourceHeight
          },
          sourceDpi: object.zugMeta.sourceDpi,
          position: {
            xInches: round(object.left / UNITS_PER_INCH, 3),
            yInches: round(object.top / UNITS_PER_INCH, 3)
          },
          size: {
            widthInches: round(object.getScaledWidth() / UNITS_PER_INCH, 3),
            heightInches: round(object.getScaledHeight() / UNITS_PER_INCH, 3)
          },
          rotation: round(object.angle || 0, 2),
          scale: {
            x: round(object.scaleX || 1, 4),
            y: round(object.scaleY || 1, 4)
          },
          effectiveDpi: round(this.getEffectiveDpi(object) || 0, 0)
        };
      }, this),
      summary: summary,
      warnings: warnings
    };
  };

  DtfGangsheetBuilder.prototype.generatePreviewData = function () {
    if (!this.getPlacedObjects().length) {
      return "";
    }

    return this.canvas.toDataURL({
      format: "png",
      multiplier: 0.55
    });
  };

  DtfGangsheetBuilder.prototype.refreshReviewArtifacts = function (includePreview) {
    if (includePreview) {
      this.state.previewDataUrl = this.generatePreviewData();
    }

    this.state.exportJson = JSON.stringify(this.serializeState(), null, 2);
    this.renderReviewArtifacts();
  };

  DtfGangsheetBuilder.prototype.renderReviewArtifacts = function () {
    if (!this.getPlacedObjects().length) {
      this.state.previewDataUrl = "";
    }

    this.state.exportJson = JSON.stringify(this.serializeState(), null, 2);
    this.refs.exportOutput.value = this.state.exportJson || "";

    if (this.state.previewDataUrl) {
      this.refs.reviewPreview.src = this.state.previewDataUrl;
      this.refs.reviewPreview.classList.remove("is-hidden");
      this.refs.previewEmpty.classList.add("is-hidden");
    } else {
      this.refs.reviewPreview.removeAttribute("src");
      this.refs.reviewPreview.classList.add("is-hidden");
      this.refs.previewEmpty.classList.remove("is-hidden");
    }
  };

  DtfGangsheetBuilder.prototype.copyExportToClipboard = function () {
    var exportValue = this.refs.exportOutput.value;
    if (!exportValue) {
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(exportValue);
    }
  };

  DtfGangsheetBuilder.prototype.buildLineItemProperties = function (exportPayload) {
    var serialized = JSON.stringify(exportPayload);
    var properties = {
      "Gang Sheet Size": this.state.sheet.label,
      "Placed Copies": String(exportPayload.summary.totalCopies),
      "Artwork Count": String(exportPayload.summary.placedAssets),
      "_zug_dtf_usage": formatPercent(exportPayload.summary.usedAreaPercent)
    };

    if (serialized.length <= MAX_INLINE_JSON_LENGTH) {
      properties._zug_dtf_handoff = "line_item_json";
      properties._zug_dtf_builder = serialized;
    } else {
      // TODO: replace this stub with a real backend export handoff once uploads and storage are live.
      properties._zug_dtf_handoff = "backend_stub_required";
      properties._zug_dtf_job_id = "stub-" + Date.now();
      properties._zug_dtf_note = "Builder export exceeded line item limits; backend handoff required.";
    }

    if (this.state.previewDataUrl && this.state.previewDataUrl.length <= MAX_INLINE_PREVIEW_LENGTH) {
      properties._zug_dtf_preview = this.state.previewDataUrl;
    }

    return properties;
  };

  DtfGangsheetBuilder.prototype.addToCart = async function () {
    if (this.state.submitting) {
      return;
    }

    if (!this.getPlacedObjects().length) {
      this.pushNotice("warning", "Add artwork to the sheet before sending this variant to cart.");
      this.render();
      return;
    }

    this.state.submitting = true;
    this.refreshReviewArtifacts(true);

    try {
      var exportPayload = this.serializeState();
      var lineProps = this.buildLineItemProperties(exportPayload);

      lineProps._product_family = "dtf";
      lineProps._zug_dtf_sheet_size = this.state.sheet.label;
      lineProps._zug_dtf_total_items = String(exportPayload.summary.totalCopies);
      lineProps._zug_dtf_artwork_count = String(exportPayload.summary.placedAssets);

      window.__ZUG_UPLOAD = window.__ZUG_UPLOAD || {};
      var keys = Object.keys(lineProps);
      for (var i = 0; i < keys.length; i++) {
        window.__ZUG_UPLOAD[keys[i]] = lineProps[keys[i]];
      }

      this.pushNotice("good", "Gang sheet approved for " + this.state.sheet.label + ". Adding to cart\u2026");
      this.render();

      var onSavedCb = this._externalConfig.onSaved;
      if (typeof onSavedCb === "function") {
        onSavedCb();
      }

      document.dispatchEvent(new CustomEvent("zug:dtf-builder:added", {
        detail: {
          sectionId: this.sectionId,
          exportPayload: exportPayload
        }
      }));
    } catch (error) {
      this.pushNotice("error", error.message || "Something went wrong while preparing the gang sheet.");
    } finally {
      this.state.submitting = false;
      this.render();
    }
  };

  DtfGangsheetBuilder.prototype.pushNotice = function (level, message) {
    this.state.notices.push(createNotice(level, message));
    this.state.notices = this.state.notices.slice(-8);
  };

  // Lightweight floating toast (bottom-right). Used for transient,
  // non-blocking confirmations like "auto fill placed N copies".
  // Container is created lazily on first call and reused thereafter.
  DtfGangsheetBuilder.prototype.showToast = function (message, duration) {
    if (!message) return;
    var ttl = typeof duration === "number" ? duration : 2500;
    var container = document.querySelector(".dtf-builder__toast-container");
    if (!container) {
      container = document.createElement("div");
      container.className = "dtf-builder__toast-container";
      container.setAttribute("role", "status");
      container.setAttribute("aria-live", "polite");
      container.setAttribute("aria-atomic", "true");
      document.body.appendChild(container);
    }
    var toast = document.createElement("div");
    toast.className = "dtf-builder__toast";
    toast.textContent = message;
    container.appendChild(toast);
    // Force a frame so the transition runs from opacity:0 to 1.
    requestAnimationFrame(function () {
      toast.classList.add("is-visible");
    });
    setTimeout(function () {
      toast.classList.remove("is-visible");
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 200);
    }, ttl);
  };

  DtfGangsheetBuilder.prototype.handleKeydown = function (event) {
    if (!this.root.contains(document.activeElement) && this.getActiveObject() === null) {
      return;
    }

    var tagName = document.activeElement && document.activeElement.tagName;
    if (tagName === "INPUT" || tagName === "TEXTAREA") {
      return;
    }

    if ((event.key === "Delete" || event.key === "Backspace") && this.getActiveObject()) {
      event.preventDefault();
      this.deleteSelectedObject();
    }

    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "d") {
      if (this.getActiveObject()) {
        event.preventDefault();
        this.duplicateSelectedObject();
      }
    }

    if (event.key.toLowerCase() === "r" && !event.metaKey && !event.ctrlKey && !event.altKey) {
      if (this.getActiveObject()) {
        event.preventDefault();
        this.rotateSelected90();
      }
    }
  };

  DtfGangsheetBuilder.prototype.renderNotice = function () {
    var latestNotice = this.state.notices[this.state.notices.length - 1];

    if (!latestNotice) {
      this.refs.notice.textContent = "";
      this.refs.notice.className = "dtf-builder__notice is-hidden";
      return;
    }

    this.refs.notice.textContent = latestNotice.message;
    this.refs.notice.className = "dtf-builder__notice is-" + latestNotice.level;
  };

  DtfGangsheetBuilder.prototype.renderAssetPreflightChip = function (asset) {
    if (!asset) return "";
    var pf = asset.preflight || { status: "idle", warnings: [] };
    var lowDpi = this.buildLowDpiWarning(asset);
    if (pf.status === "loading" && !(pf.warnings && pf.warnings.length) && !lowDpi) {
      return "<span class=\"dtf-builder__asset-chip dtf-builder__asset-chip--preflight-loading\">Checking\u2026</span>";
    }
    var warnings = sortPreflightWarnings(this.mergeLowDpiWarning(pf.warnings || [], asset));
    if (pf.status === "error" && !warnings.length) {
      return "<span class=\"dtf-builder__asset-chip dtf-builder__asset-chip--preflight-error\">Preflight unavailable</span>";
    }
    if (!warnings.length) return "";
    var hasCritical = warnings.some(function (w) { return isCriticalPreflightCode(w.code); });
    var chipMod = hasCritical
      ? " dtf-builder__asset-chip--preflight-critical"
      : "";
    if (warnings.length === 1) {
      var copy = preflightCopy(warnings[0].code);
      return "<span class=\"dtf-builder__asset-chip dtf-builder__asset-chip--preflight" + chipMod + "\">" + escapeHtml(copy.label) + "</span>";
    }
    if (warnings.length <= 3) {
      var primary = preflightCopy(warnings[0].code);
      return "<span class=\"dtf-builder__asset-chip dtf-builder__asset-chip--preflight" + chipMod + "\">" + escapeHtml(primary.label) + " +" + (warnings.length - 1) + "</span>";
    }
    return "<span class=\"dtf-builder__asset-chip dtf-builder__asset-chip--preflight" + chipMod + "\">" + warnings.length + " warnings</span>";
  };

  DtfGangsheetBuilder.prototype.renderAssetList = function () {
    var self = this;
    var assetsMarkup = this.state.assets.map(function (asset) {
      var isSelected = asset.id === self.state.selectedAssetId;
      var placedCopies = self.getPlacedObjects().filter(function (object) {
        return object.zugMeta.assetId === asset.id;
      }).length;
      var lowResCount = self.getPlacedObjects().filter(function (object) {
        return object.zugMeta.assetId === asset.id && self.getEffectiveDpi(object) < (Number(self.config.defaults.minimumDpi) || 150);
      }).length;

      return [
        (function () {
          console.log("[ZUG][DTF][SRC_TRACE] asset card thumbnail render", {
            assetId: asset.id,
            filename: asset.filename,
            removeBackground: !!asset.removeBackground,
            upscale: !!asset.upscale,
            enhancementStatus: asset.enhancementStatus,
            previewUrl: asset.previewUrl ? asset.previewUrl.substring(0, 100) : null,
            processedUrl: asset.processedUrl ? asset.processedUrl.substring(0, 100) : null,
            finalSrcUsed: asset.previewUrl ? asset.previewUrl.substring(0, 100) : null,
            finalSrcField: "previewUrl"
          });
          return "";
        })(),
        "<div class=\"dtf-builder__asset-card" + (isSelected ? " is-selected" : "") + "\" data-dtf-asset-id=\"" + asset.id + "\" role=\"group\">",
        "<button type=\"button\" class=\"dtf-builder__asset-thumb-btn\" data-dtf-asset-thumb-zoom aria-label=\"Enlarge preview of " + escapeHtml(asset.filename) + "\" title=\"Click to enlarge\">",
        "<span class=\"dtf-builder__asset-thumb\"><img src=\"" + asset.previewUrl + "\" alt=\"" + escapeHtml(asset.filename) + "\"></span>",
        "<span class=\"dtf-builder__asset-thumb-zoom-hint\" aria-hidden=\"true\">",
        "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><circle cx=\"11\" cy=\"11\" r=\"7\"></circle><path d=\"M21 21l-4.3-4.3M11 8v6M8 11h6\"></path></svg>",
        "</span>",
        "</button>",
        "<button type=\"button\" class=\"dtf-builder__asset-select\" data-dtf-asset-select aria-label=\"Select " + escapeHtml(asset.filename) + "\">",
        "<span class=\"dtf-builder__asset-meta\">",
        "<strong class=\"dtf-builder__asset-name\">" + escapeHtml(asset.filename) + "</strong>",
        "<span class=\"dtf-builder__asset-detail\">" + formatPixels(asset.intrinsicWidth, asset.intrinsicHeight) + "</span>",
        "<span class=\"dtf-builder__asset-detail dtf-builder__asset-detail--stacked\"><span class=\"dtf-builder__asset-detail-label\">Print size</span><span class=\"dtf-builder__asset-detail-value\">" + round(asset.placedWidthInches, 2) + " × " + round(asset.placedHeightInches, 2) + " in</span></span>",
        "</span>",
        "</button>",
        "<div class=\"dtf-builder__asset-badges\">",
        "<span class=\"dtf-builder__badge dtf-builder__badge--accent\">" + placedCopies + " placed</span>",
        // lowResCount badge: recommendation tone, not a "low-res"
        // shaming label. Plural collapses cleanly ("1 to enhance"
        // / "3 to enhance").
        lowResCount ? "<span class=\"dtf-builder__badge dtf-builder__badge--warning\">" + lowResCount + " to enhance</span>" : "",
        asset.removeBackground && asset.processed && (asset.processed.dtfMask || asset.processed.dtfMaskAfterUpscale) ? "<span class=\"dtf-builder__asset-chip\">BG Removed</span>" : "",
        // auto-enhance may apply <4× (recommended scale). Generic
        // "AI Enhanced" chip covers every successful run.
        asset.upscale && asset.processed && asset.processed.upscale ? "<span class=\"dtf-builder__asset-chip\">AI Enhanced</span>" : "",
        asset.dtfSafe === false ? "<span class=\"dtf-builder__asset-chip dtf-builder__asset-chip--info\">Review recommended</span>" : "",
        asset.enhancementStatus === "processing" ? "<span class=\"dtf-builder__asset-chip dtf-builder__asset-chip--processing\">Processing…</span>" : "",
        asset.enhancementStatus === "error" ? "<span class=\"dtf-builder__asset-chip dtf-builder__asset-chip--error\">Enhancement failed</span>" : "",
        self.renderAssetPreflightChip(asset),
        "</div>",
        "<div class=\"dtf-builder__asset-actions\">",
        "<button type=\"button\" class=\"dtf-builder__asset-action\" data-dtf-asset-action=\"edit\" aria-label=\"Edit " + escapeHtml(asset.filename) + "\">Edit</button>",
        "<button type=\"button\" class=\"dtf-builder__asset-action\" data-dtf-asset-action=\"add-copies\" aria-label=\"Add copies of " + escapeHtml(asset.filename) + "\">+ Copies</button>",
        "<button type=\"button\" class=\"dtf-builder__asset-action dtf-builder__asset-action--danger\" data-dtf-asset-action=\"remove\" aria-label=\"Remove " + escapeHtml(asset.filename) + "\">Remove</button>",
        "</div>",
        "</div>"
      ].join("");
    }).join("");

    this.refs.assetList.innerHTML = assetsMarkup;
    this.refs.assetEmpty.classList.toggle("is-visible", this.state.assets.length === 0);
    this.refs.assetCount.textContent = this.state.assets.length + " asset" + (this.state.assets.length === 1 ? "" : "s");

    this.refs.assetList.querySelectorAll("[data-dtf-asset-id]").forEach(function (card) {
      var assetId = card.getAttribute("data-dtf-asset-id");
      var selectBtn = card.querySelector("[data-dtf-asset-select]");
      if (selectBtn) {
        selectBtn.addEventListener("click", function () {
          self.state.selectedAssetId = assetId;
          self.render();
        });
      }
      var thumbBtn = card.querySelector("[data-dtf-asset-thumb-zoom]");
      if (thumbBtn) {
        thumbBtn.addEventListener("click", function (e) {
          e.stopPropagation();
          var assetEntry = self.state.assets.find(function (a) { return a.id === assetId; });
          if (!assetEntry) return;
          self.state.selectedAssetId = assetId;
          console.log("[ZUG][DTF][ASSET_CARD] thumb zoom clicked", assetEntry.filename);
          self.openArtZoom(assetEntry);
        });
      }
      card.querySelectorAll("[data-dtf-asset-action]").forEach(function (actionBtn) {
        actionBtn.addEventListener("click", function (e) {
          e.stopPropagation();
          var action = actionBtn.getAttribute("data-dtf-asset-action");
          var assetEntry = self.state.assets.find(function (a) { return a.id === assetId; });
          if (!assetEntry) return;
          self.state.selectedAssetId = assetId;
          if (action === "edit") {
            console.log("[ZUG][DTF][ASSET_EDIT] edit clicked for", assetEntry.filename);
            self.openSetupPopup([assetEntry], { mode: "edit" });
          } else if (action === "add-copies") {
            var qty = Math.max(1, assetEntry.lastQuantity || 1);
            console.log("[ZUG][DTF][ASSET_EDIT] add-copies clicked for", assetEntry.filename, "qty=" + qty);
            self.placeSelectedAssetCopies(qty);
          } else if (action === "remove") {
            console.log("[ZUG][DTF][ASSET_EDIT] remove clicked for", assetEntry.filename);
            self.deleteAsset(assetId);
          }
        });
      });
    });
  };

  DtfGangsheetBuilder.prototype.deleteAsset = function (assetId) {
    var idx = this.state.assets.findIndex(function (a) { return a.id === assetId; });
    if (idx === -1) return;
    var asset = this.state.assets[idx];

    if (this.canvas) {
      this.getPlacedObjects().forEach(function (object) {
        if (object.zugMeta && object.zugMeta.assetId === assetId) {
          this.canvas.remove(object);
        }
      }, this);
      this.canvas.discardActiveObject();
    }

    if (asset.originalPreviewUrl) {
      try { URL.revokeObjectURL(asset.originalPreviewUrl); } catch (e) {}
    }
    if (asset.trimmedPreviewUrl) {
      try { URL.revokeObjectURL(asset.trimmedPreviewUrl); } catch (e) {}
    }

    this.state.assets.splice(idx, 1);
    if (this.state.selectedAssetId === assetId) {
      this.state.selectedAssetId = this.state.assets.length ? this.state.assets[0].id : null;
    }

    if (this.state.setupQueue.some(function (a) { return a.id === assetId; })) {
      this.closeSetupPopup();
    }

    this.pushNotice("good", "Removed " + asset.filename + ".");
    this.render();
  };

  DtfGangsheetBuilder.prototype.renderSelectedAssetCard = function () {
    if (!this.refs.selectedAsset) return;
    var asset = this.getSelectedAsset();

    if (!asset) {
      this.refs.selectedAsset.innerHTML = "<div class=\"dtf-builder__selected-asset-empty\">Choose an uploaded asset to set its copy quantity and add it to the sheet.</div>";
      return;
    }

    var placedAssetObjects = this.getPlacedObjects().filter(function (object) {
      return object.zugMeta.assetId === asset.id;
    });
    var placedCopies = placedAssetObjects.length;

    // Pick the worst placed-copy state so the chip reflects whichever
    // instance most needs attention. Falls back to the asset's
    // currently-set placedWidth/Height when nothing is on the sheet
    // yet (matches what the popup reasoning will show).
    var qsForChip = null;
    if (placedAssetObjects.length) {
      var worstRank = -1;
      // 5-tier ranking — looks_great is best, then good_quality,
      // then passable, then the two warning tiers. Used to pick
      // the chip for the worst placed copy of this asset.
      var rank = { looks_great: 0, good_quality: 1, passable: 2, enhancement_recommended: 3, too_large_for_best_quality: 4 };
      for (var pi = 0; pi < placedAssetObjects.length; pi++) {
        var s = this.computeObjectQualityState(placedAssetObjects[pi]);
        if (s && rank[s.quality_state] > worstRank) {
          worstRank = rank[s.quality_state];
          qsForChip = s;
        }
      }
    } else {
      qsForChip = getAssetQualityState(asset, asset.placedWidthInches, asset.placedHeightInches);
    }

    var qualityChipHtml = "";
    if (qsForChip) {
      // The three calm tiers (looks_great / good_quality /
      // passable) all reuse the "good" chip styling — only the
      // label text differs, driven by QUALITY_OUTLINE[state].label
      // below. This avoids needing new CSS for tiers whose whole
      // point is "don't push the customer toward Upscale".
      var calmStates = { looks_great: 1, good_quality: 1, passable: 1 };
      var chipMod = calmStates[qsForChip.quality_state] ? "good"
                  : qsForChip.quality_state === "enhancement_recommended" ? "recommended"
                  : "too-large";
      qualityChipHtml = "<span class=\"dtf-builder__quality-chip dtf-builder__quality-chip--" + chipMod + "\" title=\"" + qsForChip.effective_dpi + " DPI at current size\">" + escapeHtml(QUALITY_OUTLINE[qsForChip.quality_state].label) + "</span>";
    }

    this.refs.selectedAsset.innerHTML = [
      "<div class=\"dtf-builder__selected-asset-card\">",
      "<div class=\"dtf-builder__selected-thumb\"><img src=\"" + asset.previewUrl + "\" alt=\"" + escapeHtml(asset.filename) + "\"></div>",
      "<div class=\"dtf-builder__selected-meta\">",
      "<strong class=\"dtf-builder__selected-name\">" + escapeHtml(asset.filename) + "</strong>",
      "<span class=\"dtf-builder__selected-detail\">" + formatPixels(asset.intrinsicWidth, asset.intrinsicHeight) + "</span>",
      "<span class=\"dtf-builder__selected-detail\">Print size: " + round(asset.placedWidthInches, 2) + " × " + round(asset.placedHeightInches, 2) + " in · ~" + round(asset.estimatedDpi, 0) + " DPI</span>",
      "<span class=\"dtf-builder__selected-detail\">On sheet: " + placedCopies + " cop" + (placedCopies === 1 ? "y" : "ies") + "</span>",
      qualityChipHtml,
      (function () {
        // Gate chips on cache-slot presence (not enhancementStatus)
        // so an upscale that succeeded survives a rmbg failure in
        // the same run.
        var hasMask = !!(asset.processed && (asset.processed.dtfMask || asset.processed.dtfMaskAfterUpscale));
        var hasUp = !!(asset.processed && asset.processed.upscale);
        var bgChip = (asset.removeBackground && hasMask) ? "<span class=\"dtf-builder__asset-chip\">BG Removed</span>" : "";
        var upChip = (asset.upscale && hasUp) ? "<span class=\"dtf-builder__asset-chip\">AI Enhanced</span>" : "";
        var unsafeChip = (asset.dtfSafe === false) ? "<span class=\"dtf-builder__asset-chip dtf-builder__asset-chip--info\">Review recommended</span>" : "";
        var anyChip = bgChip || upChip || unsafeChip;
        if (anyChip) {
          return "<span class=\"dtf-builder__asset-badges\">" + bgChip + upChip + unsafeChip + "</span>";
        }
        if (asset.enhancementStatus === "error" && !hasUp && !hasMask) {
          return "<span class=\"dtf-builder__asset-badges\"><span class=\"dtf-builder__asset-chip dtf-builder__asset-chip--error\">Enhancement failed</span></span>";
        }
        return "";
      }()),
      "</div>",
      "</div>"
    ].join("");
  };

  DtfGangsheetBuilder.prototype.renderSheetSummary = function () {
    this.refs.sheetVariant.textContent = this.state.sheet.variantTitle;
    this.refs.sheetSize.textContent = this.state.sheet.label;
    this.refs.printableSize.textContent = round(this.state.sheet.printableWidthInches, 2) + " × " + round(this.state.sheet.printableHeightInches, 2) + " in";
    this.refs.spacingValue.textContent = formatInches(this.state.spacingInches);
    this.refs.stageSheet.textContent = this.state.sheet.label + " sheet";
    if (this.refs.reviewSheetSize) {
      this.refs.reviewSheetSize.textContent = this.state.sheet.label;
    }
  };

  DtfGangsheetBuilder.prototype.renderStatus = function () {
    var activeObject = this.getActiveObject();
    var summary = this.getSummary();
    var warnings = this.composeWarnings();
    var warningCount = warnings.filter(function (warning) {
      return warning.level === "warning" || warning.level === "error";
    }).length;
    var warningItem = this.refs.statusWarnings.closest(".dtf-builder__status-item");
    var activeObjectWarning = false;

    if (activeObject) {
      var dpi = this.getEffectiveDpi(activeObject);
      activeObjectWarning = !!dpi && dpi < (Number(this.config.defaults.minimumDpi) || 150);
      this.refs.statusDpi.textContent = round(dpi || 0, 0) + " DPI";
      this.refs.stageSelection.textContent = activeObject.zugMeta.filename + " • " + formatInches(activeObject.getScaledWidth() / UNITS_PER_INCH) + " × " + formatInches(activeObject.getScaledHeight() / UNITS_PER_INCH);
      this.refs.stageSelection.classList.add("is-active");
      this.refs.stageSelection.classList.toggle("is-warning", activeObjectWarning);
    } else {
      this.refs.statusDpi.textContent = "Waiting for selection";
      this.refs.stageSelection.textContent = "No artwork selected";
      this.refs.stageSelection.classList.remove("is-active", "is-warning");
    }

    this.refs.statusAssets.textContent = String(summary.placedAssets);
    this.refs.statusCopies.textContent = String(summary.totalCopies);
    this.refs.statusUsage.textContent = formatPercent(summary.usedAreaPercent);
    this.refs.statusWarnings.textContent = warningCount ? warningCount + " warning" + (warningCount === 1 ? "" : "s") : "Clear";
    warningItem.classList.toggle("is-alert", warningCount > 0);
    this.refs.stageEmpty.classList.toggle("is-hidden", summary.totalCopies > 0);
  };

  DtfGangsheetBuilder.prototype.renderWarnings = function () {
    var actionable = this.state.warnings.filter(function (w) {
      return w.level === "warning" || w.level === "error";
    });

    var markup = actionable.map(function (warning) {
      return "<li>" + escapeHtml(warning.message) + "</li>";
    }).join("");

    // Customer-facing Print check card: show the green "Looks good
    // for print" badge when there are no actionable warnings; show
    // the warning list otherwise. Both nodes may be absent if a
    // legacy template is in use, so guard before writing.
    var hasWarnings = actionable.length > 0;
    if (this.refs.warningList) {
      this.refs.warningList.innerHTML = markup;
      this.refs.warningList.classList.toggle("is-hidden", !hasWarnings);
    }
    if (this.refs.printCheckGood) {
      this.refs.printCheckGood.classList.toggle("is-hidden", hasWarnings);
    }
  };

  DtfGangsheetBuilder.prototype.renderReviewSummary = function () {
    var summary = this.getSummary();

    this.refs.reviewAssets.textContent = String(summary.placedAssets);
    this.refs.reviewCopies.textContent = String(summary.totalCopies);
    this.refs.reviewUsed.textContent = formatPercent(summary.usedAreaPercent);
    if (this.refs.reviewFree) {
      this.refs.reviewFree.textContent = formatPercent(summary.freeAreaPercent);
    }

    // Lowest effective DPI across placed copies — single, simple
    // production metric for the customer-facing Review summary.
    if (this.refs.reviewMinDpi) {
      var placed = this.getPlacedObjects();
      var minDpi = null;
      for (var i = 0; i < placed.length; i++) {
        var qs = this.computeObjectQualityState(placed[i]);
        if (qs && typeof qs.effective_dpi === "number" && isFinite(qs.effective_dpi)) {
          if (minDpi === null || qs.effective_dpi < minDpi) minDpi = qs.effective_dpi;
        }
      }
      this.refs.reviewMinDpi.textContent = (minDpi === null) ? "—" : Math.round(minDpi) + " DPI";
    }
  };

  DtfGangsheetBuilder.prototype.renderButtons = function () {
    var currentIndex = MODE_ORDER.indexOf(this.state.currentMode);
    var isReview = this.state.currentMode === "review";
    var hasSelectedAsset = !!this.getSelectedAsset();
    var hasActiveObject = !!this.getActiveObject();
    var hasObjects = this.getPlacedObjects().length > 0;
    var label = "Continue to Review";

    if (this.state.currentMode === "uploads") {
      label = "Next: Arrange";
    } else if (this.state.currentMode === "arrange") {
      label = "Continue to Review";
    } else if (this.state.currentMode === "sheet") {
      // Defensive: the sheet step is no longer reachable via the
      // rail or next/back, but if any external code force-sets
      // mode to "sheet" we still surface a sensible label.
      label = "Continue to Review";
    } else if (this.state.currentMode === "review") {
      label = this.config.defaults.primaryButtonLabel || "Approve & Add to Cart";
    }

    this.refs.backButton.disabled = currentIndex <= 0;
    this.refs.primaryButton.disabled = this.state.submitting || (isReview && !hasObjects);
    this.refs.primaryButton.textContent = this.state.submitting
      ? "Adding to Cart..."
      : label;
    if (this.refs.addCopies) this.refs.addCopies.disabled = !hasSelectedAsset;
    if (this.refs.autoFill) {
      this.refs.autoFill.disabled = !hasSelectedAsset;
      this.refs.autoFill.classList.toggle("is-disabled", !hasSelectedAsset);
    }
    if (this.refs.rotateSelected) {
      this.refs.rotateSelected.disabled = !hasActiveObject;
      this.refs.rotateSelected.classList.toggle("is-disabled", !hasActiveObject);
    }
    if (this.refs.duplicateGroup) this.refs.duplicateGroup.disabled = !hasActiveObject;
    if (this.refs.deleteSelected) this.refs.deleteSelected.disabled = !hasActiveObject;
    if (this.refs.clearAll) this.refs.clearAll.disabled = !hasObjects;
  };

  DtfGangsheetBuilder.prototype.renderModes = function () {
    var currentMode = this.state.currentMode;

    this.refs.railButtons.forEach(function (button) {
      var isActive = button.getAttribute("data-mode") === currentMode;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });

    this.refs.panels.forEach(function (panel) {
      panel.classList.toggle("is-active", panel.getAttribute("data-dtf-panel") === currentMode);
    });
  };

  DtfGangsheetBuilder.prototype.render = function () {
    this.renderModes();
    this.renderAssetList();
    this.renderSelectedAssetCard();
    this.syncPlacedSizeInputs();
    this.renderSheetSummary();
    this.renderStatus();
    this.renderReviewSummary();
    this.renderWarnings();
    this.renderReviewPreflight();
    this.renderNotice();
    this.renderButtons();
    this.renderReviewArtifacts();
    this.positionCanvasActions();

    if (this.refs.copyQuantity) this.refs.copyQuantity.value = String(this.state.copyQuantity);
    this.refs.spacingInput.value = round(this.state.spacingInches, 2);
  };

  globalApi.DtfGangsheetBuilder = DtfGangsheetBuilder;

  globalApi.createAndInit = function (rootEl, externalConfig) {
    globalApi.instances.forEach(function (inst) {
      try { inst.destroy(); } catch (e) { console.warn("[ZUG][DTF] destroy failed during pre-init cleanup", e); }
    });
    globalApi.instances = [];

    var instance = new DtfGangsheetBuilder(rootEl, externalConfig);
    globalApi.instances.push(instance);
    instance.init();
    return instance;
  };

  globalApi.destroyAll = function () {
    globalApi.instances.forEach(function (inst) {
      try { inst.destroy(); } catch (e) { console.warn("[ZUG][DTF] destroy failed", e); }
    });
    globalApi.instances = [];
  };

})();
